import {
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  type ReviewSupportingPagesColumn
} from '../../utils/review-supporting-pages';
import {
  supportingPagesEmptyReady,
  type SupportingPagesGateState
} from '../../utils/supporting-pages-render-gate';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
  DataForSEOService,
  SupportingPagesV2EvalData,
  SupportingPagesV2RowResult,
  SupportingPagesByTopicSnapshotData,
  SerpRivalCoverageData,
  CompareSummary,
  CompareSummaryTarget,
  SectionCoverageRow,
  HeadingPathSegment,
  PageDemandSnapshot,
  PageHealthSnapshot,
  PageRealizationSnapshot,
  PageIntentDiagnosis,
  PageIntentDiagnosisBullet,
  PageSerpResultRow,
  PageSerpSnapshot,
  SecondaryPageHealthSnapshot
} from '../../services/dataforseo';
import { FeatureFlagService } from '../../services/feature-flag';
import { formatPageDemandSourceConfidence } from '../../utils/page-demand-display';
import {
  classifyPageHealth,
  effectiveHealthBandClass,
  effectiveHealthBandLabel,
  EffectiveHealthDisplayBand,
  pageHealthBandClass,
  pageHealthBandLabel,
  resolveEffectiveHealthBand
} from '../content-inventory/inventory-page-health';
import {
  formatInventoryRealizationNumber,
  formatInventoryRealizationRatio,
  inventoryRealizationGapTitle,
  inventoryRealizationRatioTitle
} from '../content-inventory/inventory-realization-sort';
import {
  buildPageSignalsForUrl,
  type InventoryPageSignal,
  type InventoryPageSignalKind
} from '../content-inventory/inventory-page-signals';
import {
  InventoryPlanClustersService,
  type ActionPlanInternalLinkRow,
  type ActionPlanUnclusteredPageRow,
  type ActionPlanV2Row
} from '../../services/inventory-plan-clusters.service';
import { PlanV2IssueQueueService } from '../../services/plan-v2-issue-queue.service';
import { PlanV2EmbedContextService } from '../../services/plan-v2-embed-context.service';
import { CrawlRunPeriodService, type CrawlRunPeriodView } from '../../services/crawl-run-period.service';
import { TechnicalRollupService } from '../../services/technical-rollup.service';
import { OwnSiteUrlsService } from '../../services/own-site-urls.service';
import { CompetingPagesService } from '../../services/competing-pages.service';
import { NearMissSearchesService } from '../../services/near-miss-searches.service';
import type { NearMissPageResult } from '@taptimize/shared/dist/review/near-miss-searches';
import {
  NEAR_MISS_CARD_TITLE,
  nearMissNumbersLabel,
  type NearMissCardView,
  type NearMissItemSearch
} from '@taptimize/shared/dist/review/near-miss-card';
import type {
  CannibalizationPairView,
  CompetingPagesPayload
} from '@taptimize/shared/dist/review/cannibalization-card';
import { encodeWorkItemDetail } from '../../utils/plan-v2-work-item-display';
import { ReviewSelectionService } from '../../services/review-selection.service';
import { dashboardPageRows } from '../../utils/dashboard-page-rows';
import {
  buildPlanV2ExpandPlaybook,
  type PlanV2ExpandPlaybookStep
} from '../../utils/plan-v2-expand-playbook';
import { isNonCompetitorDomain } from '../../utils/non-competitor-domains';
import type {
  EstimateBesideRivalView,
  PageEstimatePairSource
} from '@taptimize/shared/dist/review/estimate-beside-rival';
import {
  ReviewPageModel,
  type ReviewPageSource,
  type TopicOwnershipDomain
} from '@taptimize/shared/dist/review/review-page-model';
import type { SerpSubjectWithheldNotice } from '@taptimize/shared/dist/review/serp-subject-withheld';
import { compareDrawerTarget } from '../../utils/compare-evidence';
import {
  hasSectionGapsForV2Row,
  sectionGapsForV2Row
} from '../../utils/section-gaps';
import {
  type ReviewMissingSectionsView
} from '../../utils/review-missing-sections';
import {
  type ReviewRankGapsView
} from '../../utils/review-rank-gaps';
import {
  weakSerpCompareBadgeLabel,
  weakSerpCompareReason
} from '../../utils/weak-serp-compare-target';
import {
  buildFreshnessWorkItemContext,
  composeFreshnessWorkItem,
  composeInternalLinkWorkItem,
  composeInternalLinkWorkItemsFromSelection,
  buildInternalLinkRowDisplay,
  composeInternalLinksWorkItem,
  composeSerpIntentWorkItem,
  composeSerpPhraseWorkItem,
  composeInternalLinkWorkItemOnSourcePage,
  composeTechnicalIssuesWorkItem,
  internalLinkWorkItemIssueKey,
  formatInternalLinkRelevanceScore,
  internalLinkDirectionChipClass,
  internalLinkDirectionLabel,
  filterInternalLinksIncomingToPage,
  internalLinkSuggestionKeysInWorkItems,
  listInternalLinkComposeOptions,
  splitPlacementByAnchor,
  listTechnicalIssueComposeOptions,
  buildTopicOwnershipComposeState,
  composeTopicOwnershipWorkItemFromState,
  topicOwnershipComposeHasSelection,
  topicOwnershipHeadingComposeId,
  type InternalLinkComposeOption,
  type TechnicalIssueComposeOption,
  type TopicOwnershipComposeState
} from '../../utils/plan-v2-work-item-compose';
import {
  buildPlanV2IssueKey,
  type PlanV2IssueCardPayload,
  type PlanV2IssueType
} from '../../utils/plan-v2-issues';
import {
  buildPageEvidencePanel,
  isPageEvidenceEmpty,
  type EvidenceTone,
  type PageEvidence,
  type PageEvidencePanel
} from '../../utils/page-evidence';
import { DomainContextService } from '../../services/domain-context';
import { CompareContextService } from '../../services/compare-context';
import { UrlSerpRefreshProgress, UrlSerpSyncService } from '../../services/url-serp-sync';
import {
  buildSerpFoldedTermSegments,
  buildSerpSnippetSegments,
  type SerpSnippetSegment
} from '../../utils/serp-snippet-segments';
import {
  buildCollapsedStructurePassageSegments,
  parseStructurePassageHeading
} from '../../utils/serp-structure-passage';
import {
  buildYourPageVocabularySegments,
  collectMisalignedPhraseLinks,
  foldLexicalToken,
  type MisalignedPhraseLink
} from '../../utils/serp-intent-highlight';
import { DomainTopicsService, ParentTopic } from '../../services/domain-topics';
import { InventoryQuickCompareDrawerComponent } from '../inventory-quick-compare-drawer/inventory-quick-compare-drawer';
import { PlanV2IssueCardActionsComponent } from '../plan-v2-issue-card-actions/plan-v2-issue-card-actions';
import { UrlRunComparisonComponent } from '../url-run-comparison/url-run-comparison';
import {
  buildPageTopicLabelsBySlug,
  extractTopicTaggedPagesFromDiagnostic,
  filterSupportingPagesByRelevance,
  filterRowsByFocalAnchorTopics,
  getTopTopicsForSupportingPages,
  mergeTopicTaggedPagesByUrl,
  rowOverlapsFocalAnchorTopics,
  normalizeTopicSlug,
  normalizeUrlForCompare,
  SUPPORTING_PAGE_INITIAL_VISIBLE_COUNT,
  SUPPORTING_PAGE_USER_INITIAL_VISIBLE_COUNT,
  SUPPORTING_PAGE_MIN_RELEVANCE_SCORE,
  uniqueParentDiagnosticSlugs,
  compareSupportingPagesByRelevance,
  type DomainTopicUrlRow
} from '../../utils/topic-supporting-pages';
import {
  resolveFocalAnchorTopics,
  topicOwnershipDomainsFromSnapshot
} from '@taptimize/shared/dist/review/topic-ownership-source';
import {
  orderTopicSignalsForClaim,
  topicSignalMatchesDemandClaim
} from '../../utils/page-demand-claim-alignment';
import {
  splitSectionTextByAbsentTerms,
  type SectionCompareHighlightSegment
} from '../../utils/section-compare-highlight';
import {
  type SupportingPagesUnifiedColumn
} from '../../utils/supporting-pages-unified-columns';
import {
  rankDrivenGapsForV2Row,
  type RankDrivenSectionGap
} from '../../utils/rank-driven-section-gaps';
import { splitPassageByKeywordTokens, rankDrivenHeadingTag } from '../../utils/keyword-passage-highlight';
import {
  attachSupportingPageSimilarityMatches,
  buildLexicalByNormalizedUrl,
  collectSupportingPageUrls,
  formatSupportingPageTokenPreview,
  PageLexicalFields,
  SUPPORTING_PAGE_SIMILARITY_MIN_SCORE
} from '../../utils/supporting-page-similarity';
import {
  buildOnpageTechnicalIssueViews,
  type OnpageIssueView
} from '../../utils/url-detail-onpage-issues';
import { catchError, forkJoin, map, of, Subscription, switchMap, take } from 'rxjs';
import { InFlightRequest } from '../../utils/in-flight-request';
import { type ReviewAnswerVocab } from '../../utils/review-answer-vocab';
import {
  type SectionCompareNotice
} from '../../utils/section-compare-unavailable';
import {
  type MixedSerpAlignment,
  type PageTypeExample
} from '../../utils/page-type-alignment';

const DEMAND_DIAGNOSTICS_INFO: Record<string, string> = {
  'demand-and-realization-gap':
    'Dev-only breakdown of accountable demand vs how much visibility this URL still leaves on the table.',
  'demand-labeling':
    'Stored claim vs what rankings already associate with the page, plus ambient context when they disagree.',
  'realization-gap':
    'Health band and gap math comparing realized visibility to a keyword-backed demand ceiling.',
  claim: 'Subtopic demand this URL is accountable for—not the whole cluster or plan group.',
  'intent-tokens':
    'Slug/title modifiers (how, template, vs, etc.) that gate which keywords count toward the ceiling.',
  'claim-source': 'Where the claim came from and how confident we are (high, low, or none).',
  'claim-detail': 'Plain-language note from the labeling rule that chose this claim.',
  'market-realization': 'Best stored ranked keyword already aligned with the claim, if any.',
  'realization-source': 'Source for the realization line and its confidence.',
  'ambient-topic': 'Strongest on-page topic that conflicts with the claim; context only, not the claim.',
  'ambient-source': 'Source for the ambient topic signal and its confidence.',
  'split-reasons': 'Why claim, realization, or ambient topic diverge on this URL.',
  computed: 'When demand labels were last written for this URL.',
  'health-band':
    'Critical for HTTP errors; Weak, OK, or Best from scored gap; Unscored when gap is not scored.',
  'health-model': 'Realization-gap thresholds when scored; Unscored when gap is not scored; Eligibility for HTTP failures.',
  'health-reasons': 'Short rule lines showing which checks set the current band.',
  'gap-gate': 'Whether ratio and gap are scored, pending on confidence or immaterial ceiling, or withheld.',
  'realized-visibility':
    'ETV when rankings are missing or coherent with rank proxy; otherwise claim-aligned keyword traffic at actual positions.',
  'demand-ceiling': 'Max upside from aligned ranked keywords at the configured target rank.',
  'ceiling-used': 'Ceiling after floors—the denominator for the realization ratio.',
  'realization-ratio': 'Realized visibility divided by ceiling used; blank when gap is not scored.',
  gap: 'Ceiling used minus realized visibility; blank when gap is not scored.',
  'ceiling-driver': 'Aligned keyword with the largest upside at the target rank.',
  'driver-volume': 'Monthly search volume for the ceiling-driver keyword.',
  'driver-match-tokens': 'Claim intent tokens the driver keyword shares.',
  'upside-at-target-rank': 'Estimated traffic at the target rank (volume × CTR) for the driver.',
  'aligned-keywords-in-pool': 'Ranked keywords that pass claim and intent alignment for the ceiling.',
  'ranked-keywords': 'Stored Labs ranked keywords for this URL from the latest analysis snapshot.',
  'secondary-health':
    'When realization gap is unscored, a separate earned-visibility read without a demand ceiling.',
  'secondary-health-band': 'Weak, OK, or Best from claim-aligned earned visibility when secondary scoring applies.',
  'secondary-health-model': 'Whether secondary scoring is held, skipped, or uses earned visibility.',
  'secondary-health-reasons': 'Rule lines for the secondary model only; primary Health stays Unscored.',
  'secondary-unscored-segment': 'Why the primary gap is unscored (market data, ceiling pool, material floor).',
  'recently-updated-hold': 'Content modified within six months; secondary earned-visibility scoring is held.',
};

interface PageRankedKeywordRow {
  keyword: string;
  position: number;
  searchVolume: number | null;
}

/** Per-page topic signal (mirrors backend `TopicSignal`). */
interface PageTopicSignal {
  topicSlug: string;
  label: string;
  weight: number;
  salience: number;
  relativeDensity: number;
}

interface ContentElement {
  type: 'heading' | 'text' | 'link' | 'table';
  level?: number; // for headings (h1, h2, etc.)
  content: string;
  href?: string; // for links
  tableData?: any[]; // for table content
}

interface CompetitorTopicMatch {
  domain: string;
  authorityScore: number;
  pageCount: number;
  matchedParentTopicSlug: string;
}

/** Marked competitor ahead on a topic overlapping this page (for Improvements v1). */
interface ImprovementCompetitorLead {
  domain: string;
  authorityScore: number;
  pageCount: number;
  topicLabel: string;
  topicSlug: string;
}

interface CompetitorTopicUrlPreview {
  url: string;
  score: number;
  salience?: number;
  estimatedOrganicTraffic?: number;
}

interface CompetitorTopicTooltipState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  urls: CompetitorTopicUrlPreview[];
}

interface TopicOwnershipCompareOption {
  url: string;
  label: string;
  /**
   * Absent on the SERP-winner option, which does not come from the topic
   * snapshot and has no topical score — it is prepended rather than ranked.
   * Optional rather than 0 so it cannot be sorted or displayed as a measured
   * floor.
   */
  seoIntentScore?: number;
  focalMatchScore?: number;
}

interface SerpCompetitionMarkedAbove {
  position: number;
  domain: string;
  url: string;
  title: string;
}

interface SerpCompetitionTopLeader {
  position: number;
  domain: string;
  url: string;
  title: string;
  descriptionPreview: string;
  publishedLabel: string | null;
}

/** Top-3 SERP slot for Who's beating you? — includes social; sorted by position. */
interface SerpCompetitionDisplayRow extends SerpCompetitionTopLeader {
  isSocial: boolean;
  /** Homepage / login / app-shell — listed but not a compare target. */
  isWeakCompareTarget: boolean;
  weakCompareBadge: string | null;
  isMarked: boolean;
  intentLabel: string;
}

interface SerpCompetitionInsight {
  yourPosition: number | null;
  markedAboveYou: boolean;
  markedCompetitorsAbove: SerpCompetitionMarkedAbove[];
  topLeaders: SerpCompetitionTopLeader[];
}

type ClaimOverrideRefreshPhase = 'claim' | 'serp' | 'rival' | 'reload';

interface ClaimOverrideRefreshProgress {
  percent: number;
  statusLine: string;
  etaLabel: string | null;
  phase: ClaimOverrideRefreshPhase;
  indeterminate?: boolean;
}

export type UrlDetailEmbedPreset = 'planV2';

/**
 * The verdict a focus card's diagnosis chip reports.
 *
 * It drives two things that must not drift apart: the badge tone the chip
 * paints, and whether the Issues embed shows that card at all. Those used to
 * be one value -- a CSS class name -- and `embedDiagnosisIsGreen()` read the
 * verdict back out of it with `chipClass.includes('--ok')`. Renaming the class
 * during the component migration would have silently changed which cards an
 * operator sees in actions-only mode, with a green build and no failing test.
 * The tone is the value now; the class is derived from it.
 */
/**
 * Aliased onto `EvidenceTone` rather than re-declared. The two were the same
 * five words written twice, and the evidence panel SORTS on this union — a
 * tone added here and not there would order rows by a rank it does not have.
 */
type DiagnosisTone = EvidenceTone;

/** Tone -> the `.badge` modifier that paints it. `neutral` is the badge's own
 *  default, so it names nothing rather than naming a sixth tone.
 *
 *  `bad` is not a louder `warn`. A warning is "there is something to do here";
 *  `bad` is a measured loss — a rival is above you in the SERP right now. The
 *  badge has had a `--danger` tone since it was built and nothing reached for
 *  it, so every verdict on this screen read at one volume. */
const DIAGNOSIS_BADGE_CLASS: Record<DiagnosisTone, string> = {
  ok: 'badge--success',
  warn: 'badge--warning',
  bad: 'badge--danger',
  neutral: '',
  muted: 'badge--muted'
};

@Component({
  selector: 'app-url-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    InventoryQuickCompareDrawerComponent,
    PlanV2IssueCardActionsComponent,
    UrlRunComparisonComponent
  ],
  templateUrl: './url-detail.html',
  styleUrl: './url-detail.css'
})
export class UrlDetailComponent implements OnInit, OnChanges, OnDestroy, DoCheck {
  /** When set with `embedPreset`, renders URL focus cards inline (e.g. Plan v2 expand) instead of full page chrome. */
  @Input() embedUrl: string | null = null;
  @Input() embedPreset: UrlDetailEmbedPreset | null = null;
  /** Plan v2 expand: hide “all clear” cards (green diagnosis chips). Signals stay when present. */
  @Input() embedActionsOnly = false;
  @Input() embedPlanV2Domain: string | null = null;
  @Input() embedPlanV2Row: ActionPlanV2Row | null = null;
  /** 1-based rank within the active Issues queue (for playbook rationale). */
  @Input() embedPlanV2QueueRank: number | null = null;
  /** Plan v2 Issues drawer: full-height embed (no 70vh cap). */
  @Input() embedPlanV2InDrawer = false;

  /**
   * This host is a review surface: somewhere an operator decides, not digs.
   *
   * It turns on two families of behaviour. Claims are held back until nothing
   * can still change them, and the card is stripped to the verdict and the
   * decision — no methodology, no evidence columns, no controls that would take
   * the operator somewhere else to find out more.
   *
   * One flag rather than one per rule, because there is one distinction being
   * drawn and every rule follows from it. Set by review mode and the dashboard
   * peek; everything else keeps the deep-dive behaviour untouched.
   *
   * Deliberately NOT inferred from `embedPlanV2InDrawer`, which would have been
   * the convenient flag and is the wrong one: Content Inventory's Issues queue
   * opens that same drawer, and it is a place to dig.
   */
  @Input() embedReviewSurface = false;

  /**
   * Fires when this embed has finished the loading a reader would wait on.
   *
   * Exists so the review can prefetch the NEXT page once this one is done,
   * rather than starting both at once and making the visible page wait behind
   * the invisible one — the sidecar behind these evaluations is effectively
   * single-threaded (TECHNICAL_DEBT 0ad), so concurrent work is not free.
   *
   * An output rather than the host reaching in: the embed owns its loading,
   * and a host that re-derived "is it done" from the outside would be a second
   * definition of settled, drifting the moment either side changed.
   */
  @Output() embedSettledChange = new EventEmitter<boolean>();
  /**
   * Dashboard movers expand: same actionable Issue cards as the drawer, but
   * compacted (no triage buttons, no SERP tables / vocab grids). Implies
   * actions-only filtering when used with `embedPreset="planV2"`.
   */
  @Input() embedCompactPeek = false;

  /**
   * Whether the compact peek's evidence rows carry their own Dismiss / Work
   * item pair.
   *
   * Off on the Dashboard, where one Expand control replaces them: a peek that
   * offers triage on four rows without showing what is being triaged asks the
   * operator to act on a chip. Expanding first, then acting, is the same two
   * clicks with the evidence in between. Defaults to on so nothing that mounts
   * a peek without an opinion silently loses its actions.
   */
  @Input() embedPeekActions = true;
  /** Emitted after a successful intent-override cascade finishes and URL analysis is reloaded. */
  @Output() intentOverrideCompleted = new EventEmitter<{ urlId: string; url: string }>();

  @HostBinding('class.url-detail--embed-plan-v2')
  get hostEmbedPlanV2Class(): boolean {
    return this.isPlanV2Embed();
  }

  @HostBinding('class.url-detail--embed-plan-v2-drawer')
  get hostEmbedPlanV2DrawerClass(): boolean {
    return this.isPlanV2Embed() && this.embedPlanV2InDrawer;
  }

  @HostBinding('class.url-detail--embed-compact-peek')
  get hostEmbedCompactPeekClass(): boolean {
    return this.isCompactPeek();
  }

  /** Lets the stylesheet lay the review surface out without a flag per rule. */
  @HostBinding('class.url-detail--embed-review')
  get hostEmbedReviewClass(): boolean {
    return this.embedReviewSurface;
  }

  url: string | null = null;
  pageUrlId = '';
  planClusters: string[] = [];
  pageSignals: InventoryPageSignal[] = [];
  pageSignalsLoading = false;
  /** Which pages compete, for the Cannibalization card; review surface only (`loadCompetingPages`). */
  private competingPages: CompetingPagesPayload | null = null;
  private competingPagesSubscription?: Subscription;
  /** The page's near-miss searches, for "You're close on these searches"; review surface only. */
  private nearMissSearches: NearMissPageResult | null = null;
  private nearMissSubscription?: Subscription;
  private internalLinkSignalRows: ActionPlanInternalLinkRow[] = [];
  private unclusteredSignalRows: ActionPlanUnclusteredPageRow[] = [];
  private signalsSubscription: Subscription | null = null;
  analysisData: any = null;
  pageDemand: PageDemandSnapshot | null = null;
  pageRealization: PageRealizationSnapshot | null = null;
  pageHealth: PageHealthSnapshot | null = null;
  pageSecondaryHealth: SecondaryPageHealthSnapshot | null = null;
  pageSerp: PageSerpSnapshot | null = null;
  pageIntentDiagnosis: PageIntentDiagnosis | null = null;
  /** Claim override modal (P2). */
  claimOverrideOpen = false;
  claimOverrideLabel = '';
  claimOverrideSaving = false;
  claimOverrideError: string | null = null;
  /** Shown on SERP card after override when claim saved but refresh partially failed. */
  claimOverrideNotice: string | null = null;
  /** Staged progress for intent override — surfaced on Expand playbook. */
  claimOverrideProgress: ClaimOverrideRefreshProgress | null = null;
  /** Vocabulary chip hover — highlights folded matches in winner snippets. */
  hoveredVocabChip: string | null = null;
  /** Winner excerpts: collapsed = top 5; expanded = full API pool (scrolls in the box). */
  winnerExcerptsExpanded = false;
  markedCompetitorDomains: string[] = [];
  urlSerpRefreshRunning = false;
  urlSerpForceRefresh = false;
  urlSerpRefreshProgress: UrlSerpRefreshProgress | null = null;
  pageRefetchRunning = false;
  pageRefetchStatus: string | null = null;
  private pageRefetchPollTimer: ReturnType<typeof setTimeout> | null = null;
  serpSectionExpanded = false;
  pageTopicsExpanded = false;
  topicOwnershipStatus: 'idle' | 'loading' | 'ready' | 'error' = 'idle';
  topicOwnershipDomains: TopicOwnershipDomain[] = [];
  /** Per-domain expand for supporting-page tables (dev view). */
  private topicOwnershipPagesExpandedByDomain = new Set<string>();
  /** User view: one expand control for the whole supporting-pages card. */
  topicOwnershipUserExpanded = false;
  topicOwnershipCompareUrl = '';
  private topicOwnershipLoadGen = 0;
  /**
   * Which analysis load is the current one (TECHNICAL_DEBT §0ai).
   *
   * The belt keeps ONE instance of this component and changes `embedUrl`, so
   * two steps taken inside one round-trip put two analysis requests in flight
   * on the same instance. Without this counter the LAST response to arrive
   * wins, and that can be the older one: page 8 rendered with page 9's SERP
   * and quoted page 9's text as "how you answer". It also silently suppressed
   * page 8's rival evaluation, because `ensureMarkedSerpCompetitorEvals`
   * seeds itself from `pageSerp`.
   *
   * The by-topic and opportunity loads have carried this guard for the same
   * reason; the analysis load — the one that fills the whole card — did not.
   */
  private analysisLoadGen = 0;
  /** Best-effort cancel of a superseded analysis request; the counter above is what makes it correct. */
  private analysisSubscription?: Subscription;
  /** True when no precomputed snapshot exists — build from Crawl Management (or dev rebuild). */
  topicOwnershipCacheMiss = false;
  topicOwnershipSnapshotGeneratedAt: string | null = null;
  supportingPagesV1RebuildRunning = false;
  supportingPagesOppRebuildRunning = false;
  supportingPagesRebuildStatus: string | null = null;
  /** Supporting-page opportunity scoring (embedding + section gaps). */
  topicOwnershipV2Status: 'idle' | 'loading' | 'ready' | 'error' = 'idle';
  topicOwnershipV2Data: SupportingPagesV2EvalData | null = null;
  topicOwnershipV2Error: string | null = null;
  /** On-demand section-gap eval for marked competitors on this page's SERP. */
  markedSerpCompetitorV2Rows: SupportingPagesV2RowResult[] = [];
  /**
   * A live marked-competitor evaluation is in flight.
   *
   * Tracked because it is a THIRD source for the Supporting pages card, behind
   * the snapshot and the opportunity snapshot, and unlike those two it lands
   * after the card has already reported itself ready. On a deep-dive surface
   * that is fine — rows arriving is the page filling in. On the review surface
   * it means a verdict changes while the operator is reading it.
   */

  /**
   * The in-flight marked-competitor evaluation, so leaving the page cancels it.
   *
   * It was untracked, which meant every page advance left its request running.
   * The belt destroys and rebuilds this component on every step — twice over,
   * once for the visible page and once for the prefetched one — so walking ten
   * pages quickly left a queue of abandoned evaluations against a sidecar that
   * is effectively single-threaded (TECHNICAL_DEBT §0ad), and the page actually
   * on screen waited behind all of them. Reported as "over 2-3 minutes".
   */
  /**
   * The Supporting pages opportunity evaluation — the third and slowest source
   * for that card, and the one the review surface waits on.
   *
   * `InFlightRequest` rather than a generation counter, a subscription and a
   * pending flag as three fields: those three desynced twice here, both times
   * leaving the surface waiting on a request that had been abandoned. See the
   * note on the class.
   */
  private readonly markedSerpEval = new InFlightRequest();
  topicOwnershipV2DetailUrl = '';
  /** Precomputed "Top SERP rival" coverage for this page (from the Setup step). */
  serpRivalCoverage: SerpRivalCoverageData | null = null;
  /** Resolved compare target + its facts, from `GET /urls/analysis`. */
  pageCompare: CompareSummary | null = null;
  /** This page's estimate beside the compare target's, from the same payload (`pageEstimatePair`). */
  pageEstimatePair: PageEstimatePairSource | null = null;
  topicOwnershipV2CoveredElsewhereUrl = '';
  topicOwnershipV2ListInventoryUrl = '';
  topicOwnershipV2RankDrivenUrl = '';
  topSerpRivalRankDrivenOpen = false;
  topicOwnershipV2TextUrl = '';
  topicOwnershipV2CalUrl = '';
  /** Collapsed side-by-side section compare (competitorUrl + sectionNumber); shown by default. */
  private topicOwnershipV2SectionCompareCollapsedKeys = new Set<string>();
  /** Compare detail filter for section coverage rows. */
  topicOwnershipV2CompareFilter: 'not_on_page' | 'gaps' | 'all' = 'not_on_page';
  private topicOwnershipV2LoadGen = 0;
  private supportingPageLexicalByUrl = new Map<string, PageLexicalFields>();
  /** Focal-page anchor topics — narrows your-site matcher pools (dev + opportunity scoring). */
  focalAnchorTopicSlugs = new Set<string>();
  focalAnchorTopicLabels: string[] = [];
  /** Documented in topic-supporting-pages.ts — exposed for template hint. */
  readonly supportingPageMinRelevance = SUPPORTING_PAGE_MIN_RELEVANCE_SCORE;
  readonly supportingPageSimilarityMin = SUPPORTING_PAGE_SIMILARITY_MIN_SCORE;
  analysisStatus: 'idle' | 'loading' | 'success' | 'error' = 'idle';

  /**
   * The review card rules, shared with the server-side agent export
   * (`shared/src/review/review-page-model.ts`). Every getter reads this
   * component's live field, so the model never holds a stale copy.
   */
  private readonly reviewSource = this.reviewPageSource();
  private readonly reviewModel = new ReviewPageModel(this.reviewSource);

  private reviewPageSource(): ReviewPageSource {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const c = this;
    return {
      get url() { return c.url; },
      get pageUrlId() { return c.pageUrlId; },
      get embedPreset() { return c.embedPreset; },
      get embedActionsOnly() { return c.embedActionsOnly; },
      get embedReviewSurface() { return c.embedReviewSurface; },
      get embedCompactPeek() { return c.embedCompactPeek; },
      get analysisData() { return c.analysisData; },
      get analysisStatus() { return c.analysisStatus; },
      get pageDemand() { return c.pageDemand; },
      get pageSerp() { return c.pageSerp; },
      get pageIntentDiagnosis() { return c.pageIntentDiagnosis; },
      get pageCompare() { return c.pageCompare; },
      get pageEstimatePair() { return c.pageEstimatePair; },
      get markedCompetitorDomains() { return c.markedCompetitorDomains; },
      get focalAnchorTopicSlugs() { return c.focalAnchorTopicSlugs; },
      get topicOwnershipStatus() { return c.topicOwnershipStatus; },
      get topicOwnershipDomains() { return c.topicOwnershipDomains; },
      get topicOwnershipV2Status() { return c.topicOwnershipV2Status; },
      get topicOwnershipV2Data() { return c.topicOwnershipV2Data; },
      get markedSerpCompetitorV2Rows() { return c.markedSerpCompetitorV2Rows; },
      get markedSerpEvalPending() { return c.markedSerpEval.pending; },
      get serpRivalCoverage() { return c.serpRivalCoverage; },
      get planClusters() { return c.planClusters; },
      get pageSignals() { return c.pageSignals; },
      get pageSignalsLoading() { return c.pageSignalsLoading; },
      get internalLinkSignalRows() { return c.internalLinkSignalRows; },
      get unclusteredSignalRows() { return c.unclusteredSignalRows; },
      get competingPages() { return c.competingPages; },
      get nearMissSearches() { return c.nearMissSearches; },
      get siteWideIssueCodes() { return c.siteWideIssueCodes; },
      get sharedIssueCauses() { return c.sharedIssueCauses; },
      get ownSiteUrls() { return c.ownSiteUrls; },
      get issueRecords() { return c.planV2IssueQueue.records; },
      isIssueVisible: (issueKey: string) => c.planV2IssueQueue.isVisibleInIssuesEmbed(issueKey)
    };
  }
  contentSectionCollapsed: boolean = true;
  linksSectionCollapsed: boolean = true;
  /** Expanded on-page issue detail rows (key: `critical:0` / `warning:1`). */
  private technicalIssueDetailsExpanded = new Set<string>();
  technicalCriticalComposing = false;
  topicOwnershipComposing = false;
  topicOwnershipComposeState: TopicOwnershipComposeState | null = null;
  internalLinksComposing = false;
  @ViewChild('internalLinksSection') internalLinksSectionRef?: ElementRef<HTMLElement>;
  @ViewChild('serpCompareYours') serpCompareYoursRef?: ElementRef<HTMLElement>;
  @ViewChild('serpCompareWinners') serpCompareWinnersRef?: ElementRef<HTMLElement>;
  technicalWarningsComposing = false;
  contentElements: ContentElement[] = [];
  
  // Link data
  internalLinks: any[] = [];
  linkFilter: 'all' | 'structural' | 'contextual' | 'same_page' = 'all';
  linkSort: 'type' | 'confidence' | 'anchor' | 'url' = 'type';
  competitorCoverageStatus: 'idle' | 'loading' | 'ready' | 'error' = 'idle';
  competitorCoverageMessage: string = '';
  private competitorTopicMatchesBySlug: Record<string, CompetitorTopicMatch[]> = {};
  private competitorTopicTooltipCache: Record<string, CompetitorTopicTooltipState> = {};
  private activeCompetitorTooltipKey: string | null = null;
  private competitorTooltipHideTimer: ReturnType<typeof setTimeout> | null = null;

  quickCompareOpen = false;
  quickComparePrimaryUrl: string | null = null;
  quickCompareCompareUrl: string | null = null;

  planV2NextAction: ActionPlanV2Row | null = null;
  planV2NextActionLoading = false;
  planV2NextActionError: string | null = null;
  /**
   * The crawl pair the Next action is being read against (LB1) — the same
   * period string the dashboard and the Issues header show. Null when this
   * domain has no captured pair; the section then says nothing about periods
   * rather than dating advice it cannot date.
   */
  planV2Period: CrawlRunPeriodView | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dataForSEOService: DataForSEOService,
    private compareContextService: CompareContextService,
    private domainTopicsService: DomainTopicsService,
    private featureFlags: FeatureFlagService,
    private urlSerpSync: UrlSerpSyncService,
    private inventoryPlanClusters: InventoryPlanClustersService,
    private domainContext: DomainContextService,
    readonly planV2IssueQueue: PlanV2IssueQueueService,
    private readonly reviewSelection: ReviewSelectionService,
    private readonly planV2EmbedContext: PlanV2EmbedContextService,
    private readonly crawlRunPeriod: CrawlRunPeriodService,
    private readonly technicalRollup: TechnicalRollupService,
    private readonly ownSiteUrlsService: OwnSiteUrlsService,
    private readonly competingPagesService: CompetingPagesService,
    private readonly nearMissSearchesService: NearMissSearchesService
  ) {}

  isDevView(): boolean {
    return !this.featureFlags.isProduction() && !this.featureFlags.isUserView();
  }

  /** User-mode columns/links layout (also used for Plan v2 embed regardless of dev flag). */
  useTopicOwnershipUserLayout(): boolean {
    return !this.isDevView() || this.isPlanV2Embed();
  }

  isActionPlanV2Enabled(): boolean {
    return this.featureFlags.isEnabled('actionPlanV2');
  }

  isEmbedMode(): boolean {
    return this.embedPreset === 'planV2';
  }

  isPlanV2Embed(): boolean {
    return this.reviewModel.isPlanV2Embed();
  }

  isCompactPeek(): boolean {
    return this.reviewModel.isCompactPeek();
  }

  /**
   * Dashboard package row, expanded: one row per `/review` card (TECHNICAL_DEBT §0ck).
   *
   * Owner's rule (2026-10-29): a page with a card on `/review` says so here too, and this panel says
   * nothing `/review` does not. The rows used to be built here by eight builders calling the card
   * predicates one at a time, and every card added after them went missing from the Dashboard. They
   * are now `dashboardPageRows()` over the belt's own cards (`shared/src/review/dashboard-page-rows.ts`),
   * so wording, counts and gates are the card's. This keeps only the triage payload, which carries the
   * Issues queue's keys.
   */
  compactPeekEvidence(): PageEvidence[] {
    if (!this.isCompactPeek() || this.analysisStatus !== 'success' || !this.analysisData) {
      return [];
    }
    return dashboardPageRows(this.reviewModel.reviewSurfaceCards()).map((row) => ({
      ...row,
      payload: this.peekRowPayload(row.key)
    }));
  }

  /** The Dismiss / Work item payload for a row: the same one the card's own buttons send. */
  private peekRowPayload(key: string): PlanV2IssueCardPayload | null {
    switch (key) {
      case 'topic_ownership':
        return this.buildTopicOwnershipIssuePayload();
      case 'freshness':
        return this.buildFreshnessIssuePayload();
      case 'serp_phrase':
        return this.buildSerpPhraseIssuePayload();
      case 'serp_intent':
        return this.buildSerpIntentIssuePayload();
      case 'technical_critical':
        return this.buildTechnicalCriticalIssuePayload();
      case 'technical_warnings':
        return this.buildTechnicalWarningsIssuePayload();
      case 'internal_link':
        return this.buildInternalLinksSectionPayload();
      case 'cannibalization':
        return this.buildCannibalizationIssuePayload();
      case 'near_miss_searches':
        return this.buildNearMissIssuePayload();
      case 'signals':
        return this.buildSignalsIssuePayload();
      default:
        // Sections: triage lives on the topic-ownership issue, which a section gap resolves into; a
        // second payload would double-count it in the queue. Notes are not work.
        return null;
    }
  }

  private peekPanelCache: PageEvidencePanel | null = null;
  private peekPanelInputs: unknown[] = [];

  /**
   * Ordered and bucketed. The template reads this, not the list.
   *
   * MEMOISED, and that is not an optimisation. A method returning a fresh
   * object on every call hands `*ngFor` a new array identity on every
   * change-detection pass, so every row re-renders on every pass — the same
   * trap the Dashboard's coverage series hit, where it hung the page.
   *
   * The inputs are every field of the card rules' source, read live, so a load
   * that lands after first paint (rival coverage, page signals, competing pages)
   * rebuilds the panel. A hand-picked key missed some of them once, and a row
   * never appeared. The records map is changed in place, so its size is read too.
   */
  compactPeekPanel(): PageEvidencePanel {
    const src = this.reviewSource as unknown as Record<string, unknown>;
    const inputs = [...Object.keys(src).map((k) => src[k]), this.planV2IssueQueue.records.size];
    const same =
      this.peekPanelCache !== null &&
      inputs.length === this.peekPanelInputs.length &&
      inputs.every((v, i) => v === this.peekPanelInputs[i]);
    if (!same) {
      this.peekPanelInputs = inputs;
      this.peekPanelCache = buildPageEvidencePanel(this.compactPeekEvidence());
    }
    return this.peekPanelCache!;
  }

  compactPeekPanelEmpty(): boolean {
    return isPageEvidenceEmpty(this.compactPeekPanel());
  }

  /** The page the peek's Compare button opens on, or null when there is none. */
  compareRowTargetUrl(): string | null {
    return compareDrawerTarget(this.pageCompare)?.url ?? null;
  }

  /** Names the rival on the button, so the recap still says who without a row. */
  compareRowTargetDomain(): string | null {
    return compareDrawerTarget(this.pageCompare)?.domain ?? null;
  }

  /**
   * Open the comparison already pointed at the resolved target (§3.1).
   *
   * The dropdown in the Supporting pages card becomes an override rather than a
   * prerequisite — the operator no longer has to know which of the rival's
   * pages is the real peer, which is the decision §2 says they are not well
   * placed to make.
   */
  openCompareFromPeek(): void {
    const url = this.compareRowTargetUrl();
    if (url) this.openQuickCompareWithUrl(url);
  }

  compactPeekReady(): boolean {
    if (!this.isCompactPeek()) return false;
    if (this.analysisStatus === 'loading') return false;
    if (this.analysisStatus !== 'success' || !this.analysisData) return true;
    // Wait until the cards are final, as the belt does before it shows a page (`ngDoCheck`): the
    // rows are the cards, and a row that appears or changes a beat later reads as a different answer.
    return !this.supportingPagesSettling() && !this.pageSignalsLoading;
  }

  embedActionsOnlyActive(): boolean {
    return this.reviewModel.embedActionsOnlyActive();
  }

  /** The badge tone class for a diagnosis chip. The template binds this; the
   *  visibility predicates below bind the tone itself. */
  diagnosisChipClass(tone: DiagnosisTone): string {
    return DIAGNOSIS_BADGE_CLASS[tone];
  }

  private embedDiagnosisIsGreen(tone: DiagnosisTone): boolean {
    return this.reviewModel.embedDiagnosisIsGreen(tone);
  }

  /**
   * `bad` is here for the same reason `warn` is, and its absence would be a
   * silent regression: actions-only mode shows a card only when its diagnosis
   * is something to act on, so a tone that means "a rival is already ahead of
   * you" dropping out of this list would hide the card exactly when it matters
   * most. `ok` and `muted` stay out — nothing to do, and nothing measured.
   */
  private embedDiagnosisIsActionable(tone: DiagnosisTone): boolean {
    return tone === 'warn' || tone === 'bad' || tone === 'neutral';
  }

  showEmbedMiniOverviewSection(): boolean {
    return this.isPlanV2Embed() && !this.embedActionsOnlyActive();
  }

  showPlanV2ExpandPlaybook(): boolean {
    return (
      this.isPlanV2Embed() &&
      this.embedPlanV2Row?.recommendedAction.type === 'prioritize'
    );
  }

  planV2ExpandPlaybook() {
    if (!this.embedPlanV2Row) return null;
    return buildPlanV2ExpandPlaybook({
      row: this.embedPlanV2Row,
      pageRealization: this.pageRealization,
      pageSerp: this.pageSerp,
      pageIntentDiagnosis: this.pageIntentDiagnosis,
      freshnessShowsWarn: !this.embedDiagnosisIsGreen(this.freshnessDiagnosisTone()),
      selfDomain: this.extractDomainFromUrl(this.url || ''),
      queueRank: this.embedPlanV2QueueRank,
      serpRivalSectionGapsReady: this.hasActiveSerpRivalSectionGaps(),
      rankDrivenGaps: this.topSerpRivalRankDrivenGaps()
    });
  }

  onPlanV2PlaybookStep(step: PlanV2ExpandPlaybookStep): void {
    if (step.kind === 'quick_compare' && step.compareUrl) {
      this.openQuickCompareWithUrl(step.compareUrl);
      return;
    }
    if (step.kind === 'scroll' && step.scrollTargetId) {
      this.scrollToPlanV2PlaybookTarget(step.scrollTargetId);
    }
  }

  private scrollToPlanV2PlaybookTarget(elementId: string): void {
    document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  showEmbedContentSection(): boolean {
    return this.reviewModel.showEmbedContentSection(Boolean(this.buildSectionGapIssuePayload()));
  }

  showEmbedSupportingPagesCard(): boolean {
    return this.reviewModel.showEmbedSupportingPagesCard();
  }

  showEmbedFreshnessSection(): boolean {
    return this.reviewModel.showEmbedFreshnessSection();
  }

  showEmbedSerpCompetitionCard(): boolean {
    // Review surface: this card is orientation — leaders, snippets and a
    // Quick Compare that spends money per click — and its one actionable
    // finding is already reported by the Sections card. Unchanged on Inventory.
    if (this.embedReviewSurface) return false;
    let base = Boolean(this.hasPageSerpSnapshot() && this.serpCompetitionInsight());
    if (base && this.embedActionsOnlyActive()) {
      base = this.embedDiagnosisIsActionable(this.serpCompetitionChipTone());
    }
    return this.embedIssueVisible(this.planV2IssueKey('serp_competition'), base);
  }

  showEmbedSerpPhraseCard(): boolean {
    return this.reviewModel.showEmbedSerpPhraseCard();
  }

  showEmbedSerpDifficultyCard(): boolean {
    // Not on the belt. Difficulty is a property of the KEYWORD, not a finding
    // about this page: there is nothing to send and nothing to dismiss, which
    // is why the card is the only one on the screen without a Sending tick. It
    // is worth knowing before committing a writer, and that is what the header's
    // keyword line and the deep dive are for.
    if (this.embedReviewSurface) return false;
    let base = true;
    if (this.embedActionsOnlyActive()) {
      base = this.embedDiagnosisIsActionable(this.serpDifficultyActionChipTone());
    }
    return this.embedIssueVisible(this.planV2IssueKey('serp_difficulty'), base);
  }

  showEmbedSerpIntentCard(): boolean {
    return this.reviewModel.showEmbedSerpIntentCard();
  }

  showEmbedSerpAnyCard(): boolean {
    return (
      this.showEmbedSerpCompetitionCard() ||
      this.showEmbedSerpPhraseCard() ||
      this.showEmbedSerpDifficultyCard() ||
      this.showEmbedSerpIntentCard()
    );
  }

  showEmbedSerpSection(): boolean {
    if (!this.isPlanV2Embed() || !this.embedActionsOnlyActive()) return true;
    if (!this.hasPageSerpSnapshot()) return true;
    return this.showEmbedSerpAnyCard();
  }

  showEmbedSerpResultsTable(): boolean {
    if (this.isCompactPeek()) return false;
    if (!this.serpSectionExpanded || !this.hasPageSerpSnapshot()) return false;
    if (!this.embedActionsOnlyActive()) return true;
    return this.showEmbedSerpAnyCard();
  }

  showEmbedTechnicalIssuesCard(): boolean {
    return this.reviewModel.showEmbedTechnicalIssuesCard();
  }

  showEmbedTechnicalWarningsCard(): boolean {
    return this.reviewModel.showEmbedTechnicalWarningsCard();
  }

  showEmbedTechnicalSection(): boolean {
    if (!this.isPlanV2Embed() || !this.embedActionsOnlyActive()) return true;
    return this.showEmbedTechnicalIssuesCard() || this.showEmbedTechnicalWarningsCard();
  }

  showEmbedSignalsSection(): boolean {
    return this.reviewModel.showEmbedSignalsSection();
  }

  /** Issues drawer: own focus card (same send animation as Technical). */
  showEmbedInternalLinksSection(): boolean {
    if (!this.isPlanV2Embed()) return false;
    const key = this.planV2IssueKey('internal_link');
    if (
      this.planV2IssueQueue.isCardExiting(key, 'work_item') ||
      this.planV2IssueQueue.isCardExiting(key, 'dismissed')
    ) {
      return true;
    }
    return this.reviewModel.reviewInternalLinkRows().length > 0;
  }

  /**
   * Empty because the operator emptied it, rather than because nothing is wrong.
   *
   * `/templates/standard-operating-procedure-template` carries five dismissals
   * and no work items — section gap, both technical cards, answer structure and
   * freshness — so the belt had nothing to render and told the operator
   * "everything looks clear". It was not clear; it was cleared, by them, and
   * saying so is the difference between a page they can trust and one they
   * have to go and check.
   */
  embedEmptyBecauseDismissed(): boolean {
    const urlId = (this.pageUrlId || '').trim();
    if (!urlId) return false;
    for (const record of this.planV2IssueQueue.records.values()) {
      if (String(record?.urlId || '') !== urlId) continue;
      if (record.queue === 'dismissed' && record.issueType !== 'check_muted') return true;
    }
    return false;
  }

  embedActionsOnlyEmpty(): boolean {
    if (!this.embedActionsOnlyActive() || this.analysisStatus !== 'success' || !this.analysisData) {
      return false;
    }
    return (
      !this.showEmbedMiniOverviewSection() &&
      // A held-back comparison is a statement about the page, not an all-clear.
      !this.reviewComparisonWithheld() &&
      !this.showEmbedContentSection() &&
      !this.showEmbedFreshnessSection() &&
      !this.showEmbedSerpSection() &&
      !this.showEmbedTechnicalSection() &&
      !this.showEmbedInternalLinksSection() &&
      !this.showEmbedCannibalizationSection() &&
      !this.showEmbedNearMissSection() &&
      !this.showEmbedSignalsSection()
    );
  }

  planV2IssueDomain(): string {
    const fromInput = (this.embedPlanV2Domain || '').trim();
    if (fromInput) return fromInput.toLowerCase().replace(/^www\./, '');
    return this.extractDomainFromUrl(this.url || '');
  }

  planV2IssueKey(issueType: PlanV2IssueType): string {
    return this.reviewModel.planV2IssueKey(issueType);
  }

  private embedIssueVisible(issueKey: string, baseVisible: boolean): boolean {
    return this.reviewModel.embedIssueVisible(issueKey, baseVisible);
  }

  planV2CardExitClass(issueKey: string): Record<string, boolean> {
    return {
      'plan-v2-card-exit-work': this.planV2IssueQueue.isCardExiting(issueKey, 'work_item'),
      'plan-v2-card-exit-dismiss': this.planV2IssueQueue.isCardExiting(issueKey, 'dismissed')
    };
  }

  buildTopicOwnershipIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url) return null;
    const base: PlanV2IssueCardPayload = {
      issueKey: buildPlanV2IssueKey(urlId, 'topic_ownership'),
      urlId,
      url,
      issueType: 'topic_ownership',
      cardTitle: 'Supporting pages',
      changeSummary: 'Close topic coverage gaps on competitors (add or strengthen supporting URLs).',
      why: this.hasTopicOwnershipCoverageGap()
        ? 'Competitors have relevant supporting URLs without a strong match on your site.'
        : this.topicOwnershipGapChipLabel(),
      diagnosisLabel: this.showTopicOwnershipGapChip() ? this.topicOwnershipGapChipLabel() : null,
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
    return this.embedReviewSurface ? this.reviewSupportingPagesWorkItem(base) : base;
  }

  /** The gap URLs this card will send — the ticked ones, in card order. */
  private reviewSupportingPagesPicked(): string[] {
    if (!this.embedReviewSurface) return [];
    return this.reviewSelection.tickedItemIds(
      (this.pageUrlId || '').trim(),
      this.reviewCardKey('topic_ownership')
    );
  }

  /**
   * A supporting-pages work item that lists the pages, not just names them.
   *
   * Same composer Inventory's compose step ends in, fed a state whose only
   * selections are the ticked rows. The review card was sending a fixed
   * sentence and a `why` of up to six paths with no `actionDetail`, so what
   * reached a writer was a list of paths with no indication of what to do with
   * them — and only the first six of them.
   *
   * Tools and sections are deliberately empty. This card is pages now; sections
   * moved to their own card, and the list-tool gap has no representation on the
   * review surface at all. Passing them as unselected rather than omitting the
   * concept keeps this honest against the composer's own shape.
   */
  private reviewSupportingPagesWorkItem(base: PlanV2IssueCardPayload): PlanV2IssueCardPayload {
    const picked = new Set(this.reviewSupportingPagesPicked());
    if (!picked.size) return base;

    const pageGaps = this.reviewSupportingPagesColumns().flatMap((col) =>
      col.gaps.map((gap) => ({
        id: `page::${col.domain}::${gap.url}`,
        competitorDomain: col.domain,
        competitorUrl: gap.url,
        path: gap.path,
        topicLabel: 'Supporting page',
        selected: picked.has(gap.url)
      }))
    );

    return composeTopicOwnershipWorkItemFromState(base, {
      pageGaps,
      toolGaps: [],
      headingMetas: [],
      selectedHeadingIds: new Set<string>(),
      activeCompetitorUrl: '',
      activeListCompetitorUrl: '',
      sectionCompetitors: [],
      listCompetitors: []
    });
  }

  needsWorkItemCompose(issueType: PlanV2IssueType): boolean {
    return (
      issueType === 'serp_phrase' ||
      issueType === 'topic_ownership' ||
      issueType === 'internal_link' ||
      issueType === 'technical_critical' ||
      issueType === 'technical_warnings'
    );
  }

  enrichWorkItemPayload(payload: PlanV2IssueCardPayload): PlanV2IssueCardPayload {
    if (payload.issueType === 'serp_phrase') {
      return composeSerpPhraseWorkItem(
        payload,
        this.pageIntentDiagnosis,
        this.pageSerpTargetQueryLabel()
      );
    }
    if (payload.issueType === 'internal_link') {
      const pageUrlId = this.pageUrlId.trim();
      const legacyRow = this.internalLinkRowForPayload(payload);
      if (legacyRow) {
        return composeInternalLinkWorkItem(payload, legacyRow, pageUrlId);
      }
      const options = listInternalLinkComposeOptions(
        this.internalLinkSignalRows,
        pageUrlId,
        internalLinkSuggestionKeysInWorkItems(this.planV2IssueQueue.records)
      );
      return composeInternalLinksWorkItem(payload, options, pageUrlId);
    }
    if (payload.issueType === 'freshness') {
      return composeFreshnessWorkItem(payload, this.freshnessWorkItemContext());
    }
    if (payload.issueType === 'serp_intent') {
      return composeSerpIntentWorkItem(payload, this.pageIntentDiagnosis);
    }
    return payload;
  }

  private freshnessWorkItemContext() {
    return buildFreshnessWorkItemContext({
      lastContentModified: this.analysisData?.lastContentModified ?? null,
      serpResults: this.pageSerp?.results ?? null
    });
  }

  beginTopicOwnershipCompose(): boolean {
    const state = buildTopicOwnershipComposeState({
      // The columns the card renders, not the raw blocks. The blocks keep each
      // domain's hero — its main competing page — so the picker was offering
      // "write this page" for a page this site already has, and offering pages
      // from domains never marked as competitors. Whatever is ticked here
      // becomes a work item somebody acts on, which makes this the one place
      // that miscount actually cost something.
      domains: this.getSupportingPagesColumns().map((col) => ({
        domain: col.domain,
        isYourSite: col.isYourSite,
        rows: col.supportingRows
      })),
      v2Rows: this.topicOwnershipV2Data?.rows,
      extractDomain: (pageUrl) => this.extractDomainFromUrl(pageUrl),
      displaySegmentsForSection: (sc) => this.topicOwnershipV2SectionDisplayHeadingSegments(sc)
    });
    if (!state) return false;
    this.topicOwnershipComposeState = state;
    if (state.activeCompetitorUrl) {
      this.topicOwnershipV2DetailUrl = state.activeCompetitorUrl;
      this.topicOwnershipV2CoveredElsewhereUrl = '';
      this.topicOwnershipV2ListInventoryUrl = '';
      this.topicOwnershipV2RankDrivenUrl = '';
    }
    return true;
  }

  endTopicOwnershipCompose(): void {
    this.topicOwnershipComposeState = null;
    this.topicOwnershipV2DetailUrl = '';
  }

  getTopicOwnershipComposeState(): TopicOwnershipComposeState | null {
    return this.topicOwnershipComposeState;
  }

  topicOwnershipComposeHasSelection(): boolean {
    return topicOwnershipComposeHasSelection(this.topicOwnershipComposeState);
  }

  finalizeTopicOwnershipComposeWorkItem(base: PlanV2IssueCardPayload): PlanV2IssueCardPayload {
    return composeTopicOwnershipWorkItemFromState(base, this.topicOwnershipComposeState);
  }

  onTopicOwnershipComposingChange(composing: boolean): void {
    this.topicOwnershipComposing = composing;
    if (!composing) {
      this.endTopicOwnershipCompose();
    }
  }

  topicOwnershipComposeHasPageGaps(): boolean {
    return (this.topicOwnershipComposeState?.pageGaps.length ?? 0) > 0;
  }

  topicOwnershipComposeHasSectionGaps(): boolean {
    return (this.topicOwnershipComposeState?.sectionCompetitors.length ?? 0) > 0;
  }

  topicOwnershipComposeHasToolGaps(): boolean {
    return (this.topicOwnershipComposeState?.listCompetitors.length ?? 0) > 0;
  }

  topicOwnershipComposeToolsForActiveCompetitor() {
    const state = this.topicOwnershipComposeState;
    const url = state?.activeListCompetitorUrl?.trim();
    if (!state || !url) return [];
    const key = normalizeUrlForCompare(url);
    return state.toolGaps.filter((tool) => normalizeUrlForCompare(tool.competitorUrl) === key);
  }

  onTopicOwnershipComposeListCompetitorChange(competitorUrl: string): void {
    if (!this.topicOwnershipComposeState) return;
    this.topicOwnershipComposeState.activeListCompetitorUrl = competitorUrl;
  }

  topicOwnershipComposeActiveV2Row(): SupportingPagesV2RowResult | null {
    const url = this.topicOwnershipComposeState?.activeCompetitorUrl?.trim();
    if (!url) return null;
    return (
      this.topicOwnershipV2Data?.rows?.find(
        (row) => normalizeUrlForCompare(row.competitorUrl) === normalizeUrlForCompare(url)
      ) ?? null
    );
  }

  onTopicOwnershipComposeCompetitorChange(competitorUrl: string): void {
    if (!this.topicOwnershipComposeState) return;
    this.topicOwnershipComposeState.activeCompetitorUrl = competitorUrl;
    this.topicOwnershipV2DetailUrl = competitorUrl;
    this.topicOwnershipV2CoveredElsewhereUrl = '';
    this.topicOwnershipV2ListInventoryUrl = '';
  }

  topicOwnershipComposeSectionRows(row: SupportingPagesV2RowResult): SectionCoverageRow[] {
    return this.topicOwnershipV2SectionCoverageGroups(row);
  }

  isTopicOwnershipComposeHeadingSelected(
    row: SupportingPagesV2RowResult,
    sc: SectionCoverageRow,
    segIndex: number
  ): boolean {
    const id = topicOwnershipHeadingComposeId(row.competitorUrl, sc, segIndex);
    return this.topicOwnershipComposeState?.selectedHeadingIds.has(id) ?? false;
  }

  topicOwnershipHeadingTreeContext(
    v2Row: SupportingPagesV2RowResult,
    sc: SectionCoverageRow,
    composeSelect: boolean
  ) {
    return {
      $implicit: this.topicOwnershipV2SectionDisplayHeadingSegments(sc),
      composeSelect,
      v2Row,
      sc
    };
  }

  /** User view defaults sections to expanded compare; compose matches that. */
  topicOwnershipV2SectionCompareExpanded(
    competitorUrl: string,
    sc: SectionCoverageRow,
    composeSelect = false
  ): boolean {
    if (composeSelect && this.topicOwnershipV2SectionCompareAvailable(sc)) return true;
    return this.isTopicOwnershipV2SectionFullTextExpanded(competitorUrl, sc);
  }

  toggleTopicOwnershipComposeHeading(
    event: Event,
    row: SupportingPagesV2RowResult,
    sc: SectionCoverageRow,
    segIndex: number
  ): void {
    if (!this.topicOwnershipComposing || !this.topicOwnershipComposeState) return;
    if (event instanceof KeyboardEvent && event.key === ' ') {
      event.preventDefault();
    }
    event.stopPropagation();
    const id = topicOwnershipHeadingComposeId(row.competitorUrl, sc, segIndex);
    const next = this.topicOwnershipComposeState.selectedHeadingIds;
    if (next.has(id)) next.delete(id);
    else next.add(id);
  }

  getTechnicalIssueComposeOptions(
    issueType: 'technical_critical' | 'technical_warnings'
  ): TechnicalIssueComposeOption[] {
    const kind = issueType === 'technical_critical' ? 'critical' : 'warning';
    const issues =
      kind === 'critical' ? this.technicalCriticalIssueViews() : this.technicalWarningIssueViews();
    return listTechnicalIssueComposeOptions(issues, kind);
  }

  finalizeTechnicalIssuesWorkItem(
    base: PlanV2IssueCardPayload,
    issueType: 'technical_critical' | 'technical_warnings',
    selected: TechnicalIssueComposeOption[]
  ): PlanV2IssueCardPayload {
    const severity = issueType === 'technical_critical' ? 'critical' : 'warning';
    return composeTechnicalIssuesWorkItem(base, selected, severity);
  }

  getInternalLinkComposeOptions(): InternalLinkComposeOption[] {
    return listInternalLinkComposeOptions(
      this.internalLinkSignalRows,
      this.pageUrlId.trim(),
      internalLinkSuggestionKeysInWorkItems(this.planV2IssueQueue.records)
    );
  }

  finalizeInternalLinksWorkItem(
    base: PlanV2IssueCardPayload,
    selected: InternalLinkComposeOption[]
  ): PlanV2IssueCardPayload[] {
    return composeInternalLinkWorkItemsFromSelection(base, selected);
  }

  private syncPlanV2EmbedHost(): void {
    this.planV2EmbedContext.registerHost(this.isPlanV2Embed() ? this : null);
  }

  buildFreshnessIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url) return null;
    const verdict = this.contentFreshnessVerdict();
    return {
      issueKey: buildPlanV2IssueKey(urlId, 'freshness'),
      urlId,
      url,
      issueType: 'freshness',
      cardTitle: 'Content freshness',
      changeSummary: 'Refresh page content to align with SERP freshness expectations.',
      why: verdict || this.freshnessDiagnosisLabel(),
      diagnosisLabel: this.freshnessDiagnosisLabel(),
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
  }

  buildSerpCompetitionIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url) return null;
    return {
      issueKey: buildPlanV2IssueKey(urlId, 'serp_competition'),
      urlId,
      url,
      issueType: 'serp_competition',
      cardTitle: "Who's beating you?",
      changeSummary: 'Improve rankings vs marked competitors on this target query.',
      why: `Your rank: ${this.serpCompetitionYourRankLabel()}. ${this.serpCompetitionChipLabel()}.`,
      diagnosisLabel: this.serpCompetitionChipLabel(),
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
  }

  /**
   * "Missing sections" — the review surface's second content card.
   *
   * Only ever built on the review surface, and only when there is something to
   * decide. Inventory keeps answering both questions in the topic card above,
   * where an operator working out what is going on wants them side by side;
   * splitting them there would cost the comparison and buy nothing, because
   * nothing on Inventory is being sent.
   */
  reviewAnswerStructure(): {
    instruction: string | null;
    vocab: ReviewAnswerVocab | null;
    yours: string | null;
    theirs: Array<{ position: number; domain: string; segments: SerpSnippetSegment[] }>;
  } | null {
    return this.reviewModel.reviewAnswerStructure();
  }

  /**
   * The count badge Missing sections was missing.
   *
   * Counts whatever the card is SHOWING, which is not always sections: the card
   * also holds rank-driven gaps — queries a rival outranks you on with ground
   * you already cover — and a page with those and no section gaps rendered a
   * full card with no badge at all, which is the thing this badge was added to
   * fix.
   *
   * Three labels rather than one, because "3 SECTIONS" over three queries would
   * be wrong and "3 GAPS" over one section is vaguer than it needs to be. The
   * mixed case is the only one that has to generalise.
   */
  reviewGapsCardTitle(): string {
    return this.reviewModel.reviewGapsCardTitle();
  }

  reviewMissingSectionsChipLabel(): string | null {
    return this.reviewModel.reviewMissingSectionsChipLabel();
  }

  buildSectionGapIssuePayload(): PlanV2IssueCardPayload | null {
    if (!this.embedReviewSurface) return null;
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url) return null;

    // Every other card gates on this and Missing sections did not, so a page
    // whose sections had already been sent still rendered the card — ticked,
    // with no Send button and nothing to do. That is the "why am I looking at
    // this again" page, and it is this line.
    const key = buildPlanV2IssueKey(urlId, 'section_gap');
    if (
      !this.planV2IssueQueue.isVisibleInIssuesEmbed(key) &&
      !this.planV2IssueQueue.isCardExiting(key, 'work_item') &&
      !this.planV2IssueQueue.isCardExiting(key, 'dismissed')
    ) {
      return null;
    }

    const content = this.reviewModel.reviewSectionGapContent();
    if (!content) return null;
    const { missing, rank } = content;

    const allRows = missing?.rows ?? [];
    const cardKey = buildPlanV2IssueKey(urlId, 'section_gap');
    // Before the rows are registered there is nothing to filter by, and the
    // card is showing all of them — so the payload says all of them. Asking
    // whether rows exist rather than whether any are ticked keeps "not yet
    // registered" apart from "operator unticked every one", which are opposite
    // situations that an empty set would make look the same.
    const ticked = new Set(this.reviewSelection.tickedItemIds(urlId, cardKey));
    const rows = this.reviewSelection.hasCardItems(urlId, cardKey)
      ? allRows.filter((r) => ticked.has(r.id))
      : allRows;
    // The work item carries what the card showed: the section, whether it is a
    // new one or a thin one, and the line of theirs that says what goes in it.
    const sections = rows.map((r) => {
      const where = r.closest ? ` (deepen “${r.closest.heading}”)` : '';
      const excerpt = r.excerpt ? ` — they open: “${r.excerpt}”` : '';
      return `${r.label}${where}${excerpt}`;
    });
    return {
      issueKey: buildPlanV2IssueKey(urlId, 'section_gap'),
      urlId,
      url,
      issueType: 'section_gap',
      cardTitle: 'Missing sections',
      changeSummary: 'Add the sections the top-ranking rival covers and this page does not.',
      why: sections.length ? sections.slice(0, 6).join(' · ') : rank?.verdict || 'Rival covers ground this page does not.',
      actionDetail: sections.length ? encodeWorkItemDetail({ bullets: sections.slice(0, 12) }) : null,
      diagnosisLabel: missing?.verdict ?? rank?.verdict ?? null,
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
  }

  buildSerpPhraseIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url) return null;
    return {
      issueKey: buildPlanV2IssueKey(urlId, 'serp_phrase'),
      urlId,
      url,
      issueType: 'serp_phrase',
      cardTitle: 'Answer structure & entities',
      changeSummary: 'Match SERP answer structure and entity vocabulary on this page.',
      why: this.serpPhraseMatchActionChipLabel(),
      diagnosisLabel: this.showSerpPhraseMatchActionChip() ? this.serpPhraseMatchActionChipLabel() : null,
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
  }

  buildSerpDifficultyIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url) return null;
    const hint = this.serpKeywordDifficultyHint();
    return {
      issueKey: buildPlanV2IssueKey(urlId, 'serp_difficulty'),
      urlId,
      url,
      issueType: 'serp_difficulty',
      cardTitle: 'SERP difficulty',
      changeSummary: 'Account for keyword difficulty when prioritizing effort on this query.',
      why: hint ? `${this.serpKeywordDifficultyShort()} — ${hint}` : this.serpKeywordDifficultyShort(),
      diagnosisLabel: this.serpDifficultyActionChipLabel(),
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
  }

  buildSerpIntentIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url) return null;
    const lines = this.pageTypeBriefLines();
    return {
      issueKey: buildPlanV2IssueKey(urlId, 'serp_intent'),
      urlId,
      url,
      issueType: 'serp_intent',
      cardTitle: 'Page type',
      changeSummary: 'Align page type with dominant SERP page type.',
      why: lines.length ? lines.join(' ') : this.pageIntentMatchActionChipLabel(),
      diagnosisLabel: this.pageIntentMatchActionChipLabel(),
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
  }

  /**
   * The findings a technical card will actually send.
   *
   * On the review surface that is the ticked ones, which is not the same as
   * what the card lists: unticked findings stay on screen — you have to be able
   * to change your mind — but they must not turn up in the work item. Off the
   * review surface there are no ticks and this is everything.
   */
  private technicalPayloadTitles(issues: OnpageIssueView[], cardKey: string): string[] {
    if (!this.embedReviewSurface) return issues.map((i) => i.title).slice(0, 4);
    // Card-scoped unticks are already out of this — see `tickedCodesForCard`.
    // Site-wide checks are out too, for the same reason they are out of the
    // work item itself: they have their own task, and a `why` naming them
    // would describe a page card whose body no longer lists them.
    const ticked = this.reviewSelection.tickedCodesForCard(this.pageUrlId.trim(), cardKey);
    const siteWide = this.reviewModel.reviewSiteWideIssueCodes();
    return this.reviewTechnicalIssues(issues)
      .filter((i) => ticked.has(i.code) && !(siteWide?.has(i.code) ?? false))
      .map((i) => i.title)
      .slice(0, 4);
  }

  /**
   * A technical work item that records what the operator actually ticked.
   *
   * Through Inventory's own composer, not a second one written for this surface.
   * The review card was sending the base payload: a fixed sentence, up to four
   * titles, and no `actionDetail` at all. Tick six findings of eight and the
   * work item named four of them with none of their detail — so the care an
   * operator puts into the ticks did not survive into the thing a writer opens.
   *
   * Reusing `composeTechnicalIssuesWorkItem` is the point rather than an
   * economy: two functions building "the technical work item" is how the two
   * surfaces come to disagree about what was decided, which is the bug this
   * codebase has now shipped in four different shapes.
   *
   * Every detail line of a ticked finding is included. The finer per-line
   * picking Inventory offers has no equivalent here, and inventing a default
   * for it would be this surface guessing at a decision nobody made.
   */
  private reviewTechnicalWorkItem(
    base: PlanV2IssueCardPayload,
    issues: OnpageIssueView[],
    kind: 'critical' | 'warning'
  ): PlanV2IssueCardPayload {
    const ticked = this.reviewSelection.tickedCodesForCard(this.pageUrlId.trim(), base.issueKey);
    // Site-wide checks are NOT this page's work item.
    //
    // Each one is already written as its own `site_wide_technical` task —
    // "fixing it clears this finding on every page that has it" — so listing
    // them here too put the same three findings in Work items twice: once
    // under Site-wide, and again under the page that happened to be on screen
    // when they were ticked. On the pilot that turned one page-specific fix
    // into a four-item page card whose other three belonged to the template.
    //
    // The site-wide/page split is how the rest of the app reads; this was the
    // one place that ignored it. Inventory and URL detail are untouched: they
    // compose through their own step, where seeing everything on one page is
    // the point.
    const siteWide = this.reviewModel.reviewSiteWideIssueCodes();
    const picked = this.reviewTechnicalIssues(issues).filter(
      (i) => ticked.has(i.code) && !(siteWide?.has(i.code) ?? false)
    );
    // Nothing left that belongs to the page: every ticked check was site-wide
    // and has its own task. The base payload is the honest wording, and the
    // card is not repeating what the Site-wide group already says.
    if (!picked.length) return base;
    return composeTechnicalIssuesWorkItem(base, listTechnicalIssueComposeOptions(picked, kind), kind);
  }

  buildTechnicalCriticalIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url) return null;
    const views = this.technicalCriticalIssueViews();
    const titles = this.technicalPayloadTitles(
      views,
      buildPlanV2IssueKey(urlId, 'technical_critical')
    );
    const base: PlanV2IssueCardPayload = {
      issueKey: buildPlanV2IssueKey(urlId, 'technical_critical'),
      urlId,
      url,
      issueType: 'technical_critical',
      cardTitle: 'Technical issues',
      changeSummary: 'Fix critical on-page / technical issues listed for this URL.',
      why: titles.length ? titles.join(' · ') : this.technicalIssuesActionChipLabel(),
      diagnosisLabel: this.technicalIssuesActionChipLabel(),
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
    // Inventory reaches the detailed version through its compose step. Review
    // has no compose step — the ticks ARE the picking — so it composes here.
    return this.embedReviewSurface ? this.reviewTechnicalWorkItem(base, views, 'critical') : base;
  }

  buildTechnicalWarningsIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url) return null;
    const views = this.technicalWarningIssueViews();
    const titles = this.technicalPayloadTitles(
      views,
      buildPlanV2IssueKey(urlId, 'technical_warnings')
    );
    const base: PlanV2IssueCardPayload = {
      issueKey: buildPlanV2IssueKey(urlId, 'technical_warnings'),
      urlId,
      url,
      issueType: 'technical_warnings',
      cardTitle: 'Technical warnings',
      changeSummary: 'Resolve on-page warnings to reduce crawl and ranking friction.',
      why: titles.length ? titles.join(' · ') : this.technicalWarningsActionChipLabel(),
      diagnosisLabel: this.technicalWarningsActionChipLabel(),
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
    return this.embedReviewSurface ? this.reviewTechnicalWorkItem(base, views, 'warning') : base;
  }

  hasNonInternalLinkSignals(): boolean {
    return this.reviewModel.hasNonInternalLinkSignals();
  }

  buildSignalsIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    if (!urlId || !url || !this.hasNonInternalLinkSignals()) return null;
    const labels = this.reviewPageSignals().filter((s) => s.kind !== 'internal_link').map((s) => s.label);
    return {
      issueKey: buildPlanV2IssueKey(urlId, 'signals'),
      urlId,
      url,
      issueType: 'signals',
      cardTitle: 'Workspace signals',
      changeSummary: 'Resolve open cannibalization or cluster signals for this URL.',
      why: labels.join(' · '),
      diagnosisLabel: null,
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
  }

  buildInternalLinksSectionPayload(options?: { includeLinksInWorkItems?: boolean }): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    const rows = options?.includeLinksInWorkItems
      ? this.pageSignalInternalLinkDetails()
      : this.pageSignalInternalLinkDetailsPendingTriage();
    if (!urlId || !url || rows.length === 0) return null;
    const count = rows.length;
    return {
      issueKey: buildPlanV2IssueKey(urlId, 'internal_link'),
      urlId,
      url,
      issueType: 'internal_link',
      cardTitle: 'Internal link suggestions',
      changeSummary: `Add ${count} contextual internal link${count === 1 ? '' : 's'}.`,
      why: 'Other pages where you can add a link pointing here.',
      diagnosisLabel: null,
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
  }

  /** Shown in Issues embed while in queue or playing send/dismiss exit (same as other focus cards). */
  internalLinksIssuesPayload(): PlanV2IssueCardPayload | null {
    if (!this.isPlanV2Embed()) return null;
    const urlId = (this.pageUrlId || '').trim();
    const key = buildPlanV2IssueKey(urlId, 'internal_link');
    const exiting =
      this.planV2IssueQueue.isCardExiting(key, 'work_item') ||
      this.planV2IssueQueue.isCardExiting(key, 'dismissed');
    const payload = this.buildInternalLinksSectionPayload({ includeLinksInWorkItems: exiting });
    if (!payload) return null;
    if (exiting) return payload;
    return this.planV2IssueQueue.isVisibleInIssuesEmbed(key) ? payload : null;
  }

  internalLinkRowForPayload(payload: PlanV2IssueCardPayload): ActionPlanInternalLinkRow | null {
    const key =
      (payload.suggestionKey || '').trim() ||
      payload.issueKey.split(':').slice(2).join(':').trim();
    if (!key) return null;
    return (
      this.internalLinkSignalRows.find((r) => r.suggestionKey === key && r.status === 'open') || null
    );
  }

  /** Triage buttons only when the card reflects a real problem (not green / all-clear chips). */
  showIssueCardActions(payload: PlanV2IssueCardPayload | null | undefined): boolean {
    if (!payload || !this.isPlanV2Embed() || this.isCompactPeek()) return false;
    if (!this.planV2IssueQueue.isVisibleInIssuesEmbed(payload.issueKey)) return false;
    return this.isIssuePayloadActionable(payload);
  }

  /**
   * The same three conditions for the compact peek, which `showIssueCardActions`
   * excludes outright.
   *
   * That exclusion was correct while the peek was a row of chips with nothing to
   * act on. It is a separate predicate rather than a relaxed flag on the first
   * one because the two surfaces answer differently on the FIRST clause and
   * identically on the other two, and folding them into
   * `isCompactPeek() ? … : …` reads as if the difference were cosmetic.
   */
  showPeekActions(payload: PlanV2IssueCardPayload | null | undefined): boolean {
    if (!this.embedPeekActions) return false;
    if (!payload || !this.isCompactPeek()) return false;
    if (!this.planV2IssueQueue.isVisibleInIssuesEmbed(payload.issueKey)) return false;
    return this.isIssuePayloadActionable(payload);
  }

  isIssuePayloadActionable(payload: PlanV2IssueCardPayload): boolean {
    switch (payload.issueType) {
      case 'topic_ownership':
        // The surface-aware predicate: on review this card is pages only, so a
        // page whose only finding is a missing section must not offer a tick
        // here — the Sections card below is where that decision lives.
        return this.supportingPagesGapForThisSurface();
      case 'section_gap':
        return Boolean(this.reviewMissingSections() || this.reviewRankGaps());
      case 'freshness':
        return !this.embedDiagnosisIsGreen(this.freshnessDiagnosisTone());
      case 'serp_competition':
        return false;
      case 'serp_phrase':
        return !this.embedDiagnosisIsGreen(this.serpPhraseMatchActionChipTone());
      case 'serp_difficulty':
        return false;
      case 'serp_intent':
        return !this.embedDiagnosisIsGreen(this.pageIntentMatchActionChipTone());
      case 'technical_critical':
        return this.hasTechnicalCriticalIssues();
      case 'technical_warnings':
        return this.hasTechnicalWarnings();
      case 'signals':
        return this.hasNonInternalLinkSignals();
      case 'internal_link':
        return this.pageSignalInternalLinkDetailsPendingTriage().length > 0;
      default:
        return false;
    }
  }

  openPlanV2InInventory(): void {
    this.router.navigate(['/inventory'], { queryParams: { section: 'planV2' } });
  }

  hasDemandDiagnostics(): boolean {
    return Boolean(this.pageDemand || this.pageRealization || this.pageHealth || this.pageSecondaryHealth);
  }

  hasRankedKeywords(): boolean {
    return this.rankedKeywordsRows().length > 0;
  }

  rankedKeywordsRows(): PageRankedKeywordRow[] {
    const keywords = this.analysisData?.rankedKeywords?.keywords;
    if (!Array.isArray(keywords)) return [];

    return keywords
      .map((row: any) => ({
        keyword: String(row?.keyword || '').trim(),
        position: Number(row?.position),
        searchVolume: Number.isFinite(row?.searchVolume) ? Number(row.searchVolume) : null
      }))
      .filter((row: PageRankedKeywordRow) => row.keyword && Number.isFinite(row.position))
      .sort((a: PageRankedKeywordRow, b: PageRankedKeywordRow) => {
        if (a.position !== b.position) return a.position - b.position;
        return a.keyword.localeCompare(b.keyword);
      });
  }

  rankedKeywordsFetchedAt(): string | null {
    const fetchedAt = this.analysisData?.rankedKeywords?.fetchedAt;
    if (!fetchedAt) return null;
    return String(fetchedAt);
  }

  formatRankedKeywordVolume(value: number | null): string {
    if (!Number.isFinite(value)) return '—';
    return String(Math.round(value as number));
  }

  trackByRankedKeyword(_index: number, row: PageRankedKeywordRow): string {
    return `${row.keyword}:${row.position}`;
  }

  demandDiagnosticsInfo(key: string): string {
    return DEMAND_DIAGNOSTICS_INFO[key] ?? '';
  }

  pageDemandIntentTokensText(): string {
    const tokens = this.pageDemand?.claimIntentTokens;
    if (!Array.isArray(tokens) || tokens.length === 0) return '—';
    return tokens.join(', ');
  }

  pageDemandSourceConfidence(side: 'claim' | 'realization' | 'ambientTopic'): string {
    if (!this.pageDemand) return '—';
    if (side === 'claim') return formatPageDemandSourceConfidence(this.pageDemand.claim);
    if (side === 'realization') return formatPageDemandSourceConfidence(this.pageDemand.realization);
    return formatPageDemandSourceConfidence(this.pageDemand.ambientTopic);
  }

  pageHealthLabel(): string {
    return this.pageHealthForDisplay().band
      ? pageHealthBandLabel(this.pageHealthForDisplay().band)
      : '—';
  }

  pageHealthClass(): string {
    return this.pageHealthForDisplay().band
      ? pageHealthBandClass(this.pageHealthForDisplay().band)
      : '';
  }

  private pageHealthForDisplay() {
    if (this.pageHealth) return this.pageHealth;
    return classifyPageHealth({
      statusCode: this.analysisData?.statusCode ?? 0,
      estimatedOrganicTraffic: this.analysisData?.estimatedOrganicTraffic ?? null,
      realization: this.pageRealization
    });
  }

  effectiveHealthBand(): EffectiveHealthDisplayBand {
    const primary = this.pageHealthForDisplay().band;
    return resolveEffectiveHealthBand(primary, this.pageSecondaryHealth);
  }

  effectiveHealthLabel(): string {
    return effectiveHealthBandLabel(
      this.pageHealthForDisplay().band,
      this.pageSecondaryHealth
    );
  }

  effectiveHealthClass(): string {
    return effectiveHealthBandClass(
      this.pageHealthForDisplay().band,
      this.pageSecondaryHealth
    );
  }

  improvementsSummary(): string {
    const claim = this.pageDemand?.claim?.label?.trim();
    const band = this.effectiveHealthBand();
    const etv = this.analysisData?.estimatedOrganicTraffic;
    const etvText = Number.isFinite(etv) ? `~${Math.round(etv)} est. traffic/mo` : 'no est. traffic yet';

    if (this.analysisStatus === 'loading') {
      return 'Loading health and demand context for this URL…';
    }
    if (this.analysisStatus === 'error' || !this.analysisData) {
      return 'Run a crawl and analysis for this URL to see improvements.';
    }

    const claimPhrase = claim ? `accountable for “${claim}”` : 'without a confident demand claim yet';

    if (band === 'critical') {
      return `This URL is not eligible for organic performance (${claimPhrase}). Fix availability before SEO improvements.`;
    }
    if (band === 'recently_updated') {
      return `Recently updated — realization gap is on hold. ${claimPhrase}; ${etvText}. Earned visibility may still be building.`;
    }
    if (band === 'unscored') {
      return `Gap not scored yet — ${claimPhrase}; ${etvText}. See drivers below; compare with marked competitors when data allows.`;
    }
    if (band === 'weak') {
      return `Under-realized vs demand — ${claimPhrase}; ${etvText}. There is material upside if rankings move toward the demand ceiling.`;
    }
    if (band === 'best') {
      return `Near ceiling for scored demand — ${claimPhrase}; ${etvText}. Focus on defending position and SERP depth vs competitors.`;
    }
    return `Pulling reasonable weight for demand — ${claimPhrase}; ${etvText}.`;
  }

  improvementsWhyBullets(): string[] {
    const bullets: string[] = [];
    const primary = this.pageHealthForDisplay();

    if (primary.reasons?.length) {
      bullets.push(...primary.reasons);
    }

    if (primary.band === 'unscored' && this.pageSecondaryHealth?.reasons?.length) {
      for (const reason of this.pageSecondaryHealth.reasons) {
        if (!bullets.includes(reason)) bullets.push(reason);
      }
    }

    if (this.pageDemand?.claim?.label) {
      bullets.push(`Demand claim: ${this.pageDemand.claim.label}`);
    }

    const realization = this.pageDemand?.realization;
    if (realization?.label) {
      const pos = realization.position !== null ? ` · pos ${realization.position}` : '';
      bullets.push(`Best aligned rank: ${realization.label}${pos}`);
    }

    const gapGate = this.pageRealization?.gapGate;
    if (gapGate && gapGate !== 'scored') {
      bullets.push(`Realization gap: ${gapGate.replace(/_/g, ' ')}`);
    } else if (this.pageRealization?.gapGate === 'scored') {
      const ratio = this.pageRealization.realizationRatio;
      const gap = this.pageRealization.gap;
      if (ratio !== null && gap !== null) {
        bullets.push(
          `Realized ${this.formatRealizationNumber(this.pageRealization.realizedVisibility)} vs ceiling ${this.formatRealizationNumber(this.pageRealization.ceiling)} (ratio ${this.formatRealizationRatio(ratio)}, gap ${this.formatRealizationNumber(gap)})`
        );
      }
    }

    if (this.pageSecondaryHealth?.model === 'freshness_hold') {
      bullets.push('Secondary scoring on hold — content updated within the last six months');
    }

    if (this.pageSecondaryHealth?.unscoredSegment) {
      bullets.push(this.secondaryUnscoredSegmentLabel());
    }

    return bullets.slice(0, 8);
  }

  /** Strongest topic signal on this page (same order as the topics table). */
  primaryPageTopicSignal(): PageTopicSignal | null {
    const signals = this.getPageTopicSignals();
    return signals.length ? signals[0] : null;
  }

  /**
   * Topic used for Improvements competitor list: primary signal first;
   * if no marked competitors overlap that slug, fall back to the next signal that has matches.
   */
  improvementCompetitorTopicSignal(): PageTopicSignal | null {
    const signals = this.getPageTopicSignals();
    if (!signals.length) return null;

    for (const signal of signals) {
      if (this.getCompetitorTopicMatches(signal).length > 0) {
        return signal;
      }
    }
    return signals[0];
  }

  improvementCompetitorUsesFallbackTopic(): boolean {
    const primary = this.primaryPageTopicSignal();
    const used = this.improvementCompetitorTopicSignal();
    if (!primary || !used) return false;
    return primary.topicSlug !== used.topicSlug;
  }

  improvementCompetitorTopicLabel(): string {
    return this.improvementCompetitorTopicSignal()?.label || 'this page';
  }

  improvementCompetitorLeads(): ImprovementCompetitorLead[] {
    const signal = this.improvementCompetitorTopicSignal();
    if (!signal) return [];

    return this.getCompetitorTopicMatches(signal)
      .map((match) => ({
        domain: match.domain,
        authorityScore: match.authorityScore,
        pageCount: match.pageCount,
        topicLabel: signal.label,
        topicSlug: signal.topicSlug
      }))
      .sort((a, b) => {
        if (b.authorityScore !== a.authorityScore) return b.authorityScore - a.authorityScore;
        if (b.pageCount !== a.pageCount) return b.pageCount - a.pageCount;
        return a.domain.localeCompare(b.domain);
      })
      .slice(0, 6);
  }

  hasImprovementCompetitorLeads(): boolean {
    return this.improvementCompetitorLeads().length > 0;
  }

  trackImprovementCompetitor(_index: number, lead: ImprovementCompetitorLead): string {
    return `${lead.domain}::${lead.topicSlug}`;
  }

  hasPageSerpSnapshot(): boolean {
    return Boolean(this.pageSerp?.results?.length);
  }

  pageSerpPendingReason(): string | null {
    if (this.pageSerp) return null;
    if (!this.pageDemand?.claim?.label) {
      return 'SERP pending — no demand claim yet. Run page demand labeling after crawl.';
    }
    if (this.pageDemand.claim.confidence === 'none') {
      return 'SERP pending — claim confidence too low to pick a target query.';
    }
    return 'SERP pending — no snapshot for this URL. Refresh URL SERP for the domain (dev).';
  }

  pageSerpTargetQueryLabel(): string {
    return this.reviewModel.pageSerpTargetQueryLabel();
  }

  pageDemandClaimLabel(): string {
    return this.reviewModel.pageDemandClaimLabel();
  }

  pageDemandClaimOverridden(): boolean {
    return Boolean(this.pageDemand?.overrideClaim?.label);
  }

  openClaimOverrideModal(): void {
    this.claimOverrideError = null;
    this.claimOverrideNotice = null;
    this.claimOverrideProgress = null;
    this.claimOverrideLabel = this.pageDemand?.claim?.label?.trim() || '';
    this.claimOverrideOpen = true;
  }

  closeClaimOverrideModal(): void {
    if (this.claimOverrideSaving) return;
    this.claimOverrideOpen = false;
    this.claimOverrideError = null;
  }

  claimOverrideSuggestions(): string[] {
    const suggestions = new Set<string>();
    const current = this.pageDemand?.claim?.label?.trim();
    if (current) suggestions.add(current);
    try {
      const pathname = new URL(this.url || '').pathname || '/';
      const segments = pathname.split('/').filter(Boolean);
      const slug = segments[segments.length - 1]?.replace(/-/g, ' ').trim();
      if (slug) suggestions.add(slug);
    } catch {
      // ignore
    }
    const title =
      this.analysisData?.rawData?.pageData?.meta?.title ||
      this.analysisData?.rawData?.summary?.title ||
      '';
    if (typeof title === 'string' && title.trim()) {
      suggestions.add(title.split('|')[0].trim());
    }
    for (const signal of this.getPageTopicSignals().slice(0, 5)) {
      if (signal.label) suggestions.add(signal.label);
    }
    return Array.from(suggestions).filter((label) => label.length >= 3);
  }

  saveClaimOverride(): void {
    void this.runClaimOverrideCascade();
  }

  private setClaimOverrideProgress(
    phase: ClaimOverrideRefreshPhase,
    percent: number,
    statusLine: string,
    options: { etaLabel?: string | null; indeterminate?: boolean } = {}
  ): void {
    this.claimOverrideProgress = {
      phase,
      percent: Math.min(100, Math.max(0, percent)),
      statusLine,
      etaLabel: options.etaLabel ?? null,
      indeterminate: options.indeterminate === true
    };
  }

  private applyClaimOverrideSerpProgress(progress: UrlSerpRefreshProgress): void {
    const pct = 18 + Math.round(progress.percent * 0.28);
    this.setClaimOverrideProgress('serp', pct, progress.statusLine, {
      etaLabel: progress.etaLabel
    });
  }

  private async runClaimOverrideCascade(): Promise<void> {
    const urlId = (this.pageUrlId || '').trim();
    const scopeDomain = this.planV2IssueDomain();
    const overrideClaimLabel = (this.claimOverrideLabel || '').trim();
    const pageUrl = (this.url || '').trim();

    if (!urlId || !scopeDomain || !overrideClaimLabel) {
      this.claimOverrideError = 'Enter a corrected demand claim.';
      return;
    }
    if (!pageUrl) {
      this.claimOverrideError = 'Page URL is not loaded yet.';
      return;
    }

    this.claimOverrideSaving = true;
    this.claimOverrideError = null;
    this.claimOverrideNotice = null;
    this.setClaimOverrideProgress('claim', 6, 'Saving corrected intent…');

    try {
      const claimRes = await firstValueFrom(
        this.dataForSEOService.overridePageDemandClaim({
          urlId,
          scopeDomain,
          overrideClaimLabel,
          reason: 'wrong_intent',
          skipSerpCascade: true
        })
      );
      if (claimRes.data?.pageDemand) {
        this.pageDemand = claimRes.data.pageDemand;
      }

      this.setClaimOverrideProgress('serp', 18, 'Refreshing SERP for new query…');
      const serpTotals = await this.urlSerpSync.refreshPageUrl(pageUrl, {
        forceRefresh: true,
        onProgress: (p) => this.applyClaimOverrideSerpProgress(p)
      });
      if (serpTotals.failedKeywords > 0) {
        throw new Error('SERP fetch failed for the override query');
      }

      this.setClaimOverrideProgress('rival', 50, 'Crawling top rival page…', { indeterminate: true });
      const rivalRes = await firstValueFrom(
        this.dataForSEOService.refreshSerpRivalCoverage({ url: pageUrl, scopeDomain })
      );
      const rivalCoverage = rivalRes.data?.serpRivalCoverage ?? null;
      if (rivalCoverage) {
        this.serpRivalCoverage = rivalCoverage;
      }
      const rivalDomain = rivalCoverage?.rivalDomain?.trim();
      const gapCount = sectionGapsForV2Row(rivalCoverage?.evalRow).length;
      if (rivalRes.data?.serpRivalRefresh?.success && rivalDomain) {
        const gapNote =
          gapCount > 0
            ? ` · ${gapCount} missing section${gapCount === 1 ? '' : 's'}`
            : ' · no section gaps';
        this.setClaimOverrideProgress(
          'rival',
          78,
          `Checked section gaps vs ${rivalDomain}${gapNote}`
        );
      } else if (rivalRes.data?.serpRivalRefresh?.error) {
        this.setClaimOverrideProgress(
          'rival',
          78,
          rivalRes.data.serpRivalRefresh.error,
          { indeterminate: false }
        );
      } else if (rivalDomain) {
        this.setClaimOverrideProgress('rival', 78, `Finished check vs ${rivalDomain}`);
      }

      this.setClaimOverrideProgress('reload', 90, 'Updating playbook and intent…');
      const reloadError = await this.reloadAnalysisAfterClaimOverrideAsync(
        !rivalRes.data?.serpRivalRefresh?.success,
        rivalRes.data?.serpRivalRefresh?.error
      );

      this.claimOverrideOpen = false;
      this.claimOverrideSaving = false;
      this.claimOverrideProgress = null;
      this.intentOverrideCompleted.emit({ urlId, url: pageUrl });
      if (reloadError) {
        this.claimOverrideNotice = reloadError;
      }
    } catch (err: any) {
      this.claimOverrideSaving = false;
      this.claimOverrideProgress = null;
      const message = err?.error?.message || err?.message;
      if (this.claimOverrideOpen) {
        this.claimOverrideError = message || 'Could not complete intent refresh.';
      } else {
        this.claimOverrideNotice = message || 'Intent refresh did not complete.';
      }
    }
  }

  private reloadAnalysisAfterClaimOverrideAsync(
    partialFailure = false,
    partialError?: string
  ): Promise<string | null> {
    const pageUrl = (this.url || '').trim();
    if (!pageUrl) return Promise.resolve(null);

    // Bust answer-structure LLM cache — Wrong intent? cascade refreshed SERP;
    // reclassify delivery pattern for the new (or same) snippets.
    return firstValueFrom(
      this.dataForSEOService.getUrlAnalysis(pageUrl, { refreshAnswerStructure: true })
    )
      .then((response) => {
        if (response?.data) {
          this.pageDemand = response.data.pageDemand ?? this.pageDemand;
          this.pageSerp = response.data.pageSerp ?? this.pageSerp;
          // The claim override refetches the SERP, so the compare target can
          // move — a stale one would name a page that no longer ranks.
          this.pageCompare = response.data.pageCompare ?? this.pageCompare;
          this.pageEstimatePair = response.data.pageEstimatePair ?? this.pageEstimatePair;
          this.pageIntentDiagnosis = response.data.pageIntentDiagnosis ?? this.pageIntentDiagnosis;
          this.collapseWinnerExcerpts();
          this.pageRealization = response.data.pageRealization ?? this.pageRealization;
          this.pageHealth = response.data.pageHealth ?? this.pageHealth;
          this.pageSecondaryHealth = response.data.pageSecondaryHealth ?? this.pageSecondaryHealth;
          this.loadTopicOwnership();
        }
        if (partialFailure) {
          return (
            partialError ||
            'Claim and SERP updated, but section-gap check did not finish. Try again or run Top SERP rivals in Setup.'
          );
        }
        return null;
      })
      .catch((err) => {
        return (
          err?.error?.message ||
          err?.message ||
          'Claim saved, but could not reload URL analysis.'
        );
      });
  }

  topicSignalMatchesClaim(signal: PageTopicSignal): boolean {
    return topicSignalMatchesDemandClaim(this.pageDemand?.claim?.label, signal);
  }

  getTopPageTopicSignalsForDisplay(): PageTopicSignal[] {
    const signals = this.getTopPageTopicSignals();
    return orderTopicSignalsForClaim(
      signals,
      this.pageDemand?.claim?.label,
      this.isPlanV2Embed()
    ) as PageTopicSignal[];
  }

  pageDemandClaimClass(): string {
    const confidence = this.pageDemand?.claim?.confidence;
    if (confidence === 'low' || confidence === 'none') return 'url-focus-stat-claim--low';
    if (confidence === 'medium') return 'url-focus-stat-claim--medium';
    return 'url-focus-stat-claim--high';
  }

  pageDemandClaimTitle(): string {
    const demand = this.pageDemand;
    if (!demand?.claim) return 'No demand claim stored for this URL.';
    const parts = [
      `Claim: ${demand.claim.label}`,
      `Source: ${demand.claim.source}`,
      `Confidence: ${demand.claim.confidence}`
    ];
    if (demand.overrideClaim?.label) {
      parts.push(`Override (${demand.overrideClaim.reason})`);
    }
    if (demand.claim.detail) parts.push(demand.claim.detail);
    if (demand.realization?.label) {
      parts.push(`Best aligned rank: ${demand.realization.label}`);
      if (demand.realization.position !== null) {
        parts.push(`Position ${demand.realization.position}`);
      }
    }
    return parts.join(' · ');
  }

  rankedKeywordsCountLabel(): string {
    const count = this.rankedKeywordsRows().length;
    return count > 0 ? String(count) : '—';
  }

  rankedKeywordsCountTitle(): string {
    const count = this.rankedKeywordsRows().length;
    const fetched = this.rankedKeywordsFetchedAt();
    if (!count) return 'No ranked keywords on this URL yet.';
    return fetched
      ? `${count} keywords from last ranked-keywords fetch (${fetched})`
      : `${count} keywords from ranked-keywords data`;
  }

  estimatedTrafficLabel(): string {
    const etv = this.analysisData?.estimatedOrganicTraffic;
    if (!Number.isFinite(etv)) return '—';
    return `~${Math.round(Number(etv))}/mo`;
  }

  estimatedTrafficTitle(): string {
    const etv = this.analysisData?.estimatedOrganicTraffic;
    if (!Number.isFinite(etv)) return 'No estimated organic traffic on this analysis.';
    return `DataForSEO estimated organic traffic: ~${Math.round(Number(etv))} visits per month`;
  }

  pageRealizationStatLabel(): string {
    const r = this.pageRealization;
    if (!r) return '—';
    if (r.gapGate !== 'scored') {
      return this.realizationGapGateLabel().replace(/_/g, ' ');
    }
    const gap = formatInventoryRealizationNumber(r.gap);
    const ratio = formatInventoryRealizationRatio(r.realizationRatio);
    return `${gap} · ${ratio}`;
  }

  pageRealizationStatTitle(): string {
    const r = this.pageRealization;
    if (!r) return 'No realization gap data.';
    if (r.gapGate !== 'scored') {
      return `Gap not scored (${r.gapGate}). ${inventoryRealizationGapTitle(r)}`;
    }
    return `${inventoryRealizationGapTitle(r)} · ${inventoryRealizationRatioTitle(r)}`;
  }

  pageContentLastModifiedDate(): Date | null {
    return this.reviewModel.pageContentLastModifiedDate();
  }

  pageContentLastModifiedLabel(): string {
    return this.reviewModel.pageContentLastModifiedLabel();
  }

  private serpWinnerPublishedDates(): Date[] {
    return this.reviewModel.serpWinnerPublishedDates();
  }

  serpWinnersPublishedDateSummary(): { count: number; typicalLabel: string } | null {
    return this.reviewModel.serpWinnersPublishedDateSummary();
  }

  showContentFreshnessCard(): boolean {
    return this.reviewModel.showContentFreshnessCard();
  }

  toggleSerpSectionExpanded(): void {
    this.serpSectionExpanded = !this.serpSectionExpanded;
  }

  serpSectionExpandLabel(): string {
    return this.serpSectionExpanded ? 'Hide top 10' : 'Show top 10';
  }

  freshnessDiagnosisLabel(): string {
    return this.reviewModel.freshnessDiagnosisLabel();
  }

  freshnessDiagnosisTone(): DiagnosisTone {
    return this.reviewModel.freshnessDiagnosisTone();
  }

  hasTopicOwnershipCoverageGap(): boolean {
    return this.reviewModel.hasTopicOwnershipCoverageGap();
  }

  hasSupportingPagesSectionGaps(): boolean {
    return this.reviewModel.hasSupportingPagesSectionGaps();
  }

  hasSupportingPagesListInventoryGaps(): boolean {
    return this.reviewModel.hasSupportingPagesListInventoryGaps();
  }

  hasSupportingPagesRankDrivenGaps(): boolean {
    return this.reviewModel.hasSupportingPagesRankDrivenGaps();
  }

  private isMarkedCompetitorDomain(domain: string): boolean {
    const key = this.normalizeSerpDomain(domain);
    return this.markedCompetitorDomains.some((m) => this.normalizeSerpDomain(m) === key);
  }

  hasSupportingPagesActionableGap(): boolean {
    return this.reviewModel.hasSupportingPagesActionableGap();
  }

  showTopicOwnershipGapChip(): boolean {
    return (
      (this.topicOwnershipStatus === 'ready' && this.topicOwnershipDomains.length > 0) ||
      (this.topicOwnershipV2Status === 'ready' && (this.topicOwnershipV2Data?.rows?.length ?? 0) > 0)
    );
  }

  supportingPagesColumnsReady(): boolean {
    return this.reviewModel.supportingPagesColumnsReady();
  }

  supportingPagesEmptyReady(): boolean {
    return supportingPagesEmptyReady(this.supportingPagesGateState());
  }

  /**
   * The card is on screen but its answer is not in yet.
   *
   * Three renderings were tried and the first two were both wrong. Rendering
   * the card whole — title, GAP FOUND badge, ticked "Sending" box — over an
   * empty body asserts a verdict nobody can see and volunteers it for sending.
   * Hiding the card entirely until it settles is worse in a different way: the
   * section reads as finished, so an operator can look at a page, see no
   * supporting-pages finding, and move on before one arrives.
   *
   * So the card stays, and only the parts that would LIE are withheld: the
   * verdict badge and the tick. What is left says plainly that it is still
   * working. The send is blocked separately — see `reviewCardsPending`, since
   * a page whose findings are still arriving cannot be sent whole.
   */
  reviewSupportingPagesPending(): boolean {
    return this.embedReviewSurface && this.supportingPagesPendingVisible;
  }

  reviewSupportingPagesColumns(): ReviewSupportingPagesColumn[] {
    return this.reviewModel.reviewSupportingPagesColumns();
  }

  hasReviewSupportingPagesGaps(): boolean {
    return this.reviewModel.hasReviewSupportingPagesGaps();
  }

  /**
   * Whether this card has nothing at all to say.
   *
   * "Nothing on this topic that they have and you don't" used to print above
   * three other findings the card was holding, so a page with a rival
   * outranking it on three queries opened with the word "Nothing". Those three
   * have moved to cards of their own, which makes the sentence simple again: it
   * is about pages, and it is true when there are no page gaps.
   */
  reviewSupportingPagesEmpty(): boolean {
    return !this.hasReviewSupportingPagesGaps();
  }

  /**
   * "This page" or "Site-wide" beside a technical finding, on review only.
   *
   * The deep-dive panel states the split in a sentence and lists the page's own
   * findings; that is right where an operator is reading one page closely. On a
   * belt of forty-five it has to be legible per row, because the two are
   * different jobs: one is work on this page, the other is a template fix that
   * clears the same finding on hundreds of pages at once.
   *
   * Null before the domain rollup answers, so a tag never appears and then
   * changes its mind.
   */
  reviewMissingSections(): ReviewMissingSectionsView | null {
    return this.reviewModel.reviewMissingSections();
  }

  /**
   * Links worth adding, on the review surface.
   *
   * The suggestions are already loaded here — `internalLinkSignalRows` feeds
   * the peek's Internal links row — and the review surface simply never showed
   * them. Rules in `utils/review-internal-links`.
   */
  reviewRankGaps(): ReviewRankGapsView | null {
    return this.reviewModel.reviewRankGaps();
  }

  // `reviewInternalLinks()` used to render a fourth block inside the supporting
  // pages card. It was showing the same suggestions the dedicated Internal
  // links card below already lists, under a tick that also covered pages and
  // sections — so unticking links meant unticking everything. The dedicated
  // card has its own tick and its own compose; this was the duplicate.

  reviewTechnicalIssues(issues: OnpageIssueView[]): OnpageIssueView[] {
    return this.reviewModel.reviewTechnicalIssues(issues);
  }

  reviewSettledSiteWideLabel(issues: OnpageIssueView[]): string | null {
    return this.reviewModel.reviewSettledSiteWideLabel(issues);
  }

  /**
   * Hands the page's technical checks to the send.
   *
   * This used to announce only the site-wide codes, because they were the only
   * ones the send did anything with. Every check is announced now: each one is
   * its own tick, and an untick has to be remembered whether the crawler calls
   * it a template defect or not.
   *
   * Only the checks still being ASKED about are announced. A code already
   * settled is filtered out by `reviewTechnicalIssues` upstream, and announcing
   * it here would re-send an answer the operator did not give on this page.
   */
  private announceSiteWideCodes(): void {
    if (!this.embedReviewSurface) return;
    // Runs every settled change-detection pass. `registerChecks` compares what
    // it holds and returns without touching anything when the list matches, so
    // the steady state costs one array walk and changes no state.

    const urlId = this.pageUrlId.trim();
    if (!urlId) return;

    const siteWide = this.reviewModel.reviewSiteWideIssueCodes();
    const register = (cardKey: string, issues: OnpageIssueView[]) => {
      if (!cardKey) return;
      this.reviewSelection.registerChecks(
        urlId,
        cardKey,
        this.reviewTechnicalIssues(issues).map((issue) => ({
          code: issue.code,
          cardIssueKey: cardKey,
          siteWide: Boolean(siteWide?.has(issue.code))
        }))
      );
    };

    register(
      this.buildTechnicalCriticalIssuePayload()?.issueKey || '',
      this.technicalCriticalIssueViews()
    );
    register(
      this.buildTechnicalWarningsIssuePayload()?.issueKey || '',
      this.technicalWarningIssueViews()
    );
  }

  /**
   * Whether one technical finding is ticked, on the review surface.
   *
   * The card tick says "fix this card"; these say which of its findings that
   * covers. Both are needed: a page with eight findings where six are wanted is
   * the common case, and before this the operator's only move was to take all
   * eight or none.
   */
  /**
   * The row-level ticks on the two content cards.
   *
   * Supporting pages lists pages to write and Missing sections lists sections to
   * add, and both used to be all-or-nothing: an operator who wanted one of two
   * pages had to take both or neither. Technical findings got their own ticks
   * first; there was never a reason for the content cards to be different.
   *
   * Keyed by card and row rather than by the kind of thing the row is, because
   * unlike a technical check these are not remembered — see the note on
   * `cardItemsByUrlId`.
   */
  private reviewCardKey(type: 'topic_ownership' | 'section_gap'): string {
    const urlId = (this.pageUrlId || '').trim();
    return urlId ? buildPlanV2IssueKey(urlId, type) : '';
  }

  /**
   * Whether a card is going, as its rows need to know.
   *
   * The card tick is a master switch. With it off, nothing on the card is being
   * sent, so rows that still show ticks are describing a decision that is not
   * happening — which is what made "Not this one" sit under two ticked pages.
   *
   * Rows read this to render unticked AND disabled while the card is off.
   * Disabled rather than merely unticked, for the same reason the emptied-card
   * click had to be handled: a checkbox the user can click but whose bound
   * value cannot change leaves the browser showing a tick the model denies.
   * There is also nothing to choose — no row is going either way.
   *
   * Their own state is untouched underneath, so turning the card back on
   * restores what was picked rather than flattening it.
   */
  reviewCardSending(type: 'topic_ownership' | 'section_gap'): boolean {
    const key = this.reviewCardKey(type);
    return !key || this.reviewSelection.isSelected(key);
  }

  /** Same question for the two technical cards, which key off their own payloads. */
  reviewTechnicalCardSending(payload: PlanV2IssueCardPayload | null | undefined): boolean {
    const key = payload?.issueKey || '';
    return !key || this.reviewSelection.isSelected(key);
  }

  isSupportingPageTicked(gapUrl: string): boolean {
    return this.reviewSelection.isItemTicked(
      this.reviewSelection.itemKey(this.reviewCardKey('topic_ownership'), gapUrl)
    );
  }

  toggleSupportingPage(gapUrl: string, event: Event): void {
    event.stopPropagation();
    const card = this.reviewCardKey('topic_ownership');
    // Clicking a row on a card that is off turns the card back on rather than
    // toggling into a state nothing can see.
    if (!this.reviewCardSending('topic_ownership')) {
      this.reviewSelection.reactivateCardWithItem(this.pageUrlId.trim(), card, gapUrl);
      return;
    }
    this.reviewSelection.toggleItem(this.reviewSelection.itemKey(card, gapUrl));
  }

  isMissingSectionTicked(rowId: string): boolean {
    return this.reviewSelection.isItemTicked(
      this.reviewSelection.itemKey(this.reviewCardKey('section_gap'), rowId)
    );
  }

  toggleMissingSection(rowId: string, event: Event): void {
    event.stopPropagation();
    const card = this.reviewCardKey('section_gap');
    if (!this.reviewCardSending('section_gap')) {
      this.reviewSelection.reactivateCardWithItem(this.pageUrlId.trim(), card, rowId);
      return;
    }
    this.reviewSelection.toggleItem(this.reviewSelection.itemKey(card, rowId));
  }

  /**
   * The internal-link suggestions on this page, as individual decisions.
   *
   * One registered item per link rather than one for the card, which is the
   * opposite of every other card here and is the honest shape for this one: a
   * link task belongs on the SOURCE page, where somebody edits the copy, not on
   * the page being reviewed. Inventory has always filed them that way, and one
   * bundled item filed here would put the work under the wrong page.
   *
   * `register` is bucketed by the page being reviewed — which is how the send
   * finds them — while each payload carries its own source `urlId`. Same keys
   * Inventory writes, so a link sent from either surface is the same record.
   */
  private announceReviewInternalLinks(urlId: string): void {
    for (const row of this.pageSignalInternalLinkDetailsPendingTriage()) {
      this.reviewSelection.register(
        urlId,
        composeInternalLinkWorkItemOnSourcePage(row, this.embedPlanV2Row?.priorityScore ?? null)
      );
    }
  }

  /** Every link on this card, as issue keys, for the master tick to act on. */
  private reviewInternalLinkKeys(): string[] {
    return this.pageSignalInternalLinkDetailsPendingTriage().map((row) =>
      internalLinkWorkItemIssueKey(row)
    );
  }

  isInternalLinkTicked(row: ActionPlanInternalLinkRow): boolean {
    return this.reviewSelection.isSelected(internalLinkWorkItemIssueKey(row));
  }

  toggleInternalLink(row: ActionPlanInternalLinkRow, event: Event): void {
    event.stopPropagation();
    const key = internalLinkWorkItemIssueKey(row);
    // Card off: the click means "this one", the same as every other card here.
    if (!this.reviewInternalLinksSending()) {
      for (const other of this.reviewInternalLinkKeys()) {
        const wanted = other === key;
        if (this.reviewSelection.isSelected(other) !== wanted) this.reviewSelection.toggle(other);
      }
      return;
    }
    this.reviewSelection.toggle(key);
  }

  /** The card's master tick: on when anything under it is going. */
  reviewInternalLinksSending(): boolean {
    return this.reviewInternalLinkKeys().some((key) => this.reviewSelection.isSelected(key));
  }

  /** Whether this card has links the operator can still decide on. */
  reviewInternalLinkTickable(): boolean {
    return this.embedReviewSurface && this.reviewInternalLinkKeys().length > 0;
  }

  reviewInternalLinksCountLabel(): string | null {
    if (!this.embedReviewSurface) return null;
    const n = this.reviewInternalLinkKeys().length;
    return n > 0 ? this.reviewCountBadge(n, 'LINK', 'LINKS') : null;
  }

  /**
   * The card tick, over rows that are each their own decision.
   *
   * Turning it off unticks every link; turning it on ticks them all. There is
   * no separate card record to write — unlike the other cards, this one is a
   * heading over N decisions rather than a decision of its own.
   */
  toggleReviewInternalLinksCard(event: Event): void {
    event.stopPropagation();
    const on = this.reviewInternalLinksSending();
    for (const key of this.reviewInternalLinkKeys()) {
      if (this.reviewSelection.isSelected(key) === on) this.reviewSelection.toggle(key);
    }
  }

  /** Hands both content cards' rows to the send, the way the checks are handed over. */
  private announceReviewCardItems(): void {
    if (!this.embedReviewSurface) return;
    const urlId = (this.pageUrlId || '').trim();
    if (!urlId) return;

    this.announceReviewInternalLinks(urlId);

    const pagesKey = this.reviewCardKey('topic_ownership');
    if (pagesKey) {
      this.reviewSelection.registerCardItems(
        urlId,
        pagesKey,
        this.reviewSupportingPagesColumns().flatMap((col) => col.gaps.map((g) => g.url))
      );
    }

    const sectionsKey = this.reviewCardKey('section_gap');
    if (sectionsKey) {
      this.reviewSelection.registerCardItems(
        urlId,
        sectionsKey,
        (this.reviewMissingSections()?.rows ?? []).map((r) => r.id)
      );
    }
  }

  isTechnicalCheckTicked(issue: OnpageIssueView, cardKey = ''): boolean {
    return cardKey
      ? this.reviewSelection.isCheckTickedOnCard(cardKey, issue.code)
      : this.reviewSelection.isCheckTicked(issue.code);
  }

  toggleTechnicalCheck(issue: OnpageIssueView, cardKey: string, event: Event): void {
    event.stopPropagation();
    if (cardKey && !this.reviewSelection.isSelected(cardKey)) {
      this.reviewSelection.reactivateCardWithCheck(this.pageUrlId.trim(), cardKey, issue.code);
      return;
    }
    this.reviewSelection.toggleCheck(issue.code, this.pageUrlId.trim(), cardKey);
  }

  /**
   * "Not fixing this — you unticked it on an earlier page."
   *
   * Said out loud rather than left as a quietly empty checkbox, because a
   * default the operator did not set on THIS page is a default they have to be
   * told about. Null on the page where the untick happened: they just did it.
   */
  technicalCheckMutedNote(issue: OnpageIssueView, cardKey = ''): string | null {
    if (!this.embedReviewSurface) return null;
    if (this.reviewSelection.checkMutedEarlier(issue.code, this.pageUrlId.trim())) {
      return 'muted earlier';
    }
    // Said on the page where the decision is made, not two pages later.
    //
    // Some unticks are sticky and some are not, and they look almost identical.
    // The operator finding that out on page 3 is finding out too late — this is
    // the moment they make the choice and the only moment changing it is cheap.
    //
    // It marks exactly what the send will remember, so its absence is as
    // informative as its presence: no label means the next page asks again.
    if (!cardKey) return null;
    const siteWide = Boolean(this.reviewModel.reviewSiteWideIssueCodes()?.has(issue.code));

    // Card turned off: it decides its site-wide checks and nothing else. The
    // rows all LOOK unticked here, so without the label a page-specific one
    // would be indistinguishable from a site-wide one that is about to be
    // muted for the rest of the queue.
    if (!this.reviewSelection.isSelected(cardKey)) {
      return siteWide ? 'muted for this period' : null;
    }

    // Unticked on its own: remembered whatever its scope, which is what the
    // operator asked for — a check they are not fixing is not fixed anywhere.
    return this.reviewSelection.isCheckTickedOnCard(cardKey, issue.code)
      ? null
      : 'muted for this period';
  }

  technicalIssueScopeLabel(issue: OnpageIssueView): string | null {
    return this.reviewModel.technicalIssueScopeLabel(issue);
  }

  /**
   * Whether this card, as the surface renders it, has a gap.
   *
   * The badge has to describe the body underneath it, and the two surfaces show
   * different bodies. Inventory's card holds all four findings, so the broad
   * predicate is right there. Review's holds pages only — sections and rank
   * gaps have their own card now — so the broad predicate would put GAP FOUND
   * over "Nothing on this topic that they have and you don't", which is the
   * card asserting the opposite of what it shows.
   */
  supportingPagesGapForThisSurface(): boolean {
    return this.embedReviewSurface
      ? this.hasReviewSupportingPagesGaps()
      : this.hasSupportingPagesActionableGap();
  }

  topicOwnershipGapChipLabel(): string {
    if (this.embedReviewSurface) {
      const n = this.reviewSupportingPagesColumns().reduce((sum, c) => sum + c.gaps.length, 0);
      return this.reviewCountBadge(n, 'PAGE', 'PAGES');
    }
    return this.supportingPagesGapForThisSurface() ? 'GAP FOUND' : 'NO GAP';
  }

  topicOwnershipGapChipTone(): DiagnosisTone {
    return this.supportingPagesGapForThisSurface() ? 'warn' : 'ok';
  }

  supportingPagesUnifiedLoading(): boolean {
    return this.topicOwnershipStatus === 'loading' || this.topicOwnershipV2Status === 'loading';
  }

  private supportingPagesGateState(): SupportingPagesGateState {
    return this.reviewModel.supportingPagesGateState();
  }

  supportingPagesSettling(): boolean {
    return this.reviewModel.supportingPagesSettling();
  }

  getSupportingPagesColumns(): SupportingPagesUnifiedColumn[] {
    return this.reviewModel.getSupportingPagesColumns();
  }

  private allMarkedV2Rows(): SupportingPagesV2RowResult[] {
    return this.reviewModel.allMarkedV2Rows();
  }

  private ensureMarkedSerpCompetitorEvals(): void {
    const focalUrl = (this.url || '').trim();
    if (!focalUrl || !this.pageSerp?.results?.length || !this.markedCompetitorDomains.length) {
      return;
    }
    if (this.topicOwnershipV2Status === 'loading' || this.topicOwnershipStatus === 'loading') {
      return;
    }

    const missing = this.reviewModel.missingMarkedSerpSeeds();
    if (!missing.length) return;

    // The third source. It adds rows to a card the other two have already
    // reported ready, which on a review surface means the operator watches a
    // verdict change under them -- see `supportingPagesSettling`.
    const focalQuery = this.focalQueryForRankDrivenGaps();
    const token = this.markedSerpEval.begin();
    const subscription = this.dataForSEOService
      .postSupportingPagesOpportunityEval({
        focalUrl,
        competitorUrls: missing.map((s) => s.url),
        yourCandidateUrls: [],
        focalQuery,
        v1ByCompetitor: missing.map((s) => ({
          competitorUrl: s.url,
          similarUrl: null,
          score: null
        }))
      })
      .subscribe({
        next: (res) => {
          // False means this answers a question the operator has moved on from.
          if (!this.markedSerpEval.settle(token)) return;
          const rows = res.data?.rows || [];
          if (!rows.length) return;
          const keys = new Set(
            this.markedSerpCompetitorV2Rows.map((r) => normalizeUrlForCompare(r.competitorUrl))
          );
          const added = rows.filter((r) => !keys.has(normalizeUrlForCompare(r.competitorUrl)));
          if (added.length) {
            this.markedSerpCompetitorV2Rows = [...this.markedSerpCompetitorV2Rows, ...added];
          }
        },
        error: () => {
          this.markedSerpEval.settle(token);
        }
      });
    this.markedSerpEval.track(subscription);
  }

  supportingPagesShowHeroChips(col: SupportingPagesUnifiedColumn): boolean {
    return !col.isYourSite && !!(col.heroUrl || '').trim();
  }

  private normalizeSerpDomain(domain: string): string {
    return this.reviewModel.normalizeSerpDomain(domain);
  }

  topicOwnershipHeroLinkTitle(col: SupportingPagesUnifiedColumn): string {
    if (col.isYourSite) return col.heroUrl;
    const row = col.heroRow || { url: col.heroUrl, seoIntentScore: 0, topics: [] };
    return this.topicOwnershipLinkTitle(
      { domain: col.domain, isYourSite: false, rows: col.supportingRows },
      row
    );
  }

  topicOwnershipSupportingLinkTitle(col: SupportingPagesUnifiedColumn, row: DomainTopicUrlRow): string {
    return this.topicOwnershipLinkTitle(
      { domain: col.domain, isYourSite: col.isYourSite, rows: col.supportingRows },
      row
    );
  }

  getVisibleSupportingRows(col: SupportingPagesUnifiedColumn): DomainTopicUrlRow[] {
    if (this.isTopicOwnershipPagesExpanded(col.domain)) return col.supportingRows;
    return col.supportingRows.slice(0, SUPPORTING_PAGE_USER_INITIAL_VISIBLE_COUNT);
  }

  hasMoreSupportingRowsInColumn(col: SupportingPagesUnifiedColumn): boolean {
    return col.supportingRows.length > SUPPORTING_PAGE_USER_INITIAL_VISIBLE_COUNT;
  }

  supportingRowsExpandLabel(col: SupportingPagesUnifiedColumn): string {
    if (this.isTopicOwnershipPagesExpanded(col.domain)) return 'Show fewer pages';
    const hidden = Math.max(0, col.supportingRows.length - SUPPORTING_PAGE_USER_INITIAL_VISIBLE_COUNT);
    return hidden > 0 ? `Show more pages (${hidden} more)` : 'Show more pages';
  }

  topicOwnershipV2RowForColumn(col: SupportingPagesUnifiedColumn): SupportingPagesV2RowResult | null {
    return col.v2Row;
  }

  /**
   * Whether to show the missing-sections chip at all.
   *
   * Must use the same count as the label beside it. Gating on the stored
   * `missingSectionCount` while labelling from the filtered set is how a chip
   * ends up reading "0 missing sections".
   */
  topicOwnershipV2HasMissingSections(row: SupportingPagesV2RowResult): boolean {
    return hasSectionGapsForV2Row(row);
  }

  topicOwnershipV2MissingSectionsChipLabel(row: SupportingPagesV2RowResult): string {
    // The filtered count, so the chip and the list it opens agree.
    const count = sectionGapsForV2Row(row).length;
    if (count === 1) return '1 missing section';
    return `${count} missing sections`;
  }

  topicOwnershipV2CoveredElsewhereChipLabel(row: SupportingPagesV2RowResult): string {
    return row.coveredElsewhereUrl ? 'Covered elsewhere' : 'Not covered elsewhere';
  }

  topicOwnershipV2CoveredElsewhereChipTitle(row: SupportingPagesV2RowResult): string {
    if (row.coveredElsewhereUrl) {
      return `${row.coveredElsewhereLabel || 'Covered elsewhere'}: ${row.coveredElsewhereUrl}`;
    }
    return 'No similar supporting page found elsewhere on your site for this competitor URL.';
  }

  topicOwnershipV2HasListInventory(row: SupportingPagesV2RowResult): boolean {
    return !!(
      row.listInventoryDiff?.detected && (row.listInventoryDiff.competitorOnly?.length ?? 0) > 0
    );
  }

  topicOwnershipV2ListInventoryChipLabel(row: SupportingPagesV2RowResult): string {
    const count = row.listInventoryDiff?.competitorOnly?.length ?? 0;
    if (count === 1) return '1 tool on their list, not on yours';
    return `${count} tools on their list, not on yours`;
  }

  topicOwnershipV2ListInventoryChipTitle(row: SupportingPagesV2RowResult): string {
    const diff = row.listInventoryDiff;
    if (!diff?.detected) return '';
    const missing = (diff.competitorOnly || []).map((item) => item.label).join(', ');
    return `Roundup list diff — on their list but not mentioned on your page: ${missing}`;
  }

  focalQueryForRankDrivenGaps(): string | undefined {
    return this.reviewModel.focalQueryForRankDrivenGaps();
  }

  topSerpRivalRankDrivenGaps(): RankDrivenSectionGap[] {
    return this.reviewModel.topSerpRivalRankDrivenGaps();
  }

  topicOwnershipV2RankDrivenGaps(row: SupportingPagesV2RowResult): RankDrivenSectionGap[] {
    return rankDrivenGapsForV2Row(row, {
      focalQuery: this.focalQueryForRankDrivenGaps(),
      focalAnalysis: this.analysisData,
      rivalAnalysis: undefined
    });
  }

  rankDrivenPassageSegments(keyword: string, passage: string | null | undefined) {
    return splitPassageByKeywordTokens(keyword, passage || '');
  }

  readonly rankDrivenHeadingTag = rankDrivenHeadingTag;

  topicOwnershipV2HasRankDrivenGaps(row: SupportingPagesV2RowResult): boolean {
    return this.topicOwnershipV2RankDrivenGaps(row).length > 0;
  }

  topicOwnershipV2RankDrivenChipLabel(row: SupportingPagesV2RowResult): string {
    const count = this.topicOwnershipV2RankDrivenGaps(row).length;
    if (count === 1) return '1 rank-driven gap';
    return `${count} rank-driven gaps`;
  }

  topicOwnershipV2RankDrivenChipTitle(row: SupportingPagesV2RowResult): string {
    const lines = this.topicOwnershipV2RankDrivenGaps(row).map((g) => g.evidenceLine);
    return lines.join(' ');
  }

  isTopicOwnershipV2RankDrivenOpen(col: SupportingPagesUnifiedColumn): boolean {
    const v2 = col.v2Row;
    return !!v2 && this.topicOwnershipV2RankDrivenUrl === v2.competitorUrl;
  }

  toggleTopicOwnershipV2RankDriven(competitorUrl: string): void {
    if (this.topicOwnershipV2RankDrivenUrl === competitorUrl) {
      this.topicOwnershipV2RankDrivenUrl = '';
      return;
    }
    this.topicOwnershipV2RankDrivenUrl = competitorUrl;
    this.topicOwnershipV2DetailUrl = '';
    this.topicOwnershipV2ListInventoryUrl = '';
    this.topicOwnershipV2CoveredElsewhereUrl = '';
  }

  isTopicOwnershipV2ListInventoryOpen(col: SupportingPagesUnifiedColumn): boolean {
    const v2 = col.v2Row;
    return !!v2 && this.topicOwnershipV2ListInventoryUrl === v2.competitorUrl;
  }

  toggleTopicOwnershipV2ListInventory(competitorUrl: string): void {
    if (this.topicOwnershipV2ListInventoryUrl === competitorUrl) {
      this.topicOwnershipV2ListInventoryUrl = '';
      this.topicOwnershipV2RankDrivenUrl = '';
      return;
    }
    this.topicOwnershipV2ListInventoryUrl = competitorUrl;
    this.topicOwnershipV2DetailUrl = '';
    this.topicOwnershipV2RankDrivenUrl = '';
    this.topicOwnershipV2CoveredElsewhereUrl = '';
  }

  isTopicOwnershipV2CompareOpen(col: SupportingPagesUnifiedColumn): boolean {
    const v2 = col.v2Row;
    return !!v2 && this.topicOwnershipV2DetailUrl === v2.competitorUrl;
  }

  /** Missing-sections compare panel — rendered full-width below domain columns. */
  getOpenSectionCompareV2Row(): SupportingPagesV2RowResult | null {
    const detailUrl = this.topicOwnershipV2DetailUrl?.trim();
    if (!detailUrl) return null;
    const row = this.mergedV2Rows().find(
      (r) => normalizeUrlForCompare(r.competitorUrl) === normalizeUrlForCompare(detailUrl)
    );
    if (!row || !this.topicOwnershipV2HasSectionCoverage(row)) return null;
    return row;
  }

  isTopicOwnershipV2CoveredElsewhereOpen(col: SupportingPagesUnifiedColumn): boolean {
    const v2 = col.v2Row;
    return !!v2 && this.topicOwnershipV2CoveredElsewhereUrl === v2.competitorUrl;
  }

  toggleTopicOwnershipV2CoveredElsewhere(competitorUrl: string): void {
    if (this.topicOwnershipV2CoveredElsewhereUrl === competitorUrl) {
      this.topicOwnershipV2CoveredElsewhereUrl = '';
      return;
    }
    this.topicOwnershipV2CoveredElsewhereUrl = competitorUrl;
    this.topicOwnershipV2DetailUrl = '';
    this.topicOwnershipV2ListInventoryUrl = '';
    this.topicOwnershipV2RankDrivenUrl = '';
  }

  topicOwnershipCompareOptions(): TopicOwnershipCompareOption[] {
    const seen = new Set<string>();
    const options: TopicOwnershipCompareOption[] = [];
    for (const block of this.topicOwnershipDomains) {
      if (block.isYourSite) continue;
      for (const row of block.rows) {
        const normalized = normalizeUrlForCompare(row.url);
        if (!normalized || seen.has(normalized)) continue;
        seen.add(normalized);
        const path = this.topicOwnershipPagePath(row.url);
        const traffic = this.topicOwnershipPageTraffic(row.estimatedOrganicTraffic);
        const label = traffic ? `${block.domain} · ${path} (${traffic})` : `${block.domain} · ${path}`;
        options.push({
          url: row.url,
          label,
          seoIntentScore: row.seoIntentScore,
          focalMatchScore: row.focalMatchScore
        });
      }
    }
    const sorted = options.sort((a, b) =>
      compareSupportingPagesByRelevance(
        { focalMatchScore: a.focalMatchScore, seoIntentScore: a.seoIntentScore ?? 0 },
        { focalMatchScore: b.focalMatchScore, seoIntentScore: b.seoIntentScore ?? 0 }
      )
    );

    // The SERP winner is prepended rather than sorted in, and unsorted is the
    // point: this list is ordered by topical relevance, and the page that took
    // the position is not competing on that axis. It usually is not in the
    // topic snapshot at all — nobody marked `projectmanagementacademy.net`.
    const target = this.compareDefaultTarget();
    if (!target) return sorted;
    const key = normalizeUrlForCompare(target.url);
    if (!key || seen.has(key)) return sorted;

    return [
      {
        url: target.url,
        label: `${target.domain} · ${this.topicOwnershipPagePath(target.url)}${
          target.position ? ` (#${target.position} on this SERP)` : ''
        }`
      },
      ...sorted
    ];
  }

  /**
   * The auto-selected compare target (§3.1) — what the Compare button opens on.
   *
   * Null in every state where there is nothing to compare, so the button and
   * the "why this page" line disappear together rather than the button
   * offering a comparison the row has just said cannot be made.
   */
  compareDefaultTarget(): CompareSummaryTarget | null {
    if (!this.pageCompare) return null;
    return compareDrawerTarget(this.pageCompare);
  }

  /** The sentence under the button: why THIS page is the default. */
  compareDefaultReason(): string | null {
    return this.compareDefaultTarget()?.reason ?? null;
  }

  openCompareDefault(): void {
    const target = this.compareDefaultTarget();
    if (target) this.openQuickCompareWithUrl(target.url);
  }

  openQuickCompareWithUrl(compareUrl: string): void {
    const primaryUrl = this.normalizeNavigableUrl(this.url || '');
    const normalizedCompare = this.normalizeNavigableUrl(compareUrl);
    if (!primaryUrl || !normalizedCompare) return;
    this.quickComparePrimaryUrl = primaryUrl;
    this.quickCompareCompareUrl = normalizedCompare;
    this.quickCompareOpen = true;
  }

  onTopicOwnershipCompareSelected(competitorUrl: string): void {
    if (!competitorUrl?.trim()) return;
    this.openQuickCompareWithUrl(competitorUrl);
    this.topicOwnershipCompareUrl = '';
  }

  private reviewCountBadge(n: number, one: string, many: string): string {
    return this.reviewModel.reviewCountBadge(n, one, many);
  }

  technicalIssuesActionChipLabel(): string {
    return this.reviewModel.technicalIssuesActionChipLabel();
  }

  technicalIssuesActionChipTone(): DiagnosisTone {
    return this.hasTechnicalCriticalIssues()
      ? 'warn'
      : 'ok';
  }

  technicalWarningsActionChipLabel(): string {
    return this.reviewModel.technicalWarningsActionChipLabel();
  }

  technicalWarningsActionChipTone(): DiagnosisTone {
    return this.hasTechnicalWarnings()
      ? 'warn'
      : 'ok';
  }

  pageIntentMatchActionChipLabel(): string {
    return this.reviewModel.pageIntentMatchActionChipLabel();
  }

  pageIntentMatchActionChipTone(): DiagnosisTone {
    return this.reviewModel.pageIntentMatchActionChipTone();
  }

  showSerpPhraseMatchActionChip(): boolean {
    if (!this.pageIntentDiagnosis) return false;
    return (
      this.hasLexicalVocabChips() ||
      this.showFragmentedSerpVocabularyNote() ||
      (this.pageIntentDiagnosis.missingTerms?.length ?? 0) > 0 ||
      this.pageIntentDiagnosis.answerStructureAligned === false
    );
  }

  serpPhraseMatchActionChipLabel(): string {
    const d = this.pageIntentDiagnosis;
    if (!d) return '—';
    const missing = d.missingTerms?.length ?? 0;
    const structureMismatch =
      Boolean(d.answerStructureFamily) &&
      d.answerStructureFamily !== 'unknown' &&
      d.answerStructureFamily !== 'fragmented' &&
      d.answerStructureAligned === false;
    if (missing === 0 && !structureMismatch) {
      return this.showFragmentedSerpVocabularyNote() ? 'REVIEW STRUCTURE' : 'ALIGNED';
    }
    if (structureMismatch && missing === 0) return 'STRUCTURE GAP';
    return missing > 0 ? 'MISSING SIGNALS' : 'ALIGNED';
  }

  serpPhraseMatchActionChipTone(): DiagnosisTone {
    return this.reviewModel.serpPhraseMatchActionChipTone();
  }

  serpDifficultyBand(): 'easy' | 'medium' | 'hard' | 'unknown' {
    const kd = this.pageSerp?.keywordDifficulty;
    if (!Number.isFinite(kd)) return 'unknown';
    const n = Number(kd);
    if (n < 15) return 'easy';
    if (n <= 25) return 'medium';
    return 'hard';
  }

  serpDifficultyActionChipLabel(): string {
    const band = this.serpDifficultyBand();
    if (band === 'unknown') return '—';
    return band.toUpperCase();
  }

  serpDifficultyActionChipTone(): DiagnosisTone {
    const band = this.serpDifficultyBand();
    if (band === 'easy') return 'ok';
    if (band === 'medium') return 'neutral';
    if (band === 'hard') return 'warn';
    return 'muted';
  }

  pageTypeMixedAlignment(): MixedSerpAlignment | null {
    return this.reviewModel.pageTypeMixedAlignment();
  }

  pageTypeExamples(): PageTypeExample[] {
    return this.reviewModel.pageTypeExamples();
  }

  pageTypeExamplesLabel(): string {
    return this.reviewModel.pageTypeExamplesLabel();
  }

  pageTypeBriefLines(): string[] {
    return this.reviewModel.pageTypeBriefLines();
  }

  /** Answer structure & entities card brief. */
  serpAnswerStructureIsFragmented(): boolean {
    return this.reviewModel.serpAnswerStructureIsFragmented();
  }

  answerStructureBriefLines(): string[] {
    return this.reviewModel.answerStructureBriefLines();
  }

  /** @deprecated Prefer pageTypeBriefLines / answerStructureBriefLines. */
  pageIntentBriefLines(): string[] {
    return this.pageTypeBriefLines();
  }

  showSerpPhraseMatchCard(): boolean {
    return this.reviewModel.showSerpPhraseMatchCard();
  }

  serpKeywordDifficultyShort(): string {
    const kd = this.pageSerp?.keywordDifficulty;
    if (!Number.isFinite(kd)) return '—';
    return `${Math.round(Number(kd))}`;
  }

  /** Shown only when KD is missing — not the scale/source line. */
  serpKeywordDifficultyHint(): string | null {
    if (!Number.isFinite(this.pageSerp?.keywordDifficulty)) {
      return 'Refresh SERP to load keyword difficulty.';
    }
    return null;
  }

  contentFreshnessVerdict(): string | null {
    return this.reviewModel.contentFreshnessVerdict();
  }

  pageSerpQuerySourceLabel(): string {
    const source = this.pageSerp?.querySource;
    if (source === 'strategist_override') return 'Strategist override';
    if (source === 'gsc_primary') return 'Search Console';
    if (source === 'realization') return 'Realization keyword';
    if (source === 'ceiling_driver') return 'Ceiling driver';
    if (source === 'claim_fallback') return 'Claim fallback';
    return '—';
  }

  pageSerpFetchedLabel(): string {
    if (!this.pageSerp?.fetchedAt) return '—';
    try {
      return new Date(this.pageSerp.fetchedAt).toLocaleString();
    } catch {
      return this.pageSerp.fetchedAt;
    }
  }

  pageSerpKeywordDifficultyLabel(): string | null {
    const kd = this.pageSerp?.keywordDifficulty;
    if (!Number.isFinite(kd)) return null;
    const source =
      this.pageSerp?.keywordDifficultyDataSource === 'api' ? 'Labs API' : 'cached';
    return `Keyword difficulty ${Math.round(Number(kd))}/100 (${source})`;
  }

  pageSerpKeywordDifficultyTitle(): string {
    return (
      'DataForSEO Labs keyword difficulty: how hard it is to rank in the top 10 for this target query ' +
      '(0 = easy, 100 = very hard). Updated on URL SERP refresh. Not per-domain authority.'
    );
  }

  pageSerpYourPresenceLabel(): string {
    if (!this.pageSerp) return '—';
    if (this.pageSerp.yourPosition === null) return 'Not in top 10';
    const match =
      this.pageSerp.matchType === 'exact_url'
        ? 'exact URL'
        : this.pageSerp.matchType === 'domain'
          ? 'domain match'
          : 'match';
    return `#${this.pageSerp.yourPosition} (${match})`;
  }

  pageSerpResultCompetitorKind(row: PageSerpResultRow): 'you' | 'marked' | 'unknown' {
    const activeDomain = this.normalizeSerpDomain(this.extractDomainFromUrl(this.url || ''));
    const rowDomain = this.normalizeSerpDomain(row.domain);
    if (activeDomain && rowDomain === activeDomain) return 'you';
    if (rowDomain && this.markedCompetitorDomains.some((d) => this.normalizeSerpDomain(d) === rowDomain)) {
      return 'marked';
    }
    return 'unknown';
  }

  pageSerpResultCompetitorLabel(row: PageSerpResultRow): string {
    const kind = this.pageSerpResultCompetitorKind(row);
    if (kind === 'you') return 'You';
    if (kind === 'marked') return 'Marked competitor';
    return 'Unknown';
  }

  pageSerpRowIntentFamily(row: PageSerpResultRow): string {
    return this.reviewModel.pageSerpRowIntentFamily(row);
  }

  pageSerpRowIntentLabel(row: PageSerpResultRow): string {
    return this.reviewModel.pageSerpRowIntentLabel(row);
  }

  trackPageSerpRow(_index: number, row: PageSerpResultRow): string {
    return `${row.position}::${row.domain}::${row.url}`;
  }

  pageSerpSnippetSegments(row: PageSerpResultRow): SerpSnippetSegment[] {
    return buildSerpSnippetSegments(row.description || '', row.highlighted);
  }

  /** Top-10 rows whose snippet contains the hovered chip (winners compare pane). */
  pageSerpCompareWinnerRows(): PageSerpResultRow[] {
    if (!this.hoveredVocabChip || !this.pageSerp?.results?.length) return [];
    return this.pageSerp.results.filter((row) => this.pageSerpRowHasVocabChipMatch(row));
  }

  pageSerpCompareWinnerRowSegments(row: PageSerpResultRow): SerpSnippetSegment[] {
    if (!this.hoveredVocabChip) return [];
    const snippet = [row.title, row.description].filter(Boolean).join(' — ');
    return buildSerpFoldedTermSegments(snippet, this.hoveredVocabChip, foldLexicalToken);
  }

  pageSerpRowHasVocabChipMatch(row: PageSerpResultRow): boolean {
    if (!this.hoveredVocabChip) return false;
    const snippet = [row.title, row.description].filter(Boolean).join(' — ');
    if (!snippet.trim()) return false;
    return buildSerpFoldedTermSegments(snippet, this.hoveredVocabChip, foldLexicalToken).some(
      (s) => s.emphasized
    );
  }

  hasPageIntentDiagnosis(): boolean {
    return this.reviewModel.hasPageIntentDiagnosis();
  }

  hasPageIntentCompare(): boolean {
    return this.reviewModel.hasPageIntentCompare();
  }

  searchIntentStructurePagePassages(): Array<{
    label: string;
    text: string;
    matchKind: 'match' | 'closest';
    family?: string;
    evidenceSpans: string[];
    questionSpans: string[];
    headingIsQuestion: boolean;
    fitPct: number | null;
  }> {
    return this.reviewModel.searchIntentStructurePagePassages();
  }

  /** @deprecated Prefer searchIntentStructurePagePassages(). */
  searchIntentStructurePagePassage(): { label: string; text: string; matchKind: 'match' | 'closest' } | null {
    return this.searchIntentStructurePagePassages()[0] || null;
  }

  structurePassageHeading(label: string): { tag: string | null; title: string } {
    return parseStructurePassageHeading(label);
  }

  /** @deprecated Prefer per-passage fitPct on searchIntentStructurePagePassages(). */
  answerStructureMatchPct(): number | null {
    return null;
  }

  answerStructurePassageFitTitle(passage: {
    matchKind: 'match' | 'closest';
    fitPct: number | null;
    family?: string;
    headingIsQuestion?: boolean;
    questionSpans?: string[];
  }): string {
    const family = this.answerStructureFamilyLabel(
      passage.family || this.pageIntentDiagnosis?.answerStructureFamily
    );
    const pct = passage.fitPct;
    const questionNote =
      passage.headingIsQuestion || (passage.questionSpans?.length || 0) > 0
        ? ' Includes a bonus for restating the target query as a question.'
        : '';
    if (pct == null) return `How strongly this passage fits ${family} delivery.${questionNote}`;
    if (passage.matchKind === 'closest') {
      return `${pct}% fit — nearest delivery pattern on the page, not an exact ${family} match.${questionNote}`;
    }
    return `${pct}% fit — how strongly this passage exhibits ${family} delivery (cues + highlighted evidence).${questionNote}`;
  }

  searchIntentStructurePassageSegments(
    text: string,
    evidenceSpans?: string[] | null,
    questionSpans?: string[] | null,
    matchKind?: 'match' | 'closest'
  ): SerpSnippetSegment[] {
    // Keep Our-page structure view stable on chip hover — hover only filters the winners column.
    const spans =
      evidenceSpans ||
      this.pageIntentDiagnosis?.answerStructurePageEvidenceSpans ||
      [];
    return buildCollapsedStructurePassageSegments(text || '', spans, {
      questionSpans,
      matchKind
    });
  }

  structurePassageSegmentClass(
    seg: SerpSnippetSegment,
    passage: { matchKind: 'match' | 'closest' }
  ): string {
    if (!seg.emphasized) return '';
    if (seg.highlightKind === 'question') return 'url-serp-question-term';
    if (passage.matchKind === 'closest' || seg.highlightKind === 'closest') {
      return 'url-serp-closest-term';
    }
    return 'url-serp-contract-term';
  }

  hasSearchIntentQuestionEvidence(): boolean {
    return this.searchIntentStructurePagePassages().some(
      (p) => p.headingIsQuestion || (p.questionSpans?.length || 0) > 0
    );
  }

  onVocabChipHover(term: string | null): void {
    this.hoveredVocabChip = term;
  }

  clearVocabChipHover(): void {
    this.hoveredVocabChip = null;
  }

  answerStructureFamilyLabel(family: string | null | undefined): string {
    if (!family || family === 'unknown') return 'unknown';
    if (family === 'fragmented') return 'fragmented';
    return (
      UrlDetailComponent.ANSWER_STRUCTURE_LABELS[family] ||
      family.replace(/_/g, ' ')
    );
  }

  private static readonly ANSWER_STRUCTURE_LABELS: Record<string, string> = {
    item_list: 'item list',
    comparative_contrast: 'comparative contrast',
    numbered_howto: 'numbered how-to',
    tool_lander: 'tool lander',
    definitional_lede: 'definitional lede',
    feature_checklist: 'feature checklist',
    vs_framing: 'vs framing',
    fragmented: 'fragmented',
    unknown: 'unknown'
  };

  answerStructureSerpChipClass(): string {
    const d = this.pageIntentDiagnosis;
    const f = d?.answerStructureFamily;
    if (!f || f === 'unknown' || f === 'fragmented') {
      return 'badge badge--muted';
    }
    if (d?.answerStructureAligned) return 'badge badge--success';
    return 'badge';
  }

  answerStructureYourChipClass(): string {
    const d = this.pageIntentDiagnosis;
    if (!d?.answerStructurePageFamily || d.answerStructurePageFamily === 'unknown') {
      return 'badge badge--muted';
    }
    if (d.answerStructureAligned) return 'badge badge--success';
    return 'badge badge--warning';
  }

  answerStructureYourChipLabel(): string {
    const d = this.pageIntentDiagnosis;
    if (!d?.answerStructurePageFamily) return 'none';
    return this.answerStructureFamilyLabel(d.answerStructurePageFamily);
  }

  pageIntentContractTerms(): string[] {
    return this.pageIntentDiagnosis?.serpLexicalTerms || [];
  }

  isVocabChipHovered(term: string): boolean {
    return this.hoveredVocabChip === term;
  }

  pageIntentVocabChipTitle(term: string): string {
    if (this.hoveredVocabChip === term) {
      return `Showing “${term}” in matching top-10 snippets (right pane)`;
    }
    return `Hover to filter top-10 snippets for “${term}” (plural-aware)`;
  }

  pageIntentYourSectionSegments(text: string): SerpSnippetSegment[] {
    if (this.hoveredVocabChip) {
      return buildSerpFoldedTermSegments(text, this.hoveredVocabChip, foldLexicalToken);
    }
    return buildYourPageVocabularySegments(
      text,
      this.pageIntentContractTerms(),
      this.pageIntentDiagnosis?.serpCollocationHeads
    ).map((seg) =>
      seg.misaligned
        ? { text: seg.text, emphasized: false, misaligned: false }
        : seg
    );
  }

  pageIntentMisalignedPhraseLinks(text: string | null | undefined): MisalignedPhraseLink[] {
    if (!text?.trim()) return [];
    return collectMisalignedPhraseLinks(
      text,
      this.pageIntentContractTerms(),
      this.pageIntentDiagnosis?.serpCollocationHeads
    );
  }

  pageIntentMisalignedAriaLabel(seg: SerpSnippetSegment): string {
    const link = seg.misalignedLink;
    if (!link) return `${seg.text}: SERP vocabulary used in the wrong phrase context`;
    return `${link.head} connects to ${link.actual} on your page; winners use ${link.head} near ${link.expected}`;
  }

  pageIntentMisalignedChipTitle(term: string): string {
    const link = this.pageIntentMisalignedLinkForTerm(term);
    if (!link) return 'Used in the wrong phrase context';
    return `Your page: ${link.head} → ${link.actual}. Winners: ${link.head} → ${link.expected}.`;
  }

  private pageIntentMisalignedLinkForTerm(term: string) {
    const d = this.pageIntentDiagnosis;
    const texts = [d?.yourBestSection?.text, d?.yourIntroSection?.text].filter(Boolean) as string[];
    const termFold = term.toLowerCase().replace(/[^a-z0-9]+/g, '');
    for (const text of texts) {
      const link = this.pageIntentMisalignedPhraseLinks(text).find((l) => {
        const headFold = l.head.toLowerCase().replace(/[^a-z0-9]+/g, '');
        return headFold === termFold || headFold.startsWith(termFold) || termFold.startsWith(headFold);
      });
      if (link) return link;
    }
    return undefined;
  }

  hasLexicalVocabChips(): boolean {
    return this.reviewModel.hasLexicalVocabChips();
  }

  reviewAnswerStructureHasNothingToSend(): boolean {
    return this.reviewModel.reviewAnswerStructureHasNothingToSend();
  }

  serpVocabChipRows(): Array<{ term: string; tone: 'in_passage' | 'on_page' | 'absent' }> {
    return this.reviewModel.serpVocabChipRows();
  }

  serpVocabChipClass(tone: 'in_passage' | 'on_page' | 'absent'): string {
    if (tone === 'in_passage') return 'badge badge--success';
    if (tone === 'on_page') return 'badge badge--warning';
    return 'badge badge--danger';
  }

  private static readonly WINNER_EXCERPTS_DEFAULT = 5;

  searchIntentWinnerExcerptsAll(): NonNullable<PageIntentDiagnosis['serpWinnerExcerpts']> {
    return this.reviewModel.searchIntentWinnerExcerptsAll();
  }

  searchIntentWinnerExcerpts(): NonNullable<PageIntentDiagnosis['serpWinnerExcerpts']> {
    const all = this.searchIntentWinnerExcerptsAll();
    if (this.winnerExcerptsExpanded) return all;
    return all.slice(0, UrlDetailComponent.WINNER_EXCERPTS_DEFAULT);
  }

  searchIntentWinnerExcerptsHiddenCount(): number {
    return Math.max(
      0,
      this.searchIntentWinnerExcerptsAll().length - UrlDetailComponent.WINNER_EXCERPTS_DEFAULT
    );
  }

  canExpandWinnerExcerpts(): boolean {
    return !this.winnerExcerptsExpanded && this.searchIntentWinnerExcerptsHiddenCount() > 0;
  }

  expandWinnerExcerpts(): void {
    // Capture Our-page height before expanding so the winners box fills that space
    // (scrolls inside) instead of growing the whole compare row.
    const yours = this.serpCompareYoursRef?.nativeElement;
    const winners = this.serpCompareWinnersRef?.nativeElement;
    const h = yours ? Math.round(yours.getBoundingClientRect().height) : 0;
    if (winners && h > 0) winners.style.maxHeight = `${h}px`;
    this.winnerExcerptsExpanded = true;
  }

  collapseWinnerExcerpts(): void {
    this.winnerExcerptsExpanded = false;
    const winners = this.serpCompareWinnersRef?.nativeElement;
    if (winners) winners.style.maxHeight = '';
  }

  hasSearchIntentStructureEvidence(): boolean {
    const excerpts = this.pageIntentDiagnosis?.serpWinnerExcerpts || [];
    if (excerpts.some((e) => (e.structureEvidenceSpans?.length || 0) > 0)) return true;
    if ((this.pageIntentDiagnosis?.answerStructurePageEvidenceSpans || []).length > 0) return true;
    return (this.pageIntentDiagnosis?.answerStructureEvidence || []).some(
      (e) => (e.spans?.length || 0) > 0
    );
  }

  searchIntentExcerptSegments(excerpt: {
    excerpt: string;
    highlightTerms?: string[];
    structureEvidenceSpans?: string[];
  }): SerpSnippetSegment[] {
    return this.reviewModel.searchIntentExcerptSegments(excerpt);
  }

  searchIntentSectionGapsRivalUrl(): string | null {
    const cov = this.activeSerpRivalCoverage();
    // Same filtered count as everywhere else, or this names a rival for a
    // section-gap link that opens on an empty list.
    if (!cov || !hasSectionGapsForV2Row(cov.evalRow)) return null;
    return cov.rivalUrl;
  }

  searchIntentSectionGapsRivalDomain(): string {
    const url = this.searchIntentSectionGapsRivalUrl();
    if (!url) return 'top rival';
    return this.extractDomainFromUrl(url) || 'top rival';
  }

  openSearchIntentSectionGaps(): void {
    const rivalUrl = this.searchIntentSectionGapsRivalUrl();
    if (!rivalUrl) return;
    this.openSerpRivalSectionCompare(rivalUrl);
  }

  showFragmentedSerpVocabularyNote(): boolean {
    return this.reviewModel.showFragmentedSerpVocabularyNote();
  }

  pageIntentDiagnosisBulletClass(bullet: PageIntentDiagnosisBullet): string {
    if (bullet.severity === 'success') return 'url-serp-diagnosis-success';
    if (bullet.severity === 'warn') return 'url-serp-diagnosis-warn';
    return 'url-serp-diagnosis-info';
  }

  pageIntentFamilyLabel(family: string | null | undefined): string {
    return this.reviewModel.pageIntentFamilyLabel(family);
  }

  pageIntentYourPageFamilyLabel(): string {
    return this.reviewModel.pageIntentYourPageFamilyLabel();
  }

  pageIntentFamilyChipClassFor(family: string | null | undefined): string {
    const f = family || 'unknown';
    if (f === 'mixed' || f === 'unknown') return 'badge badge--muted';
    return 'badge';
  }

  pageIntentSerpFamilyChipClass(): string {
    const d = this.pageIntentDiagnosis;
    if (!d) return 'badge';
    if (d.serpFamily === 'mixed') return 'badge';
    if (d.serpFamily === 'unknown') return 'badge badge--muted';
    if (d.intentAlignedWithSerp) return 'badge badge--success';
    return 'badge';
  }

  pageIntentYourFamilyChipClass(): string {
    const d = this.pageIntentDiagnosis;
    if (!d) return 'badge badge--muted';
    if (d.intentAlignedWithSerp) return 'badge badge--success';
    const yours = d.pageTypeFamily || d.bestSegmentFamily;
    if (!yours || yours === 'unknown') return 'badge badge--muted';
    if (d.serpFamily === 'mixed') return 'badge badge--warning';
    return 'badge badge--warning';
  }

  showPageIntentAlignmentRow(): boolean {
    return this.reviewModel.showPageIntentAlignmentRow();
  }

  pageSerpCompactSummary(): string | null {
    if (!this.pageSerp?.results?.length) return null;
    const presence = this.pageSerpYourPresenceLabel();
    let you = 0;
    let marked = 0;
    let unknown = 0;
    for (const row of this.pageSerp.results) {
      const kind = this.pageSerpResultCompetitorKind(row);
      if (kind === 'you') you += 1;
      else if (kind === 'marked') marked += 1;
      else unknown += 1;
    }
    const parts = [presence];
    if (you) parts.push(`${you} you`);
    if (marked) parts.push(`${marked} marked`);
    if (unknown) parts.push(`${unknown} unknown`);
    return parts.join(' · ');
  }

  pageSerpPublishedDate(row: PageSerpResultRow): Date | null {
    if (!row.publishedAt) return null;
    try {
      const d = new Date(row.publishedAt);
      return Number.isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  }

  pageSerpPublishedAgeClass(row: PageSerpResultRow): string {
    const d = this.pageSerpPublishedDate(row);
    if (!d) return '';
    const ageMs = Date.now() - d.getTime();
    const sixMonths = 183 * 24 * 60 * 60 * 1000;
    const twoYears = 730 * 24 * 60 * 60 * 1000;
    if (ageMs < sixMonths) return 'url-serp-date-fresh';
    if (ageMs < twoYears) return 'url-serp-date-aging';
    return 'url-serp-date-stale';
  }

  serpCompetitionInsight(): SerpCompetitionInsight | null {
    if (!this.pageSerp?.results?.length) return null;
    const yourPosition = this.pageSerp.yourPosition;
    const results = [...this.pageSerp.results].sort((a, b) => a.position - b.position);

    const markedCompetitorsAbove: SerpCompetitionMarkedAbove[] = [];
    for (const row of results) {
      if (this.pageSerpResultCompetitorKind(row) !== 'marked') continue;
      if (row.position > 5) continue;
      const beatsYou = yourPosition === null || row.position < yourPosition;
      if (!beatsYou) continue;
      markedCompetitorsAbove.push({
        position: row.position,
        domain: row.domain,
        url: row.url,
        title: row.title
      });
    }

    const markedAboveYou = markedCompetitorsAbove.length > 0;
    const topLeaders: SerpCompetitionTopLeader[] = results
      .filter((row) => row.position >= 1 && row.position <= 3)
      .filter((row) => this.pageSerpResultCompetitorKind(row) !== 'you')
      .filter((row) => {
        const domain = (row.domain || '').trim().toLowerCase().replace(/^www\./, '');
        return domain && !isNonCompetitorDomain(domain);
      })
      .map((row) => ({
        position: row.position,
        domain: row.domain,
        url: row.url,
        title: row.title,
        descriptionPreview: this.serpCompetitionDescriptionPreview(row.description),
        publishedLabel: this.serpCompetitionPublishedLabel(row)
      }));

    return {
      yourPosition,
      markedAboveYou,
      markedCompetitorsAbove,
      topLeaders
    };
  }

  /**
   * Top 3 SERP slots in rank order (#1 → #3), including social/UGC.
   * Social rows are listed for visibility but should not offer Quick Compare.
   */
  serpCompetitionTopThreeRows(): SerpCompetitionDisplayRow[] {
    if (!this.pageSerp?.results?.length) return [];
    return [...this.pageSerp.results]
      .filter((row) => row.position >= 1 && row.position <= 3)
      .filter((row) => this.pageSerpResultCompetitorKind(row) !== 'you')
      .sort((a, b) => a.position - b.position)
      .map((row) => {
        const weakReason = weakSerpCompareReason(row.url, {
          title: row.title,
          description: row.description
        });
        return {
          position: row.position,
          domain: row.domain,
          url: row.url,
          title: row.title,
          descriptionPreview: this.serpCompetitionDescriptionPreview(row.description),
          publishedLabel: this.serpCompetitionPublishedLabel(row),
          isSocial: isNonCompetitorDomain(row.domain),
          isWeakCompareTarget: weakReason !== null,
          weakCompareBadge: weakReason ? weakSerpCompareBadgeLabel(weakReason) : null,
          isMarked: this.pageSerpResultCompetitorKind(row) === 'marked',
          intentLabel: this.pageSerpRowIntentLabel(row)
        };
      });
  }

  /** Marked rivals in top 5 not already shown in {@link serpCompetitionTopThreeRows}. */
  serpCompetitionMarkedRivalsExtra(insight: SerpCompetitionInsight): SerpCompetitionMarkedAbove[] {
    const topKeys = new Set(
      this.serpCompetitionTopThreeRows().map((row) => normalizeUrlForCompare(row.url))
    );
    return insight.markedCompetitorsAbove.filter(
      (row) => !topKeys.has(normalizeUrlForCompare(row.url))
    );
  }

  /**
   * Whether a SERP row offers Quick Compare.
   *
   * The two guards that remain are the ones that describe the TARGET: a social
   * result and a `isWeakCompareTarget` are pages a content comparison cannot
   * say anything useful about, so comparing them produces a confident-looking
   * answer about nothing.
   *
   * What was here and is gone is `insight.markedAboveYou || this.isDevView()`.
   * It came in with the marked-competitor supporting-pages pass, and the effect
   * was that the page ranking #1 for your own query offered no compare button
   * unless a *marked* rival also happened to be above you — so on the exact
   * page where you are not in the top 10 and want to know why, the winner was
   * the one row you could not open. Marking a competitor is a statement about
   * who you track over time; it is not a precondition for looking at whoever is
   * beating you today.
   */
  serpCompetitionShowQuickCompare(row: SerpCompetitionDisplayRow): boolean {
    if (row.isSocial || row.isWeakCompareTarget) return false;
    return Boolean(this.serpCompetitionInsight());
  }

  serpCompetitionYourRankLabel(): string {
    const pos = this.pageSerp?.yourPosition;
    if (pos === null || pos === undefined) return 'Not in top 10';
    return `#${pos}`;
  }

  serpCompetitionMarkedAboveLabel(): string {
    if (!this.markedCompetitorDomains.length) return '—';
    const insight = this.serpCompetitionInsight();
    if (!insight) return '—';
    return insight.markedAboveYou ? 'Yes' : 'No';
  }

  serpCompetitionChipLabel(): string {
    if (!this.markedCompetitorDomains.length) return 'NO MARKED RIVALS';
    const insight = this.serpCompetitionInsight();
    if (!insight) return '—';
    if (insight.markedAboveYou) return 'COMPETITORS AHEAD';
    if (insight.yourPosition === 1) return 'YOU LEAD';
    return 'NO RIVALS AHEAD';
  }

  serpCompetitionChipTone(): DiagnosisTone {
    if (!this.markedCompetitorDomains.length) return 'muted';
    const insight = this.serpCompetitionInsight();
    if (!insight) return 'muted';
    // A marked rival ranking above you is a loss that has already happened,
    // not a thing to look at: --danger, not --warning.
    if (insight.markedAboveYou) return 'bad';
    if (insight.yourPosition === 1) return 'ok';
    return 'neutral';
  }

  private serpCompetitionDescriptionPreview(description: string | undefined): string {
    const text = (description || '').replace(/\s+/g, ' ').trim();
    if (!text) return '';
    return text.length > 160 ? `${text.slice(0, 157)}…` : text;
  }

  private serpCompetitionPublishedLabel(row: PageSerpResultRow): string | null {
    const d = this.pageSerpPublishedDate(row);
    if (!d) return null;
    try {
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return null;
    }
  }

  refreshUrlSerpForDomain(): void {
    const pageUrl = this.url || '';
    if (!pageUrl || this.urlSerpRefreshRunning) return;

    this.urlSerpRefreshRunning = true;
    this.urlSerpRefreshProgress = {
      percent: 0,
      keywordsDone: 0,
      totalKeywords: 0,
      statusLine: this.urlSerpForceRefresh
        ? 'Starting URL SERP refresh (force API)…'
        : 'Starting URL SERP refresh (cache-first)…',
      etaLabel: null,
      chunksCompleted: 0,
      runningCostUsd: 0
    };

    void this.urlSerpSync
      .refreshPageUrl(pageUrl, {
        forceRefresh: this.urlSerpForceRefresh,
        onProgress: (p) => {
          this.urlSerpRefreshProgress = p;
        }
      })
      .then(() => {
        this.loadAnalysisData();
        if (this.urlSerpRefreshProgress) {
          this.urlSerpRefreshProgress = {
            ...this.urlSerpRefreshProgress,
            percent: 100,
            etaLabel: 'Done',
            statusLine: 'SERP refresh complete'
          };
        }
      })
      .catch((err) => {
        console.error('Failed to refresh URL SERP snapshots:', err);
      })
      .finally(() => {
        this.urlSerpRefreshRunning = false;
        setTimeout(() => {
          if (!this.urlSerpRefreshRunning) {
            this.urlSerpRefreshProgress = null;
          }
        }, 6000);
      });
  }

  pageHealthModelLabel(): string {
    if (!this.pageHealth) return '—';
    if (this.pageHealth.model === 'realization_gap') return 'Realization gap';
    if (this.pageHealth.model === 'unscored') return 'Unscored';
    if (this.pageHealth.model === 'eligibility') return 'Eligibility';
    return 'Unscored';
  }

  pageSecondaryHealthLabel(): string {
    if (!this.pageSecondaryHealth?.band) {
      if (this.pageSecondaryHealth?.model === 'freshness_hold') return 'Recently updated';
      return '—';
    }
    return pageHealthBandLabel(this.pageSecondaryHealth.band);
  }

  pageSecondaryHealthClass(): string {
    if (!this.pageSecondaryHealth?.band) {
      if (this.pageSecondaryHealth?.model === 'freshness_hold') return 'score-neutral';
      return '';
    }
    return pageHealthBandClass(this.pageSecondaryHealth.band);
  }

  pageSecondaryHealthModelLabel(): string {
    if (!this.pageSecondaryHealth) return '—';
    if (this.pageSecondaryHealth.model === 'earned_visibility') return 'Earned visibility';
    if (this.pageSecondaryHealth.model === 'freshness_hold') return 'Freshness hold';
    return 'None';
  }

  secondaryUnscoredSegmentLabel(): string {
    const segment = this.pageSecondaryHealth?.unscoredSegment;
    if (!segment) return '—';
    if (segment === 'no_claim') return 'No demand claim';
    if (segment === 'low_confidence_claim') return 'Low-confidence demand claim';
    if (segment === 'no_market_data') return 'Awaiting earned market data';
    if (segment === 'no_aligned_ceiling') return 'No claim-aligned demand ceiling';
    return 'Demand ceiling below material upside floor';
  }

  hasSecondaryHealthDiagnostics(): boolean {
    return !!this.pageSecondaryHealth;
  }

  realizationGapGateLabel(): string {
    return this.pageRealization?.gapGate || '—';
  }

  formatRealizationNumber(value: number | null | undefined): string {
    if (!Number.isFinite(value)) return '—';
    return Number(value).toFixed(1);
  }

  formatRealizationRatio(value: number | null | undefined): string {
    if (!Number.isFinite(value)) return '—';
    return Number(value).toFixed(2);
  }

  /**
   * The domain's site-wide issue codes, or null until they land.
   *
   * Null is not "none" — it is "we do not know yet", and the Technical evidence
   * row renders its pre-split wording while it holds. Collapsing the two would
   * make a page briefly claim every one of its issues is its own before the
   * answer arrives, which is the flicker equivalent of a wrong number.
   */
  private siteWideIssueCodes: Set<string> | null = null;

  /**
   * Domain-level explanations for issues that stay page-specific, by code.
   *
   * Empty until the rollup lands, which is the right default: an issue with no
   * shared cause reads exactly as it did before, and one whose cause we have
   * not fetched yet must not imply we looked and found none.
   */
  private sharedIssueCauses: Record<string, string> = {};

  /**
   * Load the domain's technical rollup, so this page can tell its own issues
   * from the template's.
   *
   * The service caches per domain, so the five package rows an operator opens
   * in a row share one request. A failure leaves `siteWideIssueCodes` null and
   * the panel degrades to the undivided list it showed before this existed.
   */
  private loadSiteWideIssueCodes(): void {
    const domain = this.planV2IssueDomain();
    if (!domain) return;
    this.technicalRollup.getRollup(domain).subscribe((rollup) => {
      this.siteWideIssueCodes = rollup ? new Set(rollup.siteWideCodes) : null;
      // Checks are announced from `ngDoCheck` once the page has settled, not
      // from here. The rollup is cached per domain, so from page two onward it
      // resolved before that page's analysis existed and registered an EMPTY
      // list — after which nothing re-registered, the card could not tell it
      // had been emptied, and unticking every finding left "Sending" ticked
      // over a work item naming no work.
      this.sharedIssueCauses = Object.fromEntries(
        (rollup?.sharedCauses ?? []).map((c) => [c.code, c.note])
      );
    });
  }

  /**
   * Every URL our site has, for the review card's new-page guard; null until it
   * lands, and the review card waits for it (`supportingPagesSettling`) rather
   * than list a page we already have and then take it back.
   */
  private ownSiteUrls: string[] | null = null;

  /**
   * Load the site's URLs once per domain (the service caches them). A failed
   * read or no domain degrades to an empty list: the card then keeps the two
   * guards that need no list, instead of waiting forever.
   */
  private loadOwnSiteUrls(): void {
    const domain = this.planV2IssueDomain();
    if (!domain) {
      this.ownSiteUrls = [];
      return;
    }
    this.ownSiteUrlsService.getUrls(domain).subscribe((urls) => {
      this.ownSiteUrls = urls ?? [];
    });
  }

  ngOnInit(): void {
    this.syncPlanV2EmbedHost();
    if (this.isEmbedMode()) {
      this.url = (this.embedUrl || '').trim() || null;
      // After `url`, always: embedded, the domain arrives as an @Input, but on
      // the standalone route `planV2IssueDomain()` derives it from `this.url`,
      // and asking before that is assigned resolves to no domain at all — which
      // is how the page-specific split silently worked in the Dashboard peek
      // and silently did not on the URL detail page.
      this.loadSiteWideIssueCodes();
      this.loadOwnSiteUrls();
      if (this.url) this.loadAnalysisData();
      return;
    }
    // Decode inventory route URL param (can be encoded multiple times).
    this.url = this.decodeUrlParam(this.route.snapshot.paramMap.get('url') || '');
    this.loadSiteWideIssueCodes();
    this.loadOwnSiteUrls();
    if (this.url) this.loadAnalysisData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['embedPreset'] || changes['embedUrl']) {
      this.syncPlanV2EmbedHost();
    }
    if (!this.isEmbedMode() || !changes['embedUrl'] || changes['embedUrl'].firstChange) return;
    const next = (changes['embedUrl'].currentValue || '').trim();
    if (next && next !== this.url) {
      // The settled flag is remembered per URL, so the memory has to be dropped
      // with the URL. Angular REUSES this component when the belt advances — it
      // is one `*ngIf` whose input changes, not a new instance — so without this
      // reset `lastEmittedSettled` stays `true` from the previous page. A page
      // that then loads without ever looking unsettled emits nothing at all, the
      // host never learns it is ready, and the spinner stays up forever.
      //
      // That is the "page 10 never loads" report: not slow, stuck.
      this.lastEmittedSettled = null;
      // Per-URL state, dropped with the URL — the component is REUSED as the
      // belt advances, so anything left set here describes the wrong page.
      this.markedSerpEval.cancel();
      this.url = next;
      this.loadAnalysisData();
    }
  }

  /**
   * Emits `embedSettledChange` on transitions only.
   *
   * `DoCheck` runs on every change-detection pass, so this compares against the
   * last value and emits nothing when nothing moved. The comparison is two
   * boolean reads; the alternative — a timer, or the host polling — is worse on
   * both accuracy and cost.
   */
  private lastEmittedSettled: boolean | null = null;

  ngDoCheck(): void {
    if (!this.embedReviewSurface) return;
    this.trackSupportingPagesPending();
    // Page signals feed the Internal links block, and they arrive on their own
    // request — left out of this, that block lands after the page has been
    // declared ready and shifts everything under it.
    const settled =
      this.analysisStatus === 'success' &&
      !this.supportingPagesSettling() &&
      !this.pageSignalsLoading;
    // Only once the data is final. Registering rows mid-load would hand the
    // send a list the operator never saw, and the columns genuinely do change
    // while the marked-competitor evaluation is still running.
    if (settled) {
      this.announceReviewCardItems();
      this.announceSiteWideCodes();
    }
    if (settled === this.lastEmittedSettled) return;
    this.lastEmittedSettled = settled;
    this.embedSettledChange.emit(settled);
  }

  /**
   * Only mention the wait if there is going to be one.
   *
   * With the evaluation cached server-side, a warm page settles in about
   * 200ms — so the placeholder appeared and vanished before it could be read,
   * on pages that were never going to show a card at all. A message that says
   * "checking" and then resolves to nothing is worse than silence: it draws
   * the eye to an absence.
   *
   * The threshold matches `--duration-slow` (324ms), which is the point this
   * design system already treats as "long enough that a person notices". Below
   * it, nothing renders and the card simply appears; above it, the wait is real
   * and worth explaining.
   */
  private static readonly PENDING_VISIBLE_AFTER_MS = 324;
  private supportingPagesPendingVisible = false;
  private supportingPagesPendingTimer: ReturnType<typeof setTimeout> | null = null;

  private trackSupportingPagesPending(): void {
    if (!this.supportingPagesSettling()) {
      this.clearSupportingPagesPendingTimer();
      this.supportingPagesPendingVisible = false;
      return;
    }
    if (this.supportingPagesPendingVisible || this.supportingPagesPendingTimer) return;
    this.supportingPagesPendingTimer = setTimeout(() => {
      this.supportingPagesPendingTimer = null;
      this.supportingPagesPendingVisible = true;
    }, UrlDetailComponent.PENDING_VISIBLE_AFTER_MS);
  }

  private clearSupportingPagesPendingTimer(): void {
    if (!this.supportingPagesPendingTimer) return;
    clearTimeout(this.supportingPagesPendingTimer);
    this.supportingPagesPendingTimer = null;
  }

  ngOnDestroy(): void {
    this.clearSupportingPagesPendingTimer();
    this.markedSerpEval.cancel();
    this.planV2EmbedContext.registerHost(null);
    this.signalsSubscription?.unsubscribe();
    this.competingPagesSubscription?.unsubscribe();
    this.nearMissSubscription?.unsubscribe();
    this.analysisSubscription?.unsubscribe();
    this.clearPageRefetchPollTimer();
  }

  refetchSinglePageAnalysis(): void {
    const pageUrl = (this.url || '').trim();
    if (!this.isDevView() || !pageUrl || this.pageRefetchRunning) return;

    this.pageRefetchRunning = true;
    this.pageRefetchStatus = 'Starting single-page re-fetch…';
    this.clearPageRefetchPollTimer();

    this.dataForSEOService.refetchSinglePageAnalysis(pageUrl).subscribe({
      next: (res) => {
        const taskId = String(res?.data?.taskId || '').trim();
        const status = String(res?.data?.status || '').trim();
        if (status === 'completed') {
          this.pageRefetchStatus = 'Single-page re-fetch completed. Reloading URL detail…';
          this.pageRefetchRunning = false;
          this.loadAnalysisData();
          return;
        }
        if (status === 'pending' && taskId) {
          this.pageRefetchStatus = 'Re-fetch task queued. Waiting for completion…';
          this.pollSinglePageRefetch(taskId, 1);
          return;
        }
        this.pageRefetchStatus = 'Re-fetch returned no task. Please retry.';
        this.pageRefetchRunning = false;
      },
      error: (error) => {
        this.pageRefetchStatus =
          error?.error?.message || error?.message || 'Failed to start single-page re-fetch.';
        this.pageRefetchRunning = false;
      }
    });
  }

  private pollSinglePageRefetch(taskId: string, attempt: number): void {
    const maxAttempts = 60; // 5 minutes @ 5s intervals
    this.dataForSEOService.getUrlAnalysisStatus(taskId).subscribe({
      next: (res) => {
        const status = String(res?.data?.status || '').trim();
        if (status === 'completed') {
          this.pageRefetchStatus = 'Single-page re-fetch completed. Reloading URL detail…';
          this.pageRefetchRunning = false;
          this.clearPageRefetchPollTimer();
          this.loadAnalysisData();
          return;
        }
        if (status === 'failed') {
          this.pageRefetchStatus = res?.data?.error || 'Single-page re-fetch failed.';
          this.pageRefetchRunning = false;
          this.clearPageRefetchPollTimer();
          return;
        }
        if (attempt >= maxAttempts) {
          this.pageRefetchStatus = 'Still running in background. You can refresh URL detail in a moment.';
          this.pageRefetchRunning = false;
          this.clearPageRefetchPollTimer();
          return;
        }
        this.pageRefetchStatus = `Re-fetch running… (${attempt}/${maxAttempts})`;
        this.pageRefetchPollTimer = setTimeout(() => this.pollSinglePageRefetch(taskId, attempt + 1), 5000);
      },
      error: (error) => {
        this.pageRefetchStatus =
          error?.error?.message || error?.message || 'Failed to poll single-page re-fetch status.';
        this.pageRefetchRunning = false;
        this.clearPageRefetchPollTimer();
      }
    });
  }

  private clearPageRefetchPollTimer(): void {
    if (!this.pageRefetchPollTimer) return;
    clearTimeout(this.pageRefetchPollTimer);
    this.pageRefetchPollTimer = null;
  }

  loadAnalysisData(): void {
    if (!this.url) return;
    
    this.clearPageRefetchPollTimer();
    this.analysisStatus = 'loading';
    this.pageUrlId = '';
    // Cleared with the rest: a compare target left over from the previous URL
    // would name someone else's rival under this page's heading.
    this.pageCompare = null;
    this.pageEstimatePair = null;
    this.planClusters = [];
    this.pageSignals = [];
    this.pageSignalsLoading = false;
    this.internalLinkSignalRows = [];
    this.unclusteredSignalRows = [];
    this.competingPages = null;
    this.competingPagesSubscription?.unsubscribe();
    this.nearMissSearches = null;
    this.nearMissSubscription?.unsubscribe();
    this.signalsSubscription?.unsubscribe();
    this.pageTopicsExpanded = false;
    this.topicOwnershipStatus = 'idle';
    this.topicOwnershipDomains = [];
    this.topicOwnershipPagesExpandedByDomain.clear();
    this.topicOwnershipUserExpanded = false;
    this.technicalIssueDetailsExpanded.clear();
    const gen = ++this.analysisLoadGen;
    this.analysisSubscription?.unsubscribe();
    this.analysisSubscription = this.dataForSEOService.getUrlAnalysis(this.url).subscribe({
      next: (response) => {
        if (gen !== this.analysisLoadGen) return;
        if (response.success) {
          this.pageUrlId = (response.data.urlId || '').trim();
          this.planClusters = Array.isArray(response.data.planClusters)
            ? response.data.planClusters.filter((c) => typeof c === 'string' && c.trim())
            : [];
          this.analysisData = response.data.analysis;
          this.pageDemand = response.data.pageDemand || null;
          this.pageRealization = response.data.pageRealization || null;
          this.pageHealth = response.data.pageHealth || null;
          this.pageSecondaryHealth = response.data.pageSecondaryHealth || null;
          this.pageSerp = response.data.pageSerp || null;
          this.pageCompare = response.data.pageCompare || null;
          this.pageEstimatePair = response.data.pageEstimatePair || null;
          this.ensureMarkedSerpCompetitorEvals();
          this.pageIntentDiagnosis = response.data.pageIntentDiagnosis || null;
          this.collapseWinnerExcerpts();
          this.analysisStatus = 'success';
          this.loadPageSignals();
          this.loadCompetingPages();
          this.loadNearMissSearches();
          this.loadPlanV2NextAction();
          if (this.analysisData) {
            this.extractContentElements();
            this.extractLinkData();
            this.loadCompetitorTopicCoverage();
            this.loadTopicOwnership();
          }
        } else {
          this.analysisStatus = 'error';
        }
      },
      error: (error) => {
        if (gen !== this.analysisLoadGen) return;
        console.error('Failed to load analysis:', error);
        this.analysisStatus = 'error';
      }
    });
  }

  /** Two states that look alike in one line, so they get separate sentences. */
  planV2PeriodHint(): string {
    return this.planV2Period?.hasPair
      ? 'The crawl pair this action is read against — the same period the dashboard and Issues show.'
      : 'Only one crawl has been captured for this domain, so there is no previous crawl to compare against yet.';
  }

  private loadPlanV2NextAction(): void {
    if (this.isPlanV2Embed()) {
      this.planV2NextAction = null;
      this.planV2NextActionError = null;
      this.planV2Period = null;
      return;
    }
    if (!this.isActionPlanV2Enabled()) {
      this.planV2NextAction = null;
      this.planV2NextActionError = null;
      this.planV2Period = null;
      return;
    }
    const domain = this.extractDomainFromUrl(this.url || '');
    const urlId = this.pageUrlId.trim();
    if (!domain || !urlId) {
      this.planV2NextAction = null;
      this.planV2Period = null;
      return;
    }
    this.crawlRunPeriod.viewFor(domain, { refresh: true }).subscribe((view) => {
      // Ignore a late answer for a URL the operator has already navigated away from.
      if (this.extractDomainFromUrl(this.url || '') !== domain) return;
      this.planV2Period = view;
    });
    this.planV2NextActionLoading = true;
    this.planV2NextActionError = null;
    this.inventoryPlanClusters.getActionPlanV2(domain, { urlId, pageSize: 1 }).subscribe({
      next: (res) => {
        this.planV2NextAction = res?.data?.rows?.[0] ?? null;
        this.planV2NextActionLoading = false;
      },
      error: (err) => {
        this.planV2NextAction = null;
        this.planV2NextActionLoading = false;
        this.planV2NextActionError = err?.error?.message || 'Could not load next action.';
      }
    });
  }

  private loadPageSignals(): void {
    const domain = this.extractDomainFromUrl(this.url || '');
    const urlId = this.pageUrlId.trim();
    if (!domain || !urlId) {
      this.pageSignals = buildPageSignalsForUrl(urlId, this.planClusters, [], []);
      this.pageSignalsLoading = false;
      return;
    }

    this.pageSignalsLoading = true;
    this.signalsSubscription?.unsubscribe();
    this.signalsSubscription = forkJoin({
      internalLinks: this.inventoryPlanClusters
        .getInternalLinkActionPlan(domain, {
          urlId,
          limit: 500,
          includeCompleted: false,
          includeDismissed: false
        })
        .pipe(catchError(() => of(null))),
      unclustered: this.inventoryPlanClusters
        .getUnclusteredPageActionPlan(domain, {
          limit: 200,
          includeCompleted: false,
          includeDismissed: false
        })
        .pipe(catchError(() => of(null)))
    }).subscribe({
      next: (res) => {
        this.internalLinkSignalRows = Array.isArray(res.internalLinks?.data?.rows)
          ? res.internalLinks.data.rows
          : [];
        this.unclusteredSignalRows = Array.isArray(res.unclustered?.data?.rows) ? res.unclustered.data.rows : [];
        this.pageSignals = buildPageSignalsForUrl(
          urlId,
          this.planClusters,
          this.internalLinkSignalRows.map((row) => ({
            status: row.status,
            sourceUrlId: row.sourceUrlId,
            targetUrlId: row.targetUrlId
          })),
          this.unclusteredSignalRows.map((row) => ({
            status: row.status,
            urlId: row.urlId
          }))
        );
        this.pageSignalsLoading = false;
      },
      error: () => {
        this.pageSignals = buildPageSignalsForUrl(urlId, this.planClusters, [], []);
        this.pageSignalsLoading = false;
      }
    });
  }

  /**
   * The domain's competing pages, for the Cannibalization card: review surface only, once per
   * domain (the service caches it). Mirrored by `review-page-sources.ts`.
   */
  private loadCompetingPages(): void {
    const domain = this.extractDomainFromUrl(this.url || '');
    const urlId = this.pageUrlId.trim();
    if (!this.embedReviewSurface || !domain || !urlId) return;
    this.competingPagesSubscription?.unsubscribe();
    this.competingPagesSubscription = this.competingPagesService.getForDomain(domain).subscribe((payload) => {
      this.competingPages = payload;
    });
  }

  /**
   * The page's near-miss searches, for "You're close on these searches": review surface only, after
   * the competing pages. Mirrored by `review-page-sources.ts`.
   */
  private loadNearMissSearches(): void {
    const domain = this.extractDomainFromUrl(this.url || '');
    const urlId = this.pageUrlId.trim();
    if (!this.embedReviewSurface || !domain || !urlId) return;
    this.nearMissSubscription?.unsubscribe();
    this.nearMissSubscription = this.nearMissSearchesService.getForPage(domain, urlId).subscribe((result) => {
      this.nearMissSearches = result;
    });
  }

  readonly nearMissCardTitle = NEAR_MISS_CARD_TITLE;

  reviewNearMissCard(): NearMissCardView | null {
    return this.reviewModel.reviewNearMissCard();
  }

  /** This page's estimate beside the rival page's, or null (`estimate-beside-rival.ts`). */
  reviewEstimateBesideRival(): EstimateBesideRivalView | null {
    return this.reviewModel.reviewEstimateBesideRival();
  }

  showEmbedNearMissSection(): boolean {
    return this.reviewModel.showEmbedNearMissSection();
  }

  nearMissNumbers(s: NearMissItemSearch, card: NearMissCardView): string {
    return nearMissNumbersLabel(s, card.estimated);
  }

  buildNearMissIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    const card = this.reviewNearMissCard();
    if (!urlId || !url || !card) return null;
    return {
      issueKey: buildPlanV2IssueKey(urlId, 'near_miss_searches'),
      urlId,
      url,
      issueType: 'near_miss_searches',
      cardTitle: NEAR_MISS_CARD_TITLE,
      changeSummary: card.items.map((i) => i.change).join(' '),
      why: card.items.map((i) => i.why).join(' '),
      diagnosisLabel: card.items.map((i) => i.label).join(' · '),
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
  }

  reviewCannibalizationPairs(): CannibalizationPairView[] {
    return this.reviewModel.reviewCannibalizationPairs();
  }

  showEmbedCannibalizationSection(): boolean {
    return this.reviewModel.showEmbedCannibalizationSection();
  }

  reviewPageSignals(): InventoryPageSignal[] {
    return this.reviewModel.reviewPageSignals();
  }

  buildCannibalizationIssuePayload(): PlanV2IssueCardPayload | null {
    const urlId = (this.pageUrlId || '').trim();
    const url = (this.url || '').trim();
    const pairs = this.reviewCannibalizationPairs();
    if (!urlId || !url || !pairs.length) return null;
    return {
      issueKey: buildPlanV2IssueKey(urlId, 'cannibalization'),
      urlId,
      url,
      issueType: 'cannibalization',
      cardTitle: 'Cannibalization',
      changeSummary: pairs.map((p) => p.action).join(' '),
      why: pairs.map((p) => `${p.verdictLabel} with ${p.otherPath}: ${p.why}`).join(' '),
      diagnosisLabel: pairs.map((p) => p.verdictLabel).join(' · '),
      priorityScore: this.embedPlanV2Row?.priorityScore ?? null
    };
  }

  pageSignalChipClass(kind: InventoryPageSignalKind): string {
    return `page-signal-chip--${kind}`;
  }

  hasPageSignalKind(kind: InventoryPageSignalKind): boolean {
    return this.reviewModel.hasPageSignalKind(kind);
  }


  pageSignalInternalLinkDetails(): ActionPlanInternalLinkRow[] {
    return filterInternalLinksIncomingToPage(this.internalLinkSignalRows, this.pageUrlId.trim());
  }

  pageSignalInternalLinkDetailsPendingTriage(): ActionPlanInternalLinkRow[] {
    return this.reviewModel.pageSignalInternalLinkDetailsPendingTriage();
  }

  pageSignalInternalLinkDetailsForDisplay(): ActionPlanInternalLinkRow[] {
    if (this.isPlanV2Embed()) {
      const exiting =
        this.planV2IssueQueue.isCardExiting(this.planV2IssueKey('internal_link'), 'work_item') ||
        this.planV2IssueQueue.isCardExiting(this.planV2IssueKey('internal_link'), 'dismissed');
      return exiting
        ? this.pageSignalInternalLinkDetails()
        : this.pageSignalInternalLinkDetailsPendingTriage();
    }
    return this.pageSignalInternalLinkDetails();
  }

  internalLinkRowDisplay(row: ActionPlanInternalLinkRow) {
    return buildInternalLinkRowDisplay(row, this.pageUrlId.trim());
  }

  internalLinkDirectionChip(row: ActionPlanInternalLinkRow): string {
    return internalLinkDirectionLabel(this.internalLinkRowDisplay(row).direction);
  }

  internalLinkDirectionChipClasses(row: ActionPlanInternalLinkRow): string {
    return internalLinkDirectionChipClass(this.internalLinkRowDisplay(row).direction);
  }

  internalLinkPlacementParts(row: ActionPlanInternalLinkRow) {
    const sentence = (row.where?.sentence || '').trim();
    const anchor = (row.suggestedAnchor || '').trim();
    return splitPlacementByAnchor(sentence, anchor);
  }

  formatInternalLinkScore(score: number): string {
    return formatInternalLinkRelevanceScore(score);
  }

  onInternalLinksComposingChange(composing: boolean): void {
    this.internalLinksComposing = composing;
    if (!composing) return;
    // List collapses when compose opens; keep the section anchored in the drawer viewport.
    setTimeout(() => this.scrollInternalLinksSectionIntoView(), 0);
  }

  private scrollInternalLinksSectionIntoView(): void {
    const el = this.internalLinksSectionRef?.nativeElement;
    if (!el) return;
    el.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  pageSignalUnclusteredDetail(): ActionPlanUnclusteredPageRow | null {
    return this.reviewModel.pageSignalUnclusteredDetail();
  }


  unclusteredRecommendationLabel(rec: ActionPlanUnclusteredPageRow['recommendation']): string {
    return this.reviewModel.unclusteredRecommendationLabel(rec);
  }

  unclusteredEvidenceSentence(row: ActionPlanUnclusteredPageRow): string | null {
    return this.reviewModel.unclusteredEvidenceSentence(row);
  }

  unclusteredActionLabel(row: ActionPlanUnclusteredPageRow): string {
    return this.reviewModel.unclusteredActionLabel(row);
  }

  unclusteredClusterFitSentence(row: ActionPlanUnclusteredPageRow): string | null {
    return this.reviewModel.unclusteredClusterFitSentence(row);
  }

  inventoryDetailLink(targetUrl: string): string[] {
    const trimmed = (targetUrl || '').trim();
    return trimmed ? ['/inventory', encodeURIComponent(trimmed)] : ['/inventory'];
  }

  openInventoryForSignals(): void {
    const domain = this.extractDomainFromUrl(this.url || '');
    if (domain) this.domainContext.setSelectedDomain(domain);
  }

  /**
   * Extract key content elements from structured content
   */
  extractContentElements(): void {
    const structuredContent = this.getStructuredContent();
    if (!structuredContent) return;

    this.contentElements = [];
    
    // Process main_topic content
    if (structuredContent.main_topic && Array.isArray(structuredContent.main_topic)) {
      structuredContent.main_topic.forEach((topic: any) => {
        // Add the main heading
        if (topic.h_title) {
          this.contentElements.push({
            type: 'heading',
            level: topic.level || 1,
            content: topic.h_title
          });
        }
        
        // Add primary content
        if (topic.primary_content && Array.isArray(topic.primary_content)) {
          topic.primary_content.forEach((content: any) => {
            if (content.text && content.text.trim()) {
              this.contentElements.push({
                type: 'text',
                content: content.text.trim()
              });
            }
            
            // Add links from the content
            if (content.urls && Array.isArray(content.urls)) {
              content.urls.forEach((url: any) => {
                if (url.anchor_text && url.url) {
                  this.contentElements.push({
                    type: 'link',
                    content: url.anchor_text,
                    href: url.url
                  });
                }
              });
            }
          });
        }
        

      });
    }
    
    // Process header content
    if (structuredContent.header) {
      // Add primary header content
      if (structuredContent.header.primary_content && Array.isArray(structuredContent.header.primary_content)) {
        structuredContent.header.primary_content.forEach((content: any) => {
          if (content.text && content.text.trim()) {
            this.contentElements.push({
              type: 'heading',
              level: 1,
              content: content.text.trim()
            });
          }
          
          // Add links from header
          if (content.urls && Array.isArray(content.urls)) {
            content.urls.forEach((url: any) => {
              if (url.anchor_text && url.url) {
                this.contentElements.push({
                  type: 'link',
                  content: url.anchor_text,
                  href: url.url
                });
              }
            });
          }
        });
      }
      

    }
    
    // Process footer content
    if (structuredContent.footer) {
      // Add primary footer content only
      if (structuredContent.footer.primary_content && Array.isArray(structuredContent.footer.primary_content)) {
        structuredContent.footer.primary_content.forEach((content: any) => {
          if (content.text && content.text.trim()) {
            this.contentElements.push({
              type: 'text',
              content: content.text.trim()
            });
          }
          
          // Add links from footer
          if (content.urls && Array.isArray(content.urls)) {
            content.urls.forEach((url: any) => {
              if (url.anchor_text && url.url) {
                this.contentElements.push({
                  type: 'link',
                  content: url.anchor_text,
                  href: url.url
                });
              }
            });
          }
        });
      }
    }
    
    // Process table content
    if (structuredContent.table_content && Array.isArray(structuredContent.table_content)) {
      structuredContent.table_content.forEach((table: any) => {
        if (table.body && Array.isArray(table.body)) {
          table.body.forEach((row: any, rowIndex: number) => {
            if (row.row_cells && Array.isArray(row.row_cells)) {
              // Create a table element with the row data
              this.contentElements.push({
                type: 'table',
                content: `Table Row ${rowIndex + 1}`,
                tableData: row.row_cells.map((cell: any) => ({
                  text: cell.text || '',
                  isHeader: cell.is_header || false,
                  urls: cell.urls || null
                }))
              });
            }
          });
        }
      });
    }
  }

  getScoreClass(score: number): string {
    if (score >= 90) return 'excellent';
    if (score >= 70) return 'good';
    if (score >= 50) return 'fair';
    return 'poor';
  }

  /** The same four bands as `getScoreClass()`, as a `.badge` tone. Kept
   *  separate rather than folded in: `getScoreClass()` also colours plain
   *  text (the OnPage score, the chip percentage), and a text colour and a
   *  badge tone are not the same decision even when they agree. */
  scoreBadgeClass(score: number): string {
    if (score >= 70) return 'badge--success';
    if (score >= 50) return 'badge--warning';
    return 'badge--danger';
  }

  technicalOnPageScoreLabel(): string {
    const score = this.analysisData?.onpageScore;
    if (score == null || !Number.isFinite(Number(score))) return '—';
    const n = Number(score);
    return Number.isInteger(n) ? String(n) : n.toFixed(1);
  }

  technicalOnPageIssuesLabel(): string | null {
    const { critical, warnings } = buildOnpageTechnicalIssueViews(this.analysisData);
    if (critical.length === 0 && warnings.length === 0) return null;
    const parts: string[] = [];
    if (critical.length > 0) parts.push(`${critical.length} critical`);
    if (warnings.length > 0) parts.push(`${warnings.length} warning${warnings.length === 1 ? '' : 's'}`);
    return parts.join(' · ');
  }

  technicalCriticalIssueViews(): OnpageIssueView[] {
    return this.reviewModel.technicalCriticalIssueViews();
  }

  technicalWarningIssueViews(): OnpageIssueView[] {
    return this.reviewModel.technicalWarningIssueViews();
  }

  hasTechnicalCriticalIssues(): boolean {
    return this.reviewModel.hasTechnicalCriticalIssues();
  }

  hasTechnicalWarnings(): boolean {
    return this.reviewModel.hasTechnicalWarnings();
  }

  isTechnicalIssueDetailsExpanded(kind: 'critical' | 'warning', index: number): boolean {
    return this.technicalIssueDetailsExpanded.has(`${kind}:${index}`);
  }

  toggleTechnicalIssueDetails(kind: 'critical' | 'warning', index: number): void {
    const key = `${kind}:${index}`;
    if (this.technicalIssueDetailsExpanded.has(key)) {
      this.technicalIssueDetailsExpanded.delete(key);
    } else {
      this.technicalIssueDetailsExpanded.add(key);
    }
  }

  formatTechnicalPerfMs(value: unknown): string {
    if (value == null || !Number.isFinite(Number(value))) return '—';
    return `${Math.round(Number(value))} ms`;
  }

  toggleContentSection(): void {
    this.contentSectionCollapsed = !this.contentSectionCollapsed;
  }

  toggleLinksSection(): void {
    this.linksSectionCollapsed = !this.linksSectionCollapsed;
  }

  /**
   * Extract internal links data from analysis
   */
  extractLinkData(): void {
    if (!this.analysisData?.links?.internalLinks) {
      this.internalLinks = [];
      return;
    }

    this.internalLinks = this.analysisData.links.internalLinks || [];
  }

  /**
   * Get filtered and sorted links
   */
  getFilteredLinks(): any[] {
    let filtered = this.internalLinks;

    // Apply filter
    if (this.linkFilter !== 'all') {
      filtered = filtered.filter(link => link.linkType === this.linkFilter);
    }

    // Apply sort
    filtered = [...filtered].sort((a, b) => {
      switch (this.linkSort) {
        case 'type':
          const typeOrder = { 'structural': 1, 'contextual': 2, 'same_page': 3, 'unknown': 4 };
          return (typeOrder[a.linkType as keyof typeof typeOrder] || 99) - 
                 (typeOrder[b.linkType as keyof typeof typeOrder] || 99);
        case 'confidence':
          return (b.confidence || 0) - (a.confidence || 0);
        case 'anchor':
          return (a.anchorText || '').localeCompare(b.anchorText || '');
        case 'url':
          return (a.targetRelativeUrl || '').localeCompare(b.targetRelativeUrl || '');
        default:
          return 0;
      }
    });

    return filtered;
  }

  /**
   * Get link statistics
   */
  getLinkStats(): { total: number; structural: number; contextual: number; samePage: number; unknown: number } {
    return {
      total: this.internalLinks.length,
      structural: this.internalLinks.filter(l => l.linkType === 'structural').length,
      contextual: this.internalLinks.filter(l => l.linkType === 'contextual').length,
      samePage: this.internalLinks.filter(l => l.linkType === 'same_page').length,
      unknown: this.internalLinks.filter(l => l.linkType === 'unknown' || !l.linkType).length
    };
  }

  /**
   * Get link type badge class
   */
  getLinkTypeClass(linkType: string): string {
    switch (linkType) {
      case 'structural': return 'badge--accent';
      case 'contextual': return 'badge--success';
      case 'same_page': return '';
      default: return 'badge--muted';
    }
  }

  /**
   * Get link type label
   */
  getLinkTypeLabel(linkType: string): string {
    switch (linkType) {
      case 'structural': return 'Structural';
      case 'contextual': return 'Contextual';
      case 'same_page': return 'Same Page';
      default: return 'Unknown';
    }
  }

  /**
   * Get confidence color class
   */
  getConfidenceClass(confidence: number): string {
    if (confidence >= 0.8) return 'confidence-high';
    if (confidence >= 0.6) return 'confidence-medium';
    return 'confidence-low';
  }

  /**
   * Check if links are available
   */
  hasLinks(): boolean {
    return this.internalLinks.length > 0;
  }

  /**
   * Toggle link details expansion
   */
  toggleLinkDetails(link: any): void {
    link._expanded = !link._expanded;
  }

  /**
   * Get the structured content from the analysis data
   */
  getStructuredContent(): any {
    if (!this.analysisData?.rawData?.structuredContent) {
      return null;
    }
    return this.analysisData.rawData.structuredContent;
  }

  // TODO: DataForSEO markdown_view → page_as_markdown (see CONTENT_INVENTORY_TOPIC_COMPARE_UI.md)
  // This would provide clean markdown content instead of structured content
  // Consider implementing this in the future for better content display

  /**
   * Get heading level class for styling
   */
  getHeadingClass(level: number): string {
    return `heading-level-${level}`;
  }

  /**
   * Check if content has any elements
   */
  hasContent(): boolean {
    return this.contentElements.length > 0;
  }

  /**
   * TrackBy function for ngFor performance
   */
  trackByElement(index: number, element: ContentElement): string {
    return `${element.type}-${index}-${element.content.substring(0, 20)}`;
  }

  /**
   * TrackBy function for links
   */
  trackByLink(index: number, link: any): string {
    return `${link.targetRelativeUrl}-${link.anchorText}-${index}`;
  }

  hasTopicSignals(): boolean {
    const raw = this.analysisData?.topic?.topicSignals;
    return Array.isArray(raw) && raw.length > 0;
  }

  /**
   * Topic signals for this page, strongest first (same ordering as useful for scanning).
   */
  private static readonly TOP_PAGE_TOPIC_SIGNALS = 5;

  getPageTopicSignals(): PageTopicSignal[] {
    const raw = this.analysisData?.topic?.topicSignals;
    if (!Array.isArray(raw)) return [];
    const mapped = raw
      .filter((s: any) => s && typeof s.topicSlug === 'string')
      .map((s: any) => ({
        topicSlug: s.topicSlug,
        label: typeof s.label === 'string' && s.label.trim() ? s.label : s.topicSlug,
        weight: Number.isFinite(s.weight) ? s.weight : 0,
        salience: Number.isFinite(s.salience) ? s.salience : 0,
        relativeDensity: Number.isFinite(s.relativeDensity) ? s.relativeDensity : 0
      }));
    return mapped.sort((a, b) => b.weight - a.weight);
  }

  getTopPageTopicSignals(): PageTopicSignal[] {
    return this.getPageTopicSignals().slice(0, UrlDetailComponent.TOP_PAGE_TOPIC_SIGNALS);
  }

  hasMorePageTopicsThanTop(): boolean {
    return this.getPageTopicSignals().length > UrlDetailComponent.TOP_PAGE_TOPIC_SIGNALS;
  }

  togglePageTopicsExpanded(): void {
    this.pageTopicsExpanded = !this.pageTopicsExpanded;
  }

  pageTopicsExpandControlLabel(): string {
    if (this.pageTopicsExpanded) return 'Show top topics only';
    const total = this.getPageTopicSignals().length;
    const rest = total - UrlDetailComponent.TOP_PAGE_TOPIC_SIGNALS;
    return rest > 0 ? `Show more topics (${rest} more)` : 'Show all topics';
  }

  /**
   * Same blend as content inventory / domain topic intent: 0.7·weight + 0.2·salience + 0.1·relativeDensity (clamped 0..1).
   */
  pageTopicRelevance01(signal: PageTopicSignal): number {
    const v =
      0.7 * (signal.weight ?? 0) +
      0.2 * (signal.salience ?? 0) +
      0.1 * (signal.relativeDensity ?? 0);
    return Math.min(1, Math.max(0, v));
  }

  formatTopicMetric(n: number): string {
    return Number.isFinite(n) ? n.toFixed(3) : '—';
  }

  formatPageTopicRelevancePct(signal: PageTopicSignal): string {
    return `${Math.round(this.pageTopicRelevance01(signal) * 100)}%`;
  }

  pageTopicRelevanceScore0to100(signal: PageTopicSignal): number {
    return this.pageTopicRelevance01(signal) * 100;
  }

  trackByTopicSignal(index: number, signal: PageTopicSignal): string {
    return signal.topicSlug + '-' + index;
  }

  /** Placeholder columns/lines for supporting-pages skeleton */
  readonly topicOwnershipSkeletonCols = [0, 1, 2];
  readonly topicOwnershipSkeletonLines = [0, 1, 2, 3, 4];

  loadTopicOwnership(): void {
    const gen = ++this.topicOwnershipLoadGen;
    this.topicOwnershipCompareUrl = '';
    this.resetTopicOwnershipV2Test();
    this.markedSerpCompetitorV2Rows = [];
    this.markedSerpEval.cancel();
    this.supportingPageLexicalByUrl = new Map();
    this.topicOwnershipCacheMiss = false;
    this.topicOwnershipSnapshotGeneratedAt = null;
    const pageUrl = (this.url || '').trim();
    const yourDomain = this.extractDomainFromUrl(pageUrl);
    const yourDomainKey = yourDomain ? yourDomain.toLowerCase().replace(/^www\./, '') : '';
    if (!yourDomainKey || !this.analysisData) {
      this.topicOwnershipStatus = 'ready';
      this.topicOwnershipDomains = [];
      return;
    }

    const pageTopics = getTopTopicsForSupportingPages(this.analysisData, { limit: 3 });
    if (pageTopics.length === 0) {
      this.topicOwnershipStatus = 'ready';
      this.topicOwnershipDomains = [];
      this.focalAnchorTopicSlugs = new Set();
      this.focalAnchorTopicLabels = [];
      return;
    }

    const anchor = resolveFocalAnchorTopics(
      this.analysisData,
      pageUrl
    );
    this.focalAnchorTopicSlugs = anchor.slugs;
    this.focalAnchorTopicLabels = anchor.labels;

    this.topicOwnershipStatus = 'loading';
    this.topicOwnershipDomains = [];
    this.topicOwnershipPagesExpandedByDomain.clear();
    this.topicOwnershipUserExpanded = false;

    this.loadSerpRivalCoverage(pageUrl);

    this.dataForSEOService.getSupportingPagesByTopicSnapshot(pageUrl).subscribe({
      next: (res) => {
        if (gen !== this.topicOwnershipLoadGen) return;
        if (res.cached && res.data) {
          this.applySupportingPagesSnapshot(res.data);
          this.topicOwnershipStatus = 'ready';
          this.loadTopicOwnershipV2FromCache();
          return;
        }
        this.topicOwnershipDomains = [];
        this.topicOwnershipCacheMiss = true;
        this.topicOwnershipStatus = 'ready';
        this.topicOwnershipV2Status = 'ready';
        this.ensureMarkedSerpCompetitorEvals();
      },
      error: () => {
        if (gen !== this.topicOwnershipLoadGen) return;
        this.topicOwnershipStatus = 'error';
        this.topicOwnershipDomains = [];
        this.topicOwnershipCacheMiss = true;
      }
    });
  }

  private applySupportingPagesSnapshot(data: SupportingPagesByTopicSnapshotData): void {
    this.focalAnchorTopicSlugs = new Set(data.focalAnchorTopicSlugs || []);
    this.focalAnchorTopicLabels = data.focalAnchorTopicLabels || [];
    this.topicOwnershipSnapshotGeneratedAt = data.generatedAt || null;
    this.topicOwnershipDomains = topicOwnershipDomainsFromSnapshot(data, this.url || '');
    this.topicOwnershipCacheMiss = false;
  }

  private loadTopicOwnershipV2FromCache(): void {
    const focalUrl = (this.url || '').trim();
    if (!focalUrl || this.topicOwnershipStatus !== 'ready') return;

    const gen = ++this.topicOwnershipV2LoadGen;
    this.topicOwnershipV2Status = 'loading';
    this.topicOwnershipV2Error = null;
    this.topicOwnershipV2Data = null;

    this.dataForSEOService.getSupportingPagesOpportunitySnapshot(focalUrl).subscribe({
      next: (res) => {
        if (gen !== this.topicOwnershipV2LoadGen) return;
        if (res.cached && res.data) {
          this.topicOwnershipV2Data = res.data;
          this.topicOwnershipV2Status = 'ready';
          this.ensureMarkedSerpCompetitorEvals();
          return;
        }
        this.topicOwnershipV2Status = 'ready';
        this.ensureMarkedSerpCompetitorEvals();
      },
      error: (err) => {
        if (gen !== this.topicOwnershipV2LoadGen) return;
        this.topicOwnershipV2Status = 'error';
        this.topicOwnershipV2Error =
          err?.error?.message || err?.message || 'Supporting pages opportunity snapshot failed';
      }
    });
  }

  /**
   * Load the precomputed "Top SERP rival" section coverage for this focal URL.
   * Independent of the marked-competitor supporting-pages snapshot, so it's
   * available even when no v1 topic snapshot exists (built via the Setup step).
   */
  private loadSerpRivalCoverage(pageUrl: string): void {
    this.serpRivalCoverage = null;
    this.dataForSEOService.getSerpRivalCoverage(pageUrl).subscribe({
      next: (res) => {
        this.serpRivalCoverage = res.cached && res.data ? res.data : null;
      },
      error: () => {
        this.serpRivalCoverage = null;
      }
    });
  }

  topEditorialSerpRival(): { url: string; domain: string } | null {
    return this.reviewModel.topEditorialSerpRival();
  }

  activeSerpRivalCoverage(): SerpRivalCoverageData | null {
    return this.reviewModel.activeSerpRivalCoverage();
  }

  /**
   * Whether the Top SERP rival has sections this page is missing.
   *
   * Counted from the eval row, not from the stored `missingSectionCount`. This
   * gates a step in the expand playbook — "go and look at the section gaps" —
   * and the stored number counts groups before the filters, so on a listicle it
   * would send an operator to a panel that lists nothing.
   */
  hasActiveSerpRivalSectionGaps(): boolean {
    return hasSectionGapsForV2Row(this.serpRivalEvalRow());
  }

  serpRivalEvalRow(): SupportingPagesV2RowResult | null {
    return this.reviewModel.serpRivalEvalRow();
  }

  private mergedV2Rows(): SupportingPagesV2RowResult[] {
    return this.reviewModel.mergedV2Rows();
  }

  /** The Top SERP rival coverage for a given SERP result URL, if it matches. */
  /** The comparison with Google's results, held back on this page (§0ar). The rule is in the model. */
  reviewComparisonWithheld(): SerpSubjectWithheldNotice | null {
    return this.reviewModel.reviewComparisonWithheld();
  }

  reviewSectionCompareNotice(): SectionCompareNotice | null {
    if (!this.embedReviewSurface) return null;
    return this.reviewModel.reviewSectionCompareNotice(Boolean(this.buildSectionGapIssuePayload()));
  }

  serpRivalCoverageForUrl(url: string): SerpRivalCoverageData | null {
    const cov = this.activeSerpRivalCoverage();
    if (!cov) return null;
    return normalizeUrlForCompare(cov.rivalUrl) === normalizeUrlForCompare(url) ? cov : null;
  }

  isTopSerpRival(url: string): boolean {
    const rival = this.topEditorialSerpRival();
    if (!rival) return false;
    return normalizeUrlForCompare(rival.url) === normalizeUrlForCompare(url);
  }

  /** Chip label for a rival row with section gaps (Top SERP snapshot or marked-competitor v2 eval). */
  serpRivalMissingSectionsLabel(url: string): string | null {
    const n = this.serpRivalMissingSectionCount(url);
    if (n == null || n <= 0) return null;
    return n === 1 ? '1 missing section' : `${n} missing sections`;
  }

  serpRivalRankDrivenGapsLabel(url: string): string | null {
    if (!this.isTopSerpRival(url)) return null;
    const count = this.topSerpRivalRankDrivenGaps().length;
    if (count <= 0) return null;
    return count === 1 ? '1 rank-driven gap' : `${count} rank-driven gaps`;
  }

  toggleTopSerpRivalRankDrivenPanel(): void {
    this.topSerpRivalRankDrivenOpen = !this.topSerpRivalRankDrivenOpen;
  }

  /**
   * Sections this page is missing versus one rival URL.
   *
   * Both branches count the eval row rather than reading the stored
   * `missingSectionCount`. This number is the amber "N missing sections — see
   * what they cover" button, and it sat on the same screen as the Missing
   * sections card, which has always counted the filtered set — so on the ~7% of
   * pages where the two disagree the page showed two different numbers for one
   * finding, and the button promised a list longer than the one behind it.
   */
  private serpRivalMissingSectionCount(url: string): number | null {
    const cov = this.serpRivalCoverageForUrl(url);
    if (cov?.status === 'ready') return sectionGapsForV2Row(cov.evalRow).length;

    const v2 = this.v2RowForCompetitorUrl(url);
    if (v2 && this.topicOwnershipV2HasSectionCoverage(v2)) {
      return sectionGapsForV2Row(v2).length;
    }
    return null;
  }

  private v2RowForCompetitorUrl(url: string): SupportingPagesV2RowResult | null {
    const key = normalizeUrlForCompare(url);
    if (!key) return null;
    return (
      this.allMarkedV2Rows().find((r) => normalizeUrlForCompare(r.competitorUrl) === key) ?? null
    );
  }

  /** Open section-compare for Top SERP rival snapshot or marked-competitor v2 eval. */
  openSerpRivalSectionCompare(url: string): void {
    const trimmed = (url || '').trim();
    if (!trimmed) return;

    if (this.serpRivalCoverageForUrl(trimmed)?.status === 'ready') {
      this.toggleTopicOwnershipV2Detail(trimmed);
    } else if (this.v2RowForCompetitorUrl(trimmed)) {
      this.toggleTopicOwnershipV2Detail(trimmed);
    } else {
      return;
    }

    setTimeout(() => {
      document
        .querySelector('.url-topic-ownership')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  }

  devRebuildSupportingPagesByTopic(): void {
    const pageUrl = (this.url || '').trim();
    if (!this.isDevView() || !pageUrl || this.supportingPagesV1RebuildRunning) return;

    this.supportingPagesV1RebuildRunning = true;
    this.supportingPagesRebuildStatus = 'Building Supporting pages by topic…';
    this.resetTopicOwnershipV2Test();

    this.dataForSEOService.rebuildSupportingPagesByTopicSnapshot(pageUrl).subscribe({
      next: (res) => {
        this.supportingPagesV1RebuildRunning = false;
        this.supportingPagesRebuildStatus = res.message || 'Supporting pages snapshot rebuilt';
        if (res.data) {
          this.applySupportingPagesSnapshot(res.data);
          this.topicOwnershipStatus = 'ready';
          this.loadTopicOwnershipV2FromCache();
        } else {
          this.topicOwnershipDomains = [];
          this.topicOwnershipCacheMiss = true;
          this.topicOwnershipStatus = 'ready';
          this.topicOwnershipV2Status = 'ready';
        }
      },
      error: (err) => {
        this.supportingPagesV1RebuildRunning = false;
        this.supportingPagesRebuildStatus =
          err?.error?.message || err?.message || 'Supporting pages rebuild failed';
      }
    });
  }

  devRebuildSupportingPagesOpportunities(): void {
    const pageUrl = (this.url || '').trim();
    if (!this.isDevView() || !pageUrl || this.supportingPagesOppRebuildRunning) return;

    this.supportingPagesOppRebuildRunning = true;
    this.supportingPagesRebuildStatus = 'Building Supporting page opportunities…';

    this.dataForSEOService
      .rebuildSupportingPagesOpportunitySnapshot(pageUrl, { skipV1: true })
      .subscribe({
        next: (res) => {
          this.supportingPagesOppRebuildRunning = false;
          this.supportingPagesRebuildStatus = res.message || 'Opportunity snapshot rebuilt';
          if (res.data?.opportunities) {
            this.topicOwnershipV2Data = res.data.opportunities;
            this.topicOwnershipV2Status = 'ready';
            this.topicOwnershipV2Error = null;
          } else {
            this.topicOwnershipV2Data = null;
            this.topicOwnershipV2Status = 'ready';
          }
        },
        error: (err) => {
          this.supportingPagesOppRebuildRunning = false;
          this.supportingPagesRebuildStatus =
            err?.error?.message || err?.message || 'Opportunity rebuild failed';
          this.topicOwnershipV2Status = 'error';
          this.topicOwnershipV2Error =
            err?.error?.message || err?.message || 'Opportunity rebuild failed';
        }
      });
  }

  private resetTopicOwnershipV2Test(): void {
    this.topicOwnershipV2LoadGen++;
    this.topicOwnershipV2Status = 'idle';
    this.topicOwnershipV2Data = null;
    this.topicOwnershipV2Error = null;
    this.topicOwnershipV2DetailUrl = '';
    this.topicOwnershipV2CoveredElsewhereUrl = '';
    this.topicOwnershipV2ListInventoryUrl = '';
    this.topicOwnershipV2RankDrivenUrl = '';
    this.topicOwnershipV2TextUrl = '';
    this.topicOwnershipV2CalUrl = '';
    this.topicOwnershipV2SectionCompareCollapsedKeys.clear();
  }

  private findTopicOwnershipRow(pageUrl: string): DomainTopicUrlRow | null {
    const key = normalizeUrlForCompare(pageUrl);
    for (const block of this.topicOwnershipDomains) {
      for (const row of block.rows) {
        if (normalizeUrlForCompare(row.url) === key) return row;
      }
    }
    return null;
  }

  private loadTopicOwnershipV2Test(): void {
    this.loadTopicOwnershipV2FromCache();
  }

  retryTopicOwnershipV2Test(): void {
    if (this.isDevView()) {
      this.devRebuildSupportingPagesOpportunities();
      return;
    }
    this.loadTopicOwnershipV2FromCache();
  }

  toggleTopicOwnershipV2Detail(competitorUrl: string): void {
    if (this.topicOwnershipV2DetailUrl === competitorUrl) {
      this.topicOwnershipV2DetailUrl = '';
      return;
    }
    this.topicOwnershipV2DetailUrl = competitorUrl;
    this.topicOwnershipV2CoveredElsewhereUrl = '';
    this.topicOwnershipV2ListInventoryUrl = '';
    this.topicOwnershipV2RankDrivenUrl = '';
    this.topicOwnershipV2TextUrl = '';
    this.topicOwnershipV2CalUrl = '';
  }

  toggleTopicOwnershipV2Text(competitorUrl: string): void {
    if (this.topicOwnershipV2TextUrl === competitorUrl) {
      this.topicOwnershipV2TextUrl = '';
      return;
    }
    this.topicOwnershipV2TextUrl = competitorUrl;
    this.topicOwnershipV2DetailUrl = '';
    this.topicOwnershipV2CalUrl = '';
  }

  toggleTopicOwnershipV2Cal(competitorUrl: string): void {
    if (this.topicOwnershipV2CalUrl === competitorUrl) {
      this.topicOwnershipV2CalUrl = '';
      return;
    }
    this.topicOwnershipV2CalUrl = competitorUrl;
    this.topicOwnershipV2DetailUrl = '';
    this.topicOwnershipV2TextUrl = '';
  }

  topicOwnershipV2NovelClass(level: string): string {
    if (level === 'strong') return 'url-topic-ownership-v2-pill--strong';
    if (level === 'depth') return 'url-topic-ownership-v2-pill--depth';
    if (level === 'aligned') return 'url-topic-ownership-v2-pill--aligned';
    return 'url-topic-ownership-v2-pill--unknown';
  }

  topicOwnershipV2ActionClass(action: string): string {
    if (action === 'expand_focal') return 'url-topic-ownership-v2-action--expand';
    if (action === 'new_supporting_page') return 'url-topic-ownership-v2-action--new';
    if (action === 'covered_elsewhere') return 'url-topic-ownership-v2-action--covered';
    return 'url-topic-ownership-v2-action--muted';
  }

  topicOwnershipV2RowHighlight(row: { opportunityPriority: number; novelVsFocal: string }): boolean {
    return row.opportunityPriority >= 50 || row.novelVsFocal === 'strong';
  }

  topicOwnershipV2RowPath(pageUrl: string): string {
    return this.topicOwnershipPagePath(pageUrl);
  }

  topicOwnershipV2RowDomain(pageUrl: string): string {
    try {
      return new URL(pageUrl).hostname.replace(/^www\./i, '');
    } catch {
      return pageUrl;
    }
  }

  topicOwnershipV2RowDomainPath(pageUrl: string): string {
    return `${this.topicOwnershipV2RowDomain(pageUrl)}${this.topicOwnershipV2RowPath(pageUrl)}`;
  }

  /** Position in the focal page's target-query SERP snapshot (1–10), or null if URL absent from top 10. */
  topicOwnershipV2SerpPositionForUrl(pageUrl: string): number | null {
    if (!this.pageSerp?.results?.length) return null;
    const targetKey = normalizeUrlForCompare(pageUrl);
    if (!targetKey) return null;
    for (const row of this.pageSerp.results) {
      if (normalizeUrlForCompare(row.url) === targetKey) {
        return row.position;
      }
    }
    return null;
  }

  topicOwnershipV2ShowSerpRankChip(pageUrl: string): boolean {
    return this.hasPageSerpSnapshot();
  }

  topicOwnershipV2SerpRankChipLabel(pageUrl: string): string {
    const pos = this.topicOwnershipV2SerpPositionForUrl(pageUrl);
    if (pos != null && pos >= 1) return `Ranks #${pos}`;
    return 'Not in top 10';
  }

  topicOwnershipV2SerpRankChipTitle(pageUrl: string): string {
    const query = this.pageSerpTargetQueryLabel();
    const pos = this.topicOwnershipV2SerpPositionForUrl(pageUrl);
    const page = this.topicOwnershipV2RowDomainPath(pageUrl);
    if (pos != null) {
      return `${page} ranks #${pos} for «${query}» in this URL's top-10 SERP snapshot.`;
    }
    return `${page} is not in the top 10 for «${query}» in this URL's SERP snapshot.`;
  }

  topicOwnershipV2SerpRankChipClass(pageUrl: string): string {
    const pos = this.topicOwnershipV2SerpPositionForUrl(pageUrl);
    if (pos == null) return 'url-topic-ownership-v2-serp-chip--absent';
    if (pos <= 3) return 'url-topic-ownership-v2-serp-chip--strong';
    return 'url-topic-ownership-v2-serp-chip--ranked';
  }

  topicOwnershipV2HasSectionCoverage(row: SupportingPagesV2RowResult): boolean {
    return this.topicOwnershipV2SectionCoverageRows(row).length > 0;
  }

  topicOwnershipV2SectionCoverageRows(row: SupportingPagesV2RowResult): SectionCoverageRow[] {
    const rows = row.sectionCoverage?.length ? [...row.sectionCoverage] : [];
    return rows.sort((a, b) => (a.sectionNumber ?? 0) - (b.sectionNumber ?? 0));
  }

  topicOwnershipV2SectionCoverageGroups(row: SupportingPagesV2RowResult): SectionCoverageRow[] {
    let groups = row.sectionCoverageGroups?.length ? [...row.sectionCoverageGroups] : [];
    if (groups.length) {
      if (!this.isDevView()) {
        groups = groups.filter((sc) => !sc.listicleSuppressed);
      }
      return groups.sort((a, b) => (a.sectionNumber ?? 0) - (b.sectionNumber ?? 0));
    }
    return this.topicOwnershipV2SectionCoverageRows(row).filter(
      (sc) => this.topicOwnershipV2SectionNotOnPage(sc)
    );
  }

  topicOwnershipV2CompareFilterMode(): 'not_on_page' | 'gaps' | 'all' {
    return this.topicOwnershipV2CompareFilter;
  }

  setTopicOwnershipV2CompareFilter(mode: 'not_on_page' | 'gaps' | 'all'): void {
    this.topicOwnershipV2CompareFilter = mode;
  }

  /** Section missing entirely on focal page. */
  topicOwnershipV2SectionNotOnPage(sc: SectionCoverageRow): boolean {
    return !sc.sectionCovered;
  }

  /** A section is a gap when its topic is uncovered or it has absent phrases. */
  topicOwnershipV2SectionIsGap(sc: SectionCoverageRow): boolean {
    return !sc.sectionCovered || (sc.absentTerms?.length ?? 0) > 0;
  }

  topicOwnershipV2FilteredSectionCoverageRows(
    row: SupportingPagesV2RowResult
  ): SectionCoverageRow[] {
    const mode = this.topicOwnershipV2CompareFilterMode();
    if (!this.isDevView() && mode === 'not_on_page') {
      return this.topicOwnershipV2SectionCoverageGroups(row);
    }
    const rows = this.topicOwnershipV2SectionCoverageRows(row);
    if (mode === 'all') return rows;
    if (mode === 'not_on_page') return rows.filter((sc) => this.topicOwnershipV2SectionNotOnPage(sc));
    return rows.filter((sc) => this.topicOwnershipV2SectionIsGap(sc));
  }

  topicOwnershipV2CompareFilterEmptyMessage(): string {
    const mode = this.topicOwnershipV2CompareFilterMode();
    if (mode === 'not_on_page') {
      return 'Every competitor section is covered somewhere on your page — switch filter to review gaps or all sections.';
    }
    if (mode === 'gaps') {
      return 'No section gaps — switch to All sections to review full coverage.';
    }
    return '';
  }

  topicOwnershipV2SectionPositionChip(sc: SectionCoverageRow): string {
    const num = sc.sectionNumber;
    const end = sc.sectionNumberEnd;
    const total = sc.sectionTotal;
    const place = (sc.pagePosition || '').trim();
    if (num != null && total != null && total > 0) {
      const ordinal =
        end != null && end > num ? `${num}–${end}/${total}` : `${num}/${total}`;
      return place ? `${ordinal} · ${place}` : ordinal;
    }
    return place || '';
  }

  topicOwnershipV2ListicleSectionStatusLabel(sc: SectionCoverageRow): string {
    const tool = (sc.listicleToolLabel || 'tool').trim();
    if (sc.listicleSectionRole === 'tool_root') {
      return sc.listicleToolOnFocal ? `Listicle tool — ${tool}` : 'Listicle tool — not on your list';
    }
    if (sc.listicleSectionRole === 'tool_feature') {
      return sc.listicleToolOnFocal
        ? `Listicle feature — ${tool}`
        : `Listicle feature — ${tool} (use list inventory)`;
    }
    return 'Listicle section';
  }

  topicOwnershipV2ListicleSectionStatusTitle(sc: SectionCoverageRow): string {
    const tool = (sc.listicleToolLabel || 'tool').trim();
    if (sc.listicleSectionRole === 'tool_root' && !sc.listicleToolOnFocal) {
      return `Section is a numbered tool slot (${tool}) missing from your roundup — use list inventory, not section compare.`;
    }
    if (sc.listicleSectionRole === 'tool_feature' && !sc.listicleToolOnFocal) {
      return `Feature under ${tool}, which is not on your list — use list inventory for the tool gap.`;
    }
    if (sc.listicleSectionRole === 'tool_feature' && sc.listicleToolOnFocal) {
      return `Feature subsection for ${tool} — both pages list this tool; section compare is valid here.`;
    }
    if (sc.listicleSectionRole === 'tool_root' && sc.listicleToolOnFocal) {
      return `Numbered tool slot for ${tool} — on both roundups.`;
    }
    return 'Listicle-structured section.';
  }

  topicOwnershipV2SectionStatusTitle(sc: SectionCoverageRow): string | null {
    if (sc.isNonTopical) return this.topicOwnershipV2NonTopicalStatusTitle(sc);
    if (this.isDevView() && sc.listicleSectionRole) {
      return this.topicOwnershipV2ListicleSectionStatusTitle(sc);
    }
    if (sc.listicleSuppressed) {
      return 'Gap group suppressed in user view — tool not on your roundup (see list inventory).';
    }
    return null;
  }

  topicOwnershipV2SectionStatusLabel(sc: SectionCoverageRow): string {
    if (sc.isNonTopical) return this.topicOwnershipV2NonTopicalStatusLabel(sc);
    if (this.isDevView() && sc.listicleSectionRole) {
      return this.topicOwnershipV2ListicleSectionStatusLabel(sc);
    }
    if (sc.listicleSuppressed && this.isDevView()) return 'Listicle gap (user hidden)';
    if (!sc.sectionCovered) return 'Not on your page';
    if ((sc.absentTerms?.length ?? 0) > 0) return 'Covered — missing phrases';
    return 'Covered';
  }

  topicOwnershipV2NonTopicalStatusLabel(sc: SectionCoverageRow): string {
    if (sc.nonTopicalReason === 'author_bio') return 'Author bio (ignored)';
    if (sc.nonTopicalReason === 'newsletter_signup') return 'Newsletter signup (ignored)';
    if (sc.nonTopicalReason === 'product_cta') return 'Signup pitch (ignored)';
    if (sc.nonTopicalReason === 'generic_heading') return 'Non-article section (ignored)';
    if (sc.nonTopicalReason === 'rival_brand_heading') return 'Names their own brand (ignored)';
    if (sc.nonTopicalReason === 'rival_no_outline') return 'Rival has no outline (ignored)';
    if (sc.nonTopicalReason === 'rival_brand_parent') return 'Under their own brand (ignored)';
    if (sc.nonTopicalReason === 'opposite_task') return 'Opposite of your page\u2019s job (ignored)';
    if (sc.nonTopicalReason === 'off_article_tail') return 'Page furniture after the article (ignored)';
    if (sc.nonTopicalReason === 'repeated_item_field') return 'Per-item field of a list (ignored)';
    return 'Non-article section (ignored)';
  }

  topicOwnershipV2NonTopicalStatusTitle(sc: SectionCoverageRow): string {
    if (sc.nonTopicalReason === 'author_bio') {
      return 'Detected as an author/byline block — not scored as a content gap.';
    }
    if (sc.nonTopicalReason === 'newsletter_signup') {
      return 'Detected as a newsletter / email signup CTA — not scored as a content gap.';
    }
    if (sc.nonTopicalReason === 'product_cta') {
      return 'Detected as the rival pitching their own product (free trial, demo) — not scored as a content gap.';
    }
    if (sc.nonTopicalReason === 'generic_heading') {
      return 'Structural section (intro, conclusion, FAQ, etc.) — not scored as a content gap.';
    }
    if (sc.nonTopicalReason === 'rival_brand_heading') {
      return 'The heading names the rival\u2019s own product, not the subject \u2014 not scored as a content gap.';
    }
    if (sc.nonTopicalReason === 'rival_brand_parent') {
      return 'This section sits under a heading that names the rival\u2019s own product \u2014 not scored as a content gap.';
    }
    if (sc.nonTopicalReason === 'off_article_tail') {
      return 'One of the blocks after the rival\u2019s article ends that are about something else (related posts, sign-up and promo boxes) \u2014 not scored as a content gap.';
    }
    if (sc.nonTopicalReason === 'repeated_item_field') {
      return 'A heading the rival repeats under every item of a list (Pros, Pricing\u2026), not a section of its own \u2014 not scored as a content gap.';
    }
    if (sc.nonTopicalReason === 'opposite_task') {
      return 'This section does the opposite of what your page does to the same thing (adding what your page removes, or editing it) \u2014 not scored as a content gap.';
    }
    if (sc.nonTopicalReason === 'rival_no_outline') {
      return 'This rival page has no headings at all, so it cannot be compared section by section \u2014 not scored as a content gap.';
    }
    return 'Excluded from gap scoring — not part of the article body comparison.';
  }

  topicOwnershipV2SectionStatusClass(sc: SectionCoverageRow): string {
    if (sc.isNonTopical) return 'url-topic-ownership-v2-section-status--non-topical';
    if (
      this.isDevView() &&
      sc.listicleSectionRole &&
      !sc.listicleToolOnFocal
    ) {
      return 'url-topic-ownership-v2-section-status--listicle';
    }
    if (sc.listicleSuppressed && this.isDevView()) {
      return 'url-topic-ownership-v2-section-status--listicle';
    }
    if (!sc.sectionCovered) return 'url-topic-ownership-v2-section-status--missing';
    if ((sc.absentTerms?.length ?? 0) > 0) return 'url-topic-ownership-v2-section-status--shallow';
    return 'url-topic-ownership-v2-section-status--aligned';
  }

  topicOwnershipV2SectionBodyText(sc: SectionCoverageRow): string {
    return (sc.competitorText || '').trim();
  }

  topicOwnershipV2SectionHeadingPath(sc: SectionCoverageRow): string {
    return (sc.competitorHeadingPath || sc.competitorHeading || '').trim();
  }

  private headingPathSegmentsFromLegacyPath(
    path: string,
    leafLevel?: number | null
  ): HeadingPathSegment[] {
    const parts = path
      .split('>')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!parts.length) return [];
    return parts.map((heading, index) => ({
      heading,
      level: index === parts.length - 1 ? Math.round(Number(leafLevel) || 0) : 0
    }));
  }

  private mergeHeadingPathSegments(
    path: string,
    apiSegments: HeadingPathSegment[] | undefined | null,
    leafLevel?: number | null
  ): HeadingPathSegment[] {
    const parts = path
      .split('>')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!parts.length) {
      return apiSegments?.length ? apiSegments : [];
    }

    const api = apiSegments || [];
    if (api.length === parts.length) {
      return parts.map((heading, index) => ({
        heading,
        level:
          api[index]?.level ??
          (index === parts.length - 1 ? Math.round(Number(leafLevel) || 0) : 0)
      }));
    }

    if (parts.length > api.length) {
      const leaf =
        api[api.length - 1]?.level ??
        Math.round(Number(leafLevel) || 0) ??
        0;
      return parts.map((heading, index) => {
        const matched = api.find((seg) => seg.heading === heading);
        if (matched) return matched;
        const depthFromLeaf = parts.length - 1 - index;
        const inferred = leaf > 0 ? Math.max(2, leaf - depthFromLeaf) : 0;
        return { heading, level: inferred };
      });
    }

    if (api.length) return api;
    return this.headingPathSegmentsFromLegacyPath(path, leafLevel);
  }

  topicOwnershipV2SectionHeadingSegments(sc: SectionCoverageRow): HeadingPathSegment[] {
    return this.mergeHeadingPathSegments(
      this.topicOwnershipV2SectionHeadingPath(sc),
      sc.competitorHeadingPathSegments,
      sc.competitorHeadingLevel
    );
  }

  private topicOwnershipV2HeadingSegmentKey(seg: HeadingPathSegment): string {
    return `${seg.level}::${(seg.heading || '').trim()}`;
  }

  topicOwnershipV2SectionDisplayHeadingSegments(
    sc: SectionCoverageRow
  ): Array<HeadingPathSegment & { displayRole?: string; treeDepth?: number }> {
    const memberPaths = (sc.gapMemberPaths || [])
      .map((member) => member.headingPathSegments || [])
      .filter((path) => path.length > 0);
    if (sc.isGapGroup && memberPaths.length) {
      const gapLeafKeys = new Set(
        memberPaths.map((path) => this.topicOwnershipV2HeadingSegmentKey(path[path.length - 1]))
      );
      const leafLevel = memberPaths[0][memberPaths[0].length - 1]?.level;
      const sameLeafLevel = memberPaths.every(
        (path) => path[path.length - 1]?.level === leafLevel
      );
      const parentKey = memberPaths[0]
        .slice(0, -1)
        .map((seg) => seg.heading)
        .join(' > ');
      const sameParent = memberPaths.every(
        (path) => path.slice(0, -1).map((seg) => seg.heading).join(' > ') === parentKey
      );

      if (sameLeafLevel && sameParent && memberPaths.length > 1) {
        const ancestors = memberPaths[0].slice(0, -1).map((seg, index) => ({
          ...seg,
          displayRole: 'ancestor',
          treeDepth: index
        }));
        const seen = new Set<string>();
        const leaves = memberPaths
          .map((path) => path[path.length - 1])
          .filter((leaf) => {
            const key = this.topicOwnershipV2HeadingSegmentKey(leaf);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .map((leaf) => ({
            ...leaf,
            displayRole: 'gap',
            treeDepth: ancestors.length
          }));
        return [...ancestors, ...leaves];
      }

      const spine = memberPaths.reduce(
        (longest, path) => (path.length > longest.length ? path : longest),
        memberPaths[0]
      );
      const rows: Array<HeadingPathSegment & { displayRole?: string; treeDepth?: number }> = [];
      const seenGap = new Set<string>();
      spine.forEach((seg, depth) => {
        const key = this.topicOwnershipV2HeadingSegmentKey(seg);
        rows.push({
          ...seg,
          displayRole: gapLeafKeys.has(key) ? 'gap' : 'ancestor',
          treeDepth: depth
        });
        if (gapLeafKeys.has(key)) seenGap.add(key);
      });
      for (const path of memberPaths) {
        const leaf = path[path.length - 1];
        const key = this.topicOwnershipV2HeadingSegmentKey(leaf);
        if (!seenGap.has(key)) {
          rows.push({
            ...leaf,
            displayRole: 'gap',
            treeDepth: path.length - 1
          });
          seenGap.add(key);
        }
      }
      return rows;
    }

    const segments = this.topicOwnershipV2SectionHeadingSegments(sc);
    if (!sc.sectionCovered && !sc.isNonTopical && segments.length) {
      return segments.map((seg, index) => ({
        ...seg,
        displayRole: index === segments.length - 1 ? 'gap' : 'ancestor',
        treeDepth: index
      }));
    }
    return segments.map((seg, index) => ({ ...seg, displayRole: 'default', treeDepth: index }));
  }

  topicOwnershipV2SectionDisplayHeadingRole(seg: {
    displayRole?: string;
  }): string {
    const role = (seg.displayRole || 'default').trim();
    if (role === 'gap') return 'url-topic-ownership-v2-heading-path-segment--gap';
    if (role === 'covered-sibling') {
      return 'url-topic-ownership-v2-heading-path-segment--covered-sibling';
    }
    return '';
  }

  topicOwnershipV2ClosestHeadingSegments(sc: SectionCoverageRow): HeadingPathSegment[] {
    if (!this.topicOwnershipV2HasClosestMatch(sc)) return [];
    return this.mergeHeadingPathSegments(
      this.topicOwnershipV2ClosestHeadingPath(sc),
      sc.closestMyHeadingPathSegments,
      sc.closestMyHeadingLevel
    );
  }

  topicOwnershipV2HeadingLevelChip(level?: number | null): string | null {
    const n = Math.round(Number(level) || 0);
    if (n >= 1 && n <= 6) return `H${n}`;
    return null;
  }

  topicOwnershipV2SectionHeadingLevelChip(sc: SectionCoverageRow): string | null {
    return this.topicOwnershipV2HeadingLevelChip(sc.competitorHeadingLevel);
  }

  topicOwnershipV2ClosestHeadingLevelChip(sc: SectionCoverageRow): string | null {
    return this.topicOwnershipV2HeadingLevelChip(sc.closestMyHeadingLevel);
  }

  topicOwnershipV2FocalDomain(): string {
    return this.topicOwnershipV2RowDomain(this.url || '');
  }

  topicOwnershipV2ClosestHeadingPath(sc: SectionCoverageRow): string {
    return (sc.closestMyHeadingPath || sc.closestMyHeading || '').trim() || '(Untitled)';
  }

  topicOwnershipV2SectionHighlightSegments(
    sc: SectionCoverageRow
  ): SectionCompareHighlightSegment[] {
    return splitSectionTextByAbsentTerms(
      this.topicOwnershipV2SectionBodyText(sc),
      sc.absentTerms || []
    );
  }

  topicOwnershipV2HasClosestMatch(sc: SectionCoverageRow): boolean {
    return !!(sc.closestMyHeading?.trim() || sc.closestMyText?.trim());
  }

  topicOwnershipV2SectionCompareAvailable(sc: SectionCoverageRow): boolean {
    if (sc.isNonTopical) return false;
    return (
      !!this.topicOwnershipV2SectionBodyText(sc) ||
      !!this.topicOwnershipV2ClosestMyBodyText(sc) ||
      !!sc.closestMyHeading?.trim()
    );
  }

  topicOwnershipV2ClosestMatchPct(sc: SectionCoverageRow): number {
    return Math.round((sc.sectionSimilarity || 0) * 100);
  }

  topicOwnershipV2ClosestMatchTitle(sc: SectionCoverageRow): string {
    const pct = this.topicOwnershipV2ClosestMatchPct(sc);
    return (
      `Embedding similarity ${pct}% between this competitor section and the closest block on your focal page. ` +
      `Sections need ≥55% to count as covered. Higher scores mean a closer match, not guaranteed topical coverage — read the text to verify.`
    );
  }

  topicOwnershipV2ClosestMyBodyText(sc: SectionCoverageRow): string {
    return (sc.closestMyText || '').trim();
  }

  topicOwnershipV2CompareClosestLabel(sc: SectionCoverageRow): string | null {
    if (!this.topicOwnershipV2HasClosestMatch(sc)) return null;
    if (sc.sectionCovered) return null;
    return `Closest on your page (${this.topicOwnershipV2ClosestMatchPct(sc)}% similar)`;
  }

  topicOwnershipV2CompareRightColumnTitle(sc: SectionCoverageRow): string {
    if (!this.topicOwnershipV2HasClosestMatch(sc)) {
      return 'No matching section';
    }
    return this.topicOwnershipV2ClosestHeadingPath(sc);
  }

  topicOwnershipV2CompareRightColumnSegments(sc: SectionCoverageRow): HeadingPathSegment[] {
    if (!this.topicOwnershipV2HasClosestMatch(sc)) {
      return [{ level: 0, heading: 'No matching section' }];
    }
    return this.topicOwnershipV2ClosestHeadingSegments(sc);
  }

  topicOwnershipV2SectionFullTextKey(competitorUrl: string, sc: SectionCoverageRow): string {
    if (sc.isGapGroup) {
      const end = sc.sectionNumberEnd ?? sc.sectionNumber ?? 0;
      return `${competitorUrl}::group::${sc.sectionNumber ?? 0}::${end}`;
    }
    return `${competitorUrl}::${sc.sectionNumber ?? 0}`;
  }

  isTopicOwnershipV2SectionFullTextExpanded(competitorUrl: string, sc: SectionCoverageRow): boolean {
    if (!this.topicOwnershipV2SectionCompareAvailable(sc)) return false;
    return !this.topicOwnershipV2SectionCompareCollapsedKeys.has(
      this.topicOwnershipV2SectionFullTextKey(competitorUrl, sc)
    );
  }

  toggleTopicOwnershipV2SectionFullText(competitorUrl: string, sc: SectionCoverageRow): void {
    const key = this.topicOwnershipV2SectionFullTextKey(competitorUrl, sc);
    if (this.topicOwnershipV2SectionCompareCollapsedKeys.has(key)) {
      this.topicOwnershipV2SectionCompareCollapsedKeys.delete(key);
    } else {
      this.topicOwnershipV2SectionCompareCollapsedKeys.add(key);
    }
  }

  topicOwnershipPagePath(pageUrl: string): string {
    return this.reviewModel.topicOwnershipPagePath(pageUrl);
  }

  topicOwnershipPageTraffic(traffic: number | undefined): string {
    return this.reviewModel.topicOwnershipPageTraffic(traffic);
  }

  getTopicOwnershipVisibleRows(block: TopicOwnershipDomain): DomainTopicUrlRow[] {
    const rows = this.getTopicOwnershipRowsForDisplay(block);
    if (this.useTopicOwnershipUserLayout()) {
      if (!block.isYourSite) return rows;
      if (this.topicOwnershipUserExpanded) return rows;
      return rows.slice(0, SUPPORTING_PAGE_USER_INITIAL_VISIBLE_COUNT);
    }
    if (this.isTopicOwnershipPagesExpanded(block.domain)) {
      return rows;
    }
    return rows.slice(0, SUPPORTING_PAGE_INITIAL_VISIBLE_COUNT);
  }

  private getTopicOwnershipRowsForDisplay(block: TopicOwnershipDomain): DomainTopicUrlRow[] {
    return this.reviewModel.getTopicOwnershipRowsForDisplay(block);
  }

  getYourSiteMatchPoolRows(): DomainTopicUrlRow[] {
    const yours = this.topicOwnershipDomains.find((d) => d.isYourSite);
    if (!yours?.rows.length) return [];
    if (!this.focalAnchorTopicSlugs.size) return yours.rows;
    return filterRowsByFocalAnchorTopics(yours.rows, this.focalAnchorTopicSlugs);
  }

  topicOwnershipAnchorHint(): string {
    if (!this.focalAnchorTopicLabels.length) return '';
    return this.focalAnchorTopicLabels.join(', ');
  }

  isTopicOwnershipRowOffAnchor(block: TopicOwnershipDomain, row: DomainTopicUrlRow): boolean {
    return (
      this.isDevView() &&
      block.isYourSite &&
      this.focalAnchorTopicSlugs.size > 0 &&
      !rowOverlapsFocalAnchorTopics(row, this.focalAnchorTopicSlugs)
    );
  }

  hasMoreTopicOwnershipPagesThanVisible(block: TopicOwnershipDomain): boolean {
    if (this.useTopicOwnershipUserLayout()) return false;
    return this.getTopicOwnershipRowsForDisplay(block).length > SUPPORTING_PAGE_INITIAL_VISIBLE_COUNT;
  }

  hasMoreTopicOwnershipPagesOnYourSite(): boolean {
    const yours = this.topicOwnershipDomains.find((d) => d.isYourSite);
    if (!yours) return false;
    return this.getTopicOwnershipRowsForDisplay(yours).length > SUPPORTING_PAGE_USER_INITIAL_VISIBLE_COUNT;
  }

  toggleTopicOwnershipUserExpanded(): void {
    this.topicOwnershipUserExpanded = !this.topicOwnershipUserExpanded;
  }

  topicOwnershipUserExpandLabel(): string {
    if (this.topicOwnershipUserExpanded) return 'Show fewer pages';
    const yours = this.topicOwnershipDomains.find((d) => d.isYourSite);
    const hidden = Math.max(
      0,
      (yours?.rows.length ?? 0) - SUPPORTING_PAGE_USER_INITIAL_VISIBLE_COUNT
    );
    return hidden > 0 ? `Show more pages (${hidden} more)` : 'Show more pages';
  }

  topicOwnershipLinkTitle(block: TopicOwnershipDomain, row: DomainTopicUrlRow): string {
    if (block.isYourSite) return row.url;
    if (row.similarOnYourSite?.url) {
      return `${row.url}\nCovered — similar: ${row.similarOnYourSite.url}`;
    }
    return `${row.url}\nGap — no similar page on your site`;
  }

  isTopicOwnershipPagesExpanded(domain: string): boolean {
    return this.topicOwnershipPagesExpandedByDomain.has(domain.toLowerCase().trim());
  }

  toggleTopicOwnershipPagesExpanded(domain: string): void {
    const key = domain.toLowerCase().trim();
    if (this.topicOwnershipPagesExpandedByDomain.has(key)) {
      this.topicOwnershipPagesExpandedByDomain.delete(key);
    } else {
      this.topicOwnershipPagesExpandedByDomain.add(key);
    }
  }

  topicOwnershipPagesExpandLabel(block: TopicOwnershipDomain): string {
    if (this.isTopicOwnershipPagesExpanded(block.domain)) {
      return 'Show fewer pages';
    }
    const rest = block.rows.length - SUPPORTING_PAGE_INITIAL_VISIBLE_COUNT;
    return rest > 0 ? `Show more pages (${rest} more)` : 'Show more pages';
  }

  isCurrentPageSupportingUrl(pageUrl: string): boolean {
    if (!this.url) return false;
    return normalizeUrlForCompare(pageUrl) === normalizeUrlForCompare(this.url);
  }

  private loadCompetitorTopicCoverage(): void {
    this.competitorCoverageStatus = 'idle';
    this.competitorCoverageMessage = '';
    this.competitorTopicMatchesBySlug = {};
    this.competitorTopicTooltipCache = {};

    const topicSignals = this.getPageTopicSignals();
    if (!topicSignals.length || !this.url) return;

    const activeDomain = this.extractDomainFromUrl(this.url);
    if (!activeDomain) return;

    this.competitorCoverageStatus = 'loading';

    this.compareContextService.getSettings(activeDomain).pipe(
      map((settingsRes) => {
        const domains = settingsRes?.data?.markedCompetitorDomains || [];
        this.markedCompetitorDomains = domains
          .map((d) => d.toLowerCase().trim().replace(/^www\./, ''))
          .filter((d) => !!d);
        this.ensureMarkedSerpCompetitorEvals();
        return domains
          .map((d) => d.toLowerCase().trim())
          .filter((d) => !!d && d !== activeDomain);
      }),
      map((domains) => Array.from(new Set(domains))),
      map((domains) => domains.slice(0, 10)),
      catchError(() => {
        this.competitorCoverageStatus = 'error';
        this.competitorCoverageMessage = 'Could not load marked competitors for this domain.';
        return of([] as string[]);
      })
    ).subscribe((competitorDomains) => {
      if (this.competitorCoverageStatus === 'error') return;

      if (!competitorDomains.length) {
        this.competitorCoverageStatus = 'ready';
        this.competitorCoverageMessage = 'No marked competitors configured for this domain.';
        return;
      }

      const requests = (competitorDomains as string[]).map((domain: string) =>
        this.domainTopicsService.getDomainTopics(domain, { compact: true }).pipe(
          map((res) => ({ domain, topics: (res?.data?.parentTopics || []) as ParentTopic[] })),
          catchError(() => of({ domain, topics: [] as ParentTopic[] }))
        )
      );

      forkJoin(requests).subscribe({
        next: (competitorTopics: { domain: string; topics: ParentTopic[] }[]) => {
          this.competitorTopicMatchesBySlug = this.buildCompetitorMatches(topicSignals, competitorTopics);
          this.competitorCoverageStatus = 'ready';
          this.competitorCoverageMessage = '';
        },
        error: () => {
          this.competitorCoverageStatus = 'error';
          this.competitorCoverageMessage = 'Could not load competitor topic coverage.';
        }
      });
    });
  }

  private buildCompetitorMatches(
    signals: PageTopicSignal[],
    competitorTopics: Array<{ domain: string; topics: ParentTopic[] }>
  ): Record<string, CompetitorTopicMatch[]> {
    const bySlug: Record<string, CompetitorTopicMatch[]> = {};

    for (const signal of signals) {
      const normalizedTopicSlug = signal.topicSlug.toLowerCase().trim();
      const matches: CompetitorTopicMatch[] = [];

      for (const competitor of competitorTopics) {
        const topic = competitor.topics.find((t) => {
          const parentMatch = t.parentTopicSlug?.toLowerCase().trim() === normalizedTopicSlug;
          const childMatch = Array.isArray(t.childTopicSlugs)
            && t.childTopicSlugs.some((child) => child.toLowerCase().trim() === normalizedTopicSlug);
          return parentMatch || childMatch;
        });

        if (!topic) continue;

        matches.push({
          domain: competitor.domain,
          authorityScore: Number.isFinite(topic.authorityScore) ? topic.authorityScore : 0,
          pageCount: Number.isFinite(topic.pageCount) ? topic.pageCount : 0,
          matchedParentTopicSlug: topic.parentTopicSlug
        });
      }

      bySlug[signal.topicSlug] = matches.sort((a, b) => {
        if (b.authorityScore !== a.authorityScore) return b.authorityScore - a.authorityScore;
        if (b.pageCount !== a.pageCount) return b.pageCount - a.pageCount;
        return a.domain.localeCompare(b.domain);
      });
    }

    return bySlug;
  }

  extractDomainFromUrl(url: string): string {
    return this.reviewModel.extractDomainFromUrl(url);
  }

  getCompetitorTopicMatches(signal: PageTopicSignal): CompetitorTopicMatch[] {
    return this.competitorTopicMatchesBySlug[signal.topicSlug] || [];
  }

  hasCompetitorMatches(signal: PageTopicSignal): boolean {
    return this.getCompetitorTopicMatches(signal).length > 0;
  }

  formatCompetitorPill(match: CompetitorTopicMatch): string {
    return `${match.domain} (${Math.round(match.authorityScore)} · ${match.pageCount})`;
  }

  private getCompetitorTopicTooltipKey(match: CompetitorTopicMatch): string {
    return `${match.domain}::${match.matchedParentTopicSlug}`;
  }

  onCompetitorPillHover(match: CompetitorTopicMatch): void {
    this.cancelCompetitorTooltipHide();
    const key = this.getCompetitorTopicTooltipKey(match);
    this.activeCompetitorTooltipKey = key;
    const cached = this.competitorTopicTooltipCache[key];
    if (cached && (cached.status === 'loading' || cached.status === 'ready' || cached.status === 'error')) {
      return;
    }

    this.competitorTopicTooltipCache[key] = { status: 'loading', urls: [] };

    this.domainTopicsService.getTopicDiagnostic(match.domain, match.matchedParentTopicSlug).pipe(
      map((res) => res?.data?.coverage?.directlyCoveredTopics || []),
      map((topics: any[]) => topics.find((t) => t?.parentTopicSlug === match.matchedParentTopicSlug)),
      map((topic: any) => {
        if (!topic) return [];
        const corePages = Array.isArray(topic.corePages) ? topic.corePages : [];
        const mentionPages = Array.isArray(topic.mentionPages) ? topic.mentionPages : [];
        const allPages = [...corePages, ...mentionPages];
        const byUrl = new Map<string, CompetitorTopicUrlPreview>();

        for (const page of allPages) {
          const pageUrl = typeof page?.url === 'string' ? page.url : '';
          if (!pageUrl) continue;
          const score = Number.isFinite(page?.seoIntentScore) ? page.seoIntentScore : 0;
          const salience = Number.isFinite(page?.salience) ? page.salience : undefined;
          const estimatedOrganicTraffic = typeof page?.estimatedOrganicTraffic === 'number'
            ? page.estimatedOrganicTraffic
            : undefined;
          const current = byUrl.get(pageUrl);
          if (!current || score > current.score) {
            byUrl.set(pageUrl, { url: pageUrl, score, salience, estimatedOrganicTraffic });
          }
        }

        return Array.from(byUrl.values())
          .sort((a, b) => b.score - a.score)
          .slice(0, 5);
      }),
      catchError(() => of([] as CompetitorTopicUrlPreview[]))
    ).subscribe((urls) => {
      this.competitorTopicTooltipCache[key] = {
        status: urls.length > 0 ? 'ready' : 'error',
        urls
      };
    });
  }

  onCompetitorPillLeave(): void {
    this.scheduleCompetitorTooltipHide();
  }

  onCompetitorPopoverEnter(match: CompetitorTopicMatch): void {
    this.cancelCompetitorTooltipHide();
    this.activeCompetitorTooltipKey = this.getCompetitorTopicTooltipKey(match);
  }

  onCompetitorPopoverLeave(): void {
    this.scheduleCompetitorTooltipHide();
  }

  private scheduleCompetitorTooltipHide(): void {
    this.cancelCompetitorTooltipHide();
    this.competitorTooltipHideTimer = setTimeout(() => {
      this.activeCompetitorTooltipKey = null;
      this.competitorTooltipHideTimer = null;
    }, 180);
  }

  private cancelCompetitorTooltipHide(): void {
    if (!this.competitorTooltipHideTimer) return;
    clearTimeout(this.competitorTooltipHideTimer);
    this.competitorTooltipHideTimer = null;
  }

  isCompetitorTooltipVisible(match: CompetitorTopicMatch): boolean {
    return this.activeCompetitorTooltipKey === this.getCompetitorTopicTooltipKey(match);
  }

  getCompetitorTooltipState(match: CompetitorTopicMatch): CompetitorTopicTooltipState {
    const key = this.getCompetitorTopicTooltipKey(match);
    return this.competitorTopicTooltipCache[key] || { status: 'idle', urls: [] };
  }

  getCompetitorPillTooltip(match: CompetitorTopicMatch): string {
    const key = this.getCompetitorTopicTooltipKey(match);
    const state = this.competitorTopicTooltipCache[key];
    const summary = `${match.domain}\nAuthority: ${Math.round(match.authorityScore)}\nURLs in topic: ${match.pageCount}`;

    if (!state || state.status === 'idle') {
      return `${summary}\n\nHover to load top URLs...`;
    }
    if (state.status === 'loading') {
      return `${summary}\n\nLoading URLs...`;
    }
    if (state.status === 'error') {
      return `${summary}\n\nNo URL-level preview available.`;
    }

    const lines = state.urls.map((item, i) => `${i + 1}. ${item.url} (${item.score.toFixed(3)})`);
    return `${summary}\n\nTop URLs:\n${lines.join('\n')}`;
  }

  onCompetitorPopoverUrlClick(match: CompetitorTopicMatch, competitorUrl: string, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.openQuickCompareWithUrl(competitorUrl || '');
  }

  onQuickCompareDrawerClose(): void {
    this.quickCompareOpen = false;
  }

  onOpenFullCompareFromDrawer(): void {
    const primary = (this.quickComparePrimaryUrl || '').trim();
    const compare = (this.quickCompareCompareUrl || '').trim();
    if (!primary || !compare) return;
    this.quickCompareOpen = false;
    void this.router.navigate(['/page-comparison'], {
      queryParams: {
        fullCompareYour: primary,
        fullCompareCompetitor: compare
      }
    });
  }

  private decodeUrlParam(raw: string): string {
    let value = (raw || '').trim();
    if (!value) return '';

    for (let i = 0; i < 3; i++) {
      try {
        const decoded = decodeURIComponent(value);
        if (decoded === value) break;
        value = decoded;
      } catch {
        break;
      }
    }

    return value;
  }

  private normalizeNavigableUrl(raw: string): string | null {
    const decoded = this.decodeUrlParam(raw);
    const trimmed = decoded.trim();
    if (!trimmed) return null;
    try {
      const u = new URL(trimmed);
      if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
      return u.href;
    } catch {
      return null;
    }
  }
} 